import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  addQuoteAttachment, addQuoteGroup, addQuoteItem, addQuoteRecipient, createSnippet,
  deleteQuoteAttachment, deleteQuoteGroup, deleteQuoteItem, deleteQuoteRecipient,
  fetchAssets, fetchCatalogue, fetchPortalSettings, fetchQuote, fetchQuotes, fetchSnippets,
  createPreviewLink, deleteQuoteFully, duplicateQuote, fetchQuoteEvents, fetchTermsSets,
  fetchHsOptions, fetchTrustRoster, hubspotDealUrl, patchQuote, patchQuoteGroup, patchQuoteItem,
  addRenewalNote, costEvidenceUrl, supplierQuoteFileUrl, discardTemplateWorkspace, fetchRenewalEvents, fetchTemplates,
  updateQuoteAttachment, uploadQuoteAttachment,
  logCostVariation, refreshQuotePrices, resendQuote,
  saveQuoteTemplate, saveTemplateFromWorkspace, searchContacts, searchDeals, searchSchools, sendQuote,
  setQuoteCustomer, supersedeQuote, withdrawQuote, acceptQuoteOnBehalf, declineQuoteOnBehalf,
  splitQuoteDeals, type SplitCommitResult, type SplitMappingEntry, type SplitMode, type SplitPlan,
  provisioningPack, type PackCommitResult, type PackDecisionEntry, type PackPlan, type PackRow,
  type AcceptOnBehalfResult, type AssetRow, type ContactHit, type DealHit, type HsOptions,
  type PortalSettings, type QuoteAttachment, type QuoteDetail,
  type MailFailure, type QuoteEvent, type QuoteItemRow, type RenewalEvent, type SchoolHit, type SendResult, type TemplateRow,
  type SnippetRow, type SnippetTokenRow, type TermsSetRow, askExtensionDate, extendQuoteValidity, quoteEditor,
} from '../lib/api.js'
import { formatGBP, formatGBPCompact } from '@shared/money'
import { pillStyleFor } from '../lib/pills.js'
import { formatUKDate } from '@shared/dates'
import { annualised, applySelections, checkSellPrice, computeTotals, FREQUENCY_SUFFIX, margin, sectionSubtotals, sellForMargin, termLabel, termYears } from '@shared/pricing'
import { applyTitleStandard, hasSurveyableLines, MAX_RECIPIENTS, suggestTermsSet } from '@shared/builder'
import { expandSnippetTokens, type TokenContext } from '@shared/snippetTokens'
import { includedSectionIds, QTY_MAX } from '@shared/pricing'
import { DECLINE_REASONS, selectionsFromAcceptance } from '@shared/customer'
import { MarkupToolbar, QuoteMarkup } from '../lib/markup.js'
import { ContractFacts, ContractLines } from '../components/ContractSummary.js'
import { SupplierQuoteApply } from '../components/SupplierQuoteApply.js'
import { RaisePurchaseOrder } from '../components/RaisePurchaseOrder.js'
import { AdhocPurchaseOrder } from '../components/AdhocPurchaseOrder.js'
import { mintRecipientLink, mintAmendLink, amendAcceptanceOnBehalf, type AmendAcceptanceResult } from '../lib/api.js'
import { fetchContractVariations, recordContractVariation, type ContractVariations } from '../lib/api.js'
import { copyTextFromPromise } from '../lib/clipboard.js'
import { lineKeysFor } from '@shared/productLines'
import type { BillingFrequency, Partner, PricedProduct, QuoteLine, SelectionMap } from '@shared/types'
import { FREQUENCY_LABEL } from '@shared/billing'
import { isRegisterUrn } from '@shared/sites'

/**
 * The Builder (phase 2) — workspace layout per the agreed mockup v4:
 * catalogue picker docked left (~330px), quote canvas right, RHS slide-overs
 * for line detail. Customer-first-skippable start, absolute choose-one prices,
 * no margin-hide toggle: the customer PREVIEW is the screen-share surface and
 * the build view stays frank (Mark's locked design answers, 2026-07-31).
 *
 * ⚠️ Everything commercial is enforced server-side (and again by the DB
 * trigger). The clamps here are conveniences, not the guardrail.
 */

interface Props {
  partner: Partner
  quoteId: string
  onBack: () => void
  /** Open another quote in the builder — used when superseding creates the new draft (decision 73). */
  onOpen?: (quoteId: string) => void
  /** Admin sessions may delete never-synced non-draft quotes (decision 85 — test-quote cleanup). */
  role?: 'admin' | 'user' | null
  /**
   * Decision 125: this builder is editing a TEMPLATE, not a quote — the row
   * behind it is a workspace (migration 031). Everything customer-facing and
   * deal-facing is hidden, and the header saves back to the template.
   */
  templateMode?: {
    templateId: string
    /** Resumed an unfinished edit rather than starting fresh. */
    resumed: boolean
    onSaved: (name: string, summary: string) => void
    onDiscarded: () => void
  }
}

type Slide =
  | { kind: 'line'; itemId: string }
  | { kind: 'attachments' }
  | { kind: 'send' }
  | { kind: 'sites' }      // decision 81: edit the site list on a trust quote
  | { kind: 'links' }      // decision 78: per-recipient resend / fresh links
  | { kind: 'activity' }   // decision 81: the event trail
  | { kind: 'acceptOnBehalf' }  // decision 94: staff record a customer's acceptance
  | { kind: 'declineOnBehalf' } // decision 121: staff record a customer's decline
  | { kind: 'costVariation' }   // decision 97: log a cost variation (evidence + note)
  | { kind: 'presentation' }    // decision 125: what a TEMPLATE carries — note, footer, T&Cs
  | { kind: 'contract' }        // decision 127: the contract being renewed (read-only)
  | { kind: 'splitDeals' }      // decision 171: split an accepted quote into per-site deals
  | { kind: 'provisioningPack' }  // decision 178: assets + stock tasks from the frozen accepted record
  | { kind: 'acceptance' }        // decision 191: what the customer actually accepted (read-only)
  | { kind: 'amendAcceptance' }   // decision 218: amend the acceptance — customer link, or on the customer's instruction
  | { kind: 'supplierApply' }     // decision 212: apply a distributor quote to this draft
  | { kind: 'raisePo' }           // decision 213: the Xero draft purchase order
  | { kind: 'adhocPo' }           // decision 236: ad hoc Xero POs from the accepted lines
  | null

export function Builder({ partner, quoteId, onBack, onOpen, role, templateMode }: Props) {
  const [detail, setDetail] = useState<QuoteDetail | null>(null)
  const [products, setProducts] = useState<PricedProduct[]>([])
  const [settings, setSettings] = useState<PortalSettings | null>(null)
  const [snippets, setSnippets] = useState<SnippetRow[]>([])
  const [snippetTokens, setSnippetTokens] = useState<SnippetTokenRow[]>([])
  const [termsSets, setTermsSets] = useState<TermsSetRow[]>([])
  const [assets, setAssets] = useState<AssetRow[]>([])
  const [people, setPeople] = useState<Array<{ id: string; name: string }>>([])
  const [hsOpts, setHsOpts] = useState<HsOptions | null>(null)   // decision 86
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  const [lineFilter, setLineFilter] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [slide, setSlide] = useState<Slide>(null)
  const [preview, setPreview] = useState(false)
  // Decision 240: the line editor is an OVERLAY over the quote record; on a
  // draft its session ends with Save to draft / Discard changes.
  const [editing, setEditing] = useState(false)
  const [editSession, setEditSession] = useState<{ resumed: boolean; since: string | null; by: string | null; mine: boolean } | null>(null)
  const [editorBusy, setEditorBusy] = useState(false)
  const [startOpen, setStartOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  // Decision 96: the save-as-template modal.
  const [tplModal, setTplModal] = useState(false)
  const [tplName, setTplName] = useState('')
  const [tplGlobal, setTplGlobal] = useState(false)
  // Decision 122: "Save as template" can UPDATE an existing template in place.
  const [tplList, setTplList] = useState<TemplateRow[]>([])
  const [tplTarget, setTplTarget] = useState('')   // '' = save as new
  // Decision 124: the presentation fields the TEMPLATE carries, editable here.
  // They start from this quote's values, but they belong to the template —
  // saving never writes them back to the quote (the modal says so).
  const [tplCover, setTplCover] = useState('')
  const [tplFooter, setTplFooter] = useState('')
  const [tplTerms, setTplTerms] = useState('')
  const [tplSaving, setTplSaving] = useState(false)
  const [tplError, setTplError] = useState<string | null>(null)
  // Decision 125: the template editor's own save/discard state.
  const [tplEditSaving, setTplEditSaving] = useState(false)
  const [flashId, setFlashId] = useState<string | null>(null)
  const [selections, setSelections] = useState<SelectionMap>({})
  // Decision 218: amend mode — the accepted quote's preview unlocks, seeded
  // from the frozen record, so staff can set the amended selection before
  // recording it on the customer's instruction.
  const [amending, setAmending] = useState(false)
  const [customerLink, setCustomerLink] = useState<{ url: string; copied: boolean } | null>(null)
  // Decision 248: the contract-variations read (one HubSpot line-item read on open of an accepted internal quote).
  const [contractVar, setContractVar] = useState<{ quoteId: string; data: ContractVariations | null; error: string | null } | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout>>()

  // Decision 86: HubSpot-owned picklists + portal id (cached server-side; a
  // missing token just means free-text inputs and an un-linked deal id).
  useEffect(() => {
    let live = true
    fetchHsOptions().then((o) => { if (live) setHsOpts(o) }).catch(() => {})
    return () => { live = false }
  }, [])

  const reload = useCallback(async () => {
    const d = await fetchQuote(quoteId)
    setDetail(d)
    setActiveGroup((g) => (g && d.groups.some((x) => x.id === g) ? g : d.groups[0]?.id ?? null))
    return d
  }, [quoteId])

  useEffect(() => {
    let live = true
    Promise.all([
      fetchQuote(quoteId), fetchCatalogue(), fetchPortalSettings(),
      fetchSnippets().catch(() => ({ editable: false, snippets: [], tokens: [], tokenSources: [] })),
      fetchTermsSets().catch(() => ({ editable: false, sets: [] })),
      fetchAssets(quoteId).catch(() => ({ assets: [] })),
      fetchQuotes().catch(() => null),
    ]).then(([d, cat, ps, sn, ts, as, qs]) => {
      if (!live) return
      setDetail(d)
      setActiveGroup(d.groups[0]?.id ?? null)
      setProducts(cat.products)
      setSettings(ps)
      setSnippets(sn.snippets)
      setSnippetTokens(sn.tokens ?? [])
      setTermsSets(ts.sets.filter((s) => s.active))
      setAssets(as.assets)
      if (qs) setPeople(qs.users)
      // Customer-first but skippable: an untouched quote opens on the start modal.
      // Decision 125: a TEMPLATE has no customer and never will — the
      // customer-first start modal must not ambush the template editor.
      if (!d.customer && d.quote.status === 'draft' && !d.quote.templateWorkspaceFor) setStartOpen(true)
    }).catch((e: Error) => live && setError(e.message))
    return () => { live = false }
  }, [quoteId])

  const productById = useMemo(() => new Map(products.map((p) => [p.hsProductId, p])), [products])
  const mode = partner.mode
  const ro = detail != null && detail.quote.status !== 'draft'
  useEffect(() => {
    // Decision 248: read once per accepted internal quote (staff); re-read after an amendment (version changes).
    if (!detail || detail.quote.status !== 'accepted' || role === null || partner.mode !== 'internal') { setContractVar(null); return }
    const key = `${detail.quote.id}:${detail.acceptance?.version ?? 0}`
    setContractVar({ quoteId: key, data: null, error: null })
    fetchContractVariations(detail.quote.id)
      .then((data) => setContractVar((c) => (c?.quoteId === key ? { quoteId: key, data, error: null } : c)))
      .catch((e: Error) => setContractVar((c) => (c?.quoteId === key ? { quoteId: key, data: null, error: e.message } : c)))
  }, [detail?.quote.id, detail?.quote.status, detail?.acceptance?.version, role, partner.mode])

  // Decision 241: Esc dismisses the read-only overlay like a slide-over; a
  // draft's overlay has no Esc — its exits are Save and Discard.
  useEffect(() => {
    if (!editing || !ro || slide != null) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setEditing(false); setPreview(false) } }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [editing, ro, slide])


  const showToast = (msg: string) => {
    setToast(msg)
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), 2200)
  }

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try { await fn() } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  if (error && !detail) return <p className="pill pill--stop" style={{ margin: 24 }}>{error}</p>
  if (!detail) return <p className="muted" style={{ margin: 24 }}>Loading the builder…</p>

  const { quote, groups, items, sites, staleLineIds } = detail

  // ---- derived -------------------------------------------------------------
  const itemsByGroup = new Map<string, QuoteItemRow[]>()
  for (const g of groups) itemsByGroup.set(g.id, [])
  for (const it of items) if (it.groupId && itemsByGroup.has(it.groupId)) itemsByGroup.get(it.groupId)!.push(it)

  const quoteCategories = items
    .map((it) => (it.hsProductId ? productById.get(it.hsProductId)?.category ?? null : null))
  const surveyable = hasSurveyableLines(quoteCategories)
  const multiSite = sites.length > 1
  // Decision 191: the frozen acceptance, keyed by line — null while the quote
  // is not accepted, so the rows know whether to badge at all.
  const acceptedQtyById = quote.status === 'accepted' && detail.acceptance
    ? new Map(detail.acceptance.selections.map((f) => [f.itemId, { qty: f.qty }]))
    : null

  // ---- actions ---------------------------------------------------------------
  const commitTitle = (title: string) => run(async () => {
    await patchQuote(quoteId, { title })
    setDetail((d) => d && { ...d, quote: { ...d.quote, title } })
  })

  const addProduct = (p: PricedProduct) => {
    if (ro) return
    const gid = activeGroup ?? groups[0]?.id
    if (!gid) { setError('Add a section first.'); return }
    run(async () => {
      const res = await addQuoteItem(quoteId, { groupId: gid, hsProductId: p.hsProductId })
      await reload()
      setFlashId(res.id)
      const g = groups.find((x) => x.id === gid)
      showToast(`Added ${p.name.split(' — ')[0]} to “${g?.name ?? 'section'}”`)
      document.querySelector(`[data-line="${res.id}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
  }

  const pickCustomer = (payload: {
    name: string; urn?: string | null; postcode?: string | null; address?: unknown; contactName?: string | null
    sites: Array<{ name: string; urn?: string | null; postcode?: string | null; address?: unknown; contact?: string | null; numberOnRoll?: number | null }>
  }) => run(async () => {
    await setQuoteCustomer(quoteId, payload)
    // Decision 153 — the Wave9 standard: "{Site Name}, {Postcode} : {rest}".
    const newTitle = applyTitleStandard(quote.title || 'New quote', payload.sites, payload.name)
    await patchQuote(quoteId, { title: newTitle })
    setStartOpen(false)
    await reload()
  })

  /**
   * Decision 125: save the workspace back over the template. The quote title
   * IS the template name here — the header's title field is the rename.
   */
  const saveTemplateEdit = async () => {
    if (!templateMode) return
    setTplEditSaving(true)
    try {
      const res = await saveTemplateFromWorkspace(templateMode.templateId, quote.title || undefined)
      templateMode.onSaved(res.name, `${res.summary} · ${res.carries.label.toLowerCase()}`)
    } catch (e) {
      setError((e as Error).message)
    } finally { setTplEditSaving(false) }
  }

  const attachDeal = (deal: DealHit | null) => run(async () => {
    await patchQuote(quoteId, { hsDealId: deal?.hsDealId ?? null })
    setStartOpen(false)
    await reload()
  })

  const workArea = !preview ? (
        <div className="bwork">
          <Picker
            products={products}
            mode={mode}
            filter={lineFilter}
            onFilter={setLineFilter}
            search={search}
            onSearch={setSearch}
            onAdd={addProduct}
            disabled={ro}
          />
          <div className="canvas">
            <div className="canvas__scroll">
              {groups.filter((g) => !g.parentGroupId).map((g) => {
                const children = groups.filter((c) => c.parentGroupId === g.id)
                // Decision 87: reorder within a group — swap sort with the neighbour.
                const moveLine = (it: QuoteItemRow, inGroupId: string, dir: -1 | 1) => run(async () => {
                  const list = itemsByGroup.get(inGroupId) ?? []
                  const idx = list.findIndex((x) => x.id === it.id)
                  const other = list[idx + dir]
                  if (!other) return
                  if (it.sort === other.sort) {
                    // Legacy rows can share a sort value — normalise the group first.
                    for (let i = 0; i < list.length; i++) await patchQuoteItem(quoteId, { id: list[i].id, sort: i })
                    await patchQuoteItem(quoteId, { id: it.id, sort: idx + dir })
                    await patchQuoteItem(quoteId, { id: other.id, sort: idx })
                  } else {
                    await patchQuoteItem(quoteId, { id: it.id, sort: other.sort })
                    await patchQuoteItem(quoteId, { id: other.id, sort: it.sort })
                  }
                  await reload()
                })
                const renderLine = (it: QuoteItemRow, inGroup: typeof g) => (
                  <LineRow
                    key={it.id} item={it} group={inGroup} mode={mode}
                    product={it.hsProductId ? productById.get(it.hsProductId) : undefined}
                    stale={staleLineIds.includes(it.id)}
                    flash={flashId === it.id}
                    disabled={ro || busy}
                    accepted={acceptedQtyById ? (acceptedQtyById.get(it.id) ?? null) : undefined}
                    onOpen={() => setSlide({ kind: 'line', itemId: it.id })}
                    onQty={(q) => run(async () => {
                      const res = await patchQuoteItem(quoteId, { id: it.id, qty: q })
                      setDetail((d) => d && ({
                        ...d,
                        items: d.items.map((x) => x.id === it.id ? { ...x, qty: q } : x),
                        totals: res.totals,
                      }))
                    })}
                    onSell={(s) => run(async () => {
                      const res = await patchQuoteItem(quoteId, { id: it.id, sellPrice: s })
                      setDetail((d) => d && ({
                        ...d,
                        items: d.items.map((x) => x.id === it.id ? { ...x, sellPrice: s } : x),
                        totals: res.totals,
                      }))
                    })}
                    onRemove={() => run(async () => { await deleteQuoteItem(quoteId, it.id); await reload() })}
                    onMove={ro ? undefined : (dir) => moveLine(it, inGroup.id, dir)}
                  />
                )
                return (
                <section key={g.id} className={`group ${activeGroup === g.id ? 'group--active' : ''}`} onClick={() => setActiveGroup(g.id)}>
                  <div className="group__head">
                    <input
                      className="gname"
                      key={g.name}
                      defaultValue={g.name}
                      disabled={ro}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={(e) => e.target.value !== g.name && run(async () => {
                        await patchQuoteGroup(quoteId, { id: g.id, name: e.target.value })
                        await reload()
                      })}
                    />
                    {g.kind === 'choose_one' && <span className="pill pill--info">customer picks ONE</span>}
                    {g.kind === 'section' && !g.choiceSet && (g.optional || !ro) && (
                      <label className="tiny muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: ro ? 'default' : 'pointer' }}
                        onClick={(e) => e.stopPropagation()}
                        title="the customer can include or exclude this WHOLE section on the quote page">
                        <input type="checkbox" checked={g.optional} disabled={ro}
                          onChange={(e) => run(async () => {
                            await patchQuoteGroup(quoteId, { id: g.id, optional: e.target.checked })
                            await reload()
                          })} />
                        Optional section
                      </label>
                    )}
                    {g.kind === 'section' && !g.choiceSet && g.optional && (
                      <label className="tiny muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: ro ? 'default' : 'pointer' }}
                        onClick={(e) => e.stopPropagation()}
                        title="Whether this section STARTS included when the customer opens the quote (they can change it)">
                        <input type="checkbox" checked={g.preselected} disabled={ro}
                          onChange={(e) => run(async () => {
                            await patchQuoteGroup(quoteId, { id: g.id, preselected: e.target.checked })
                            await reload()
                          })} />
                        starts selected
                      </label>
                    )}
                    {/* Decision 117: sections sharing a choice set are customer
                        ALTERNATIVES — pick a set letter on two+ sections. */}
                    {g.kind === 'section' && (!ro || g.choiceSet) && (
                      <select className="select" style={{ fontSize: 11.5, padding: '2px 6px' }}
                        value={g.choiceSet ?? ''} disabled={ro}
                        title="Sections sharing a set are alternatives — the customer picks ONE of them"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => run(async () => {
                          await patchQuoteGroup(quoteId, { id: g.id, choiceSet: e.target.value || null })
                          await reload()
                        })}>
                        <option value="">Standalone</option>
                        {['A', 'B', 'C', 'D'].map((k) => <option key={k} value={k}>Alternative — set {k}</option>)}
                      </select>
                    )}
                    {g.kind === 'section' && g.choiceSet && (
                      <label className="tiny muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: ro ? 'default' : 'pointer' }}
                        onClick={(e) => e.stopPropagation()}
                        title="The set's default pick when the customer opens the quote. No default in the set = the customer may choose none.">
                        <input type="checkbox" checked={g.preselected} disabled={ro}
                          onChange={(e) => run(async () => {
                            await patchQuoteGroup(quoteId, { id: g.id, preselected: e.target.checked })
                            await reload()
                          })} />
                        default option
                      </label>
                    )}
                    {/* Decision 119: presentation only — the quotation shows this
                        section's pricing as one-off + per-year subtotals with no
                        per-line prices. Building, options and quantities unchanged. */}
                    {g.kind === 'section' && (g.subtotalOnly || !ro) && (
                      <label className="tiny muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: ro ? 'default' : 'pointer' }}
                        onClick={(e) => e.stopPropagation()}
                        title="Show this section's pricing as a subtotal only — no per-line prices on the quotation, just one-off and per-year figures for the section">
                        <input type="checkbox" checked={g.subtotalOnly} disabled={ro}
                          onChange={(e) => run(async () => {
                            await patchQuoteGroup(quoteId, { id: g.id, subtotalOnly: e.target.checked })
                            await reload()
                          })} />
                        subtotal pricing
                      </label>
                    )}
                    {multiSite && (
                      <select
                        className="select" style={{ fontSize: 11.5, padding: '2px 6px' }}
                        value={g.siteId ?? ''}
                        disabled={ro}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => run(async () => {
                          await patchQuoteGroup(quoteId, { id: g.id, siteId: e.target.value || null })
                          await reload()
                        })}
                      >
                        <option value="">All sites</option>
                        {sites.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.postcode ?? '—'})</option>)}
                      </select>
                    )}
                    <span style={{ flex: 1 }} />
                    {activeGroup === g.id
                      ? <span className="pill pill--brand">◉ Adding here</span>
                      : <button className="btn btn--sm btn--ghost" onClick={(e) => { e.stopPropagation(); setActiveGroup(g.id) }}>Add here</button>}
                    {!ro && (
                      <>
                        <button className="btn btn--sm" title="Copy this section, its lines AND its option blocks — then amend, e.g. per school"
                          onClick={(e) => { e.stopPropagation(); run(async () => { await addQuoteGroup(quoteId, { cloneOf: g.id }); await reload() }) }}>⧉ Copy section</button>
                        <button className="gline__x" title="Remove group and its lines"
                          onClick={(e) => { e.stopPropagation(); run(async () => { await deleteQuoteGroup(quoteId, g.id); await reload() }) }}>×</button>
                      </>
                    )}
                  </div>
                  {/* Section heading bar (Mark, 2026-08-01) — brand bar, white text. */}
                  <div className="secbar">
                    <span>Item</span><span>Qty</span>
                    <span style={{ textAlign: 'right' }}>Unit {mode === 'agent' ? '(list, locked)' : 'sell'}</span>
                    <span style={{ textAlign: 'right' }}>Total /yr · once</span><span />
                  </div>
                  {(itemsByGroup.get(g.id) ?? []).map((it) => renderLine(it, g))}
                  {/* Decision 69: option-choice blocks nest inside the section. */}
                  {children.map((c, ci) => (
                    <div key={c.id} className={`oblock ${activeGroup === c.id ? 'oblock--active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setActiveGroup(c.id) }}>
                      <div className="oblock__head">
                        <span className="pill pill--info">Option choice {children.length > 1 ? ci + 1 : ''}</span>
                        <input
                          className="gname" style={{ fontSize: 13 }}
                          key={c.name}
                          defaultValue={c.name}
                          disabled={ro}
                          onClick={(e) => e.stopPropagation()}
                          onBlur={(e) => e.target.value !== c.name && run(async () => {
                            await patchQuoteGroup(quoteId, { id: c.id, name: e.target.value })
                            await reload()
                          })}
                        />
                        <span className="tiny muted">customer picks ONE of {itemsByGroup.get(c.id)?.length ?? 0}</span>
                        <span style={{ flex: 1 }} />
                        {activeGroup === c.id
                          ? <span className="pill pill--brand">◉ Adding here</span>
                          : <button className="btn btn--sm btn--ghost" onClick={(e) => { e.stopPropagation(); setActiveGroup(c.id) }}>Add here</button>}
                        {!ro && (
                          <button className="gline__x" title="Remove this option block and its options"
                            onClick={(e) => { e.stopPropagation(); run(async () => { await deleteQuoteGroup(quoteId, c.id); await reload() }) }}>×</button>
                        )}
                      </div>
                      {(itemsByGroup.get(c.id) ?? []).map((it) => renderLine(it, c))}
                      {(itemsByGroup.get(c.id) ?? []).length === 0 && (
                        <div className="group__add"><span>add the alternative products from the catalogue — Option 1, OR Option 2, OR…</span></div>
                      )}
                    </div>
                  ))}
                  <div className="group__add" onClick={(e) => e.stopPropagation()}>
                    {!ro && (
                      <button className="btn btn--sm" title="An Option 1 / OR Option 2 / OR Option 3 block inside this section — the customer picks exactly one"
                        onClick={() => run(async () => {
                          const res = await addQuoteGroup(quoteId, { kind: 'choose_one', parentGroupId: g.id })
                          await reload(); setActiveGroup(res.id)
                        })}>+ Option choice</button>
                    )}
                    {mode === 'internal' && !ro && (
                      <button className="btn btn--sm" onClick={() => run(async () => {
                        await addQuoteItem(quoteId, { groupId: g.id, adhoc: true, name: 'Ad-hoc line', price: 0 })
                        await reload()
                      })}>+ Ad-hoc line</button>
                    )}
                    <span>add products from the catalogue on the left</span>
                  </div>
                </section>
              )})}
              <div className="addgroup">
                {!ro && (
                  <button className="btn" onClick={() => run(async () => {
                    const res = await addQuoteGroup(quoteId, { kind: 'section' })
                    await reload(); setActiveGroup(res.id)
                  })}>+ Section</button>
                )}
                {surveyable && (
                  <label className="chip chip--action" style={{ marginLeft: 'auto' }}>
                    <input type="checkbox" checked={quote.eccNotice} disabled={ro} onChange={(e) => run(async () => {
                      await patchQuote(quoteId, { eccNotice: e.target.checked })
                      await reload()
                    })} />{' '}
                    Show “excess construction charges may apply following survey” on the customer page
                    <span className="tiny muted"></span>
                  </label>
                )}
              </div>
            </div>
            <TotalsBar detail={detail} mode={mode} onEditFee={() => setSlide({ kind: 'send' })} />
          </div>
        </div>
  ) : (
        <Preview
          detail={detail} partner={partner} settings={settings}
          productById={productById} termsSets={termsSets}
          selections={selections} onSelections={setSelections}
          amendMode={amending}
        />
  )

  // ---- decision 240: the quote RECORD page + the builder OVERLAY -----------------
  // Everything ABOUT the quote (customer, deal, value, validity, decision,
  // what happened after acceptance, history) is one scrolling page laid out
  // as the workflow. The line editor — Build / Customer preview — is an
  // overlay opened from it, and a DRAFT's editing session ends with an
  // explicit choice: Save to draft, or Discard changes (server snapshot).
  // The template editor (#125) keeps its original full-workspace layout.
  const openEditor = () => {
    if (ro || templateMode) { setEditing(true); return }
    setEditorBusy(true); setError(null)
    quoteEditor(quoteId, 'open')
      .then((r) => { setEditSession({ resumed: Boolean(r.resumed), since: r.snapshotAt ?? null, by: r.by ?? null, mine: r.mine !== false }); setEditing(true) })
      .catch((e: Error) => setError(e.message))
      .finally(() => setEditorBusy(false))
  }
  const closeEditor = () => { setEditing(false); setPreview(false); if (amending) setAmending(false) }
  const saveEditor = () => run(async () => {
    await quoteEditor(quoteId, 'save')
    setEditSession(null)
    closeEditor()
    await reload()
    showToast('Draft saved')
  })
  const discardEditor = (fromRecord = false) => {
    const since = editSession?.since ?? detail?.quote.editSnapshotAt ?? null
    const when = since ? new Date(since).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'the editor opened'
    // Decision 241: a colleague's session is named before it is thrown away.
    const mine = editSession ? editSession.mine : (detail?.quote.editSnapshotMine ?? true)
    const by = editSession?.by ?? detail?.quote.editSnapshotBy ?? null
    const msg = !mine && by
      ? `These are ${by}'s edits (since ${when}), not yours. Discard them anyway? The lines go back exactly as they were before ${by} started; the draft itself is kept.`
      : `Discard the changes made since ${when}? The lines go back exactly as they were; the draft itself is kept.`
    if (!window.confirm(msg)) return
    run(async () => {
      const r = await quoteEditor(quoteId, 'discard')
      setEditSession(null)
      if (!fromRecord) closeEditor()
      await reload()
      showToast(`Changes discarded — ${r.restored?.items ?? 0} line${r.restored?.items === 1 ? '' : 's'} restored`)
    })
  }

  // The record card's section table: top-level sections with their option
  // blocks, line counts and subtotals at DEFAULT selections (sell side).
  const recLines: QuoteLine[] = items.map((it) => ({
    id: it.id, groupId: it.groupId, hsProductId: it.hsProductId, name: it.name,
    description: it.description, qty: it.qty, qtyEditable: it.qtyEditable,
    qtyMin: it.qtyMin, qtyMax: it.qtyMax, subjectToSurvey: it.subjectToSurvey,
    isAdhoc: it.isAdhoc, listPrice: it.listPrice, buyPrice: it.buyPrice ?? it.listPrice,
    sellPrice: it.sellPrice, recurring: it.recurring, billingFrequency: it.billingFrequency,
    termMonths: it.termMonths, optionality: it.optionality, preselected: it.preselected, sort: it.sort,
  }))
  const recGroups = groups.map((g) => ({
    id: g.id, kind: g.kind, parentGroupId: g.parentGroupId,
    optional: g.optional, preselected: g.preselected, choiceSet: g.choiceSet ?? null, sort: g.sort,
  }))
  // On an ACCEPTED quote the card shows what the customer TOOK (#120's rule —
  // the frozen record is the truth), not the offer at default selections:
  // per section, the accepted lines at their accepted quantities and prices.
  const recSubtotals = (() => {
    if (!acceptedQtyById || !detail.acceptance) return sectionSubtotals(recLines, {}, recGroups)
    const byId = new Map(groups.map((g) => [g.id, g]))
    const out = new Map<string, { oneOffSell: number; annualSell: number; termSell?: number }>()
    for (const f of detail.acceptance.selections) {
      const it = items.find((x) => x.id === f.itemId)
      const g = it?.groupId ? byId.get(it.groupId) : undefined
      const sid = g ? (g.kind === 'choose_one' ? g.parentGroupId : g.id) : null
      if (!sid) continue
      const cur = out.get(sid) ?? { oneOffSell: 0, annualSell: 0 }
      const sell = Math.round(f.unitPrice * f.qty * 100) / 100
      const years = termYears({ recurring: f.recurring, termMonths: f.termMonths })
      if (f.recurring && years < 1) cur.termSell = Math.round(((cur.termSell ?? 0) + annualised(sell, { billingFrequency: f.billingFrequency }) * years) * 100) / 100
      else if (f.recurring) cur.annualSell = Math.round((cur.annualSell + annualised(sell, { billingFrequency: f.billingFrequency })) * 100) / 100
      else cur.oneOffSell = Math.round((cur.oneOffSell + sell) * 100) / 100
      out.set(sid, cur)
    }
    return out
  })()
  const takenIn = (groupId: string) => (itemsByGroup.get(groupId) ?? []).filter((it) => !acceptedQtyById || acceptedQtyById.has(it.id)).length
  const topSections = groups.filter((g) => !g.parentGroupId)
  const dealUrl = quote.hsDealId ? hubspotDealUrl(hsOpts?.portalId ?? null, quote.hsDealId) : null
  const dealFieldsMissing = mode === 'internal' && (!quote.dealCategory || !quote.dealType)
  const stepClass = (done: boolean, warn = false) => `qcard__step${done ? ' qcard__step--done' : warn ? ' qcard__step--warn' : ''}`
  const editorLabel = ro ? 'View lines & preview →' : items.length === 0 ? 'Add lines →' : 'Edit lines & preview →'

  // Decision 86 fields (Category / Deal type / Source) — one renderer, used
  // in the Deal card. Options mirror LIVE from HubSpot via /api/hs-options.
  const dealField = (field: 'dealCategory' | 'dealType' | 'dealSource') => {
    const opts = field === 'dealCategory' ? hsOpts?.dealCategory.options
      : field === 'dealType' ? hsOpts?.dealType?.options
      : hsOpts?.dealSource.options
    const value = quote[field]
    const required = field === 'dealCategory' || field === 'dealType'   // decision 89: Deal Type gates send too
    const label = field === 'dealCategory' ? 'Category' : field === 'dealType' ? 'Deal type' : 'Source'
    const title = field === 'dealCategory'
      ? 'HubSpot Deal Category — REQUIRED before an internal quote can send. Options are edited in HubSpot.'
      : field === 'dealType'
        ? 'HubSpot Deal Type — REQUIRED before an internal quote can send. Options are edited in HubSpot.'
        : 'HubSpot Deal Source — marketing attribution. Options are edited in HubSpot.'
    const control = ro
      ? <span>{opts?.find((o) => o.value === value)?.label ?? value ?? '—'}</span>
      : field === 'dealType' && opts && opts.length > 0 && opts.length <= 4 ? (
        // HubSpot's dealtype IS a radio — render it as one (decision 89).
        <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {opts.map((o) => (
            <label key={o.value} className="small" style={{ display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="deal-type" value={o.value}
                checked={value === o.value}
                onChange={() => run(async () => { await patchQuote(quoteId, { dealType: o.value }); await reload() })} />
              {o.label}
            </label>
          ))}
        </span>
      ) : opts && opts.length > 0 ? (
        <select className="select" style={required && !value ? { borderColor: 'var(--warn)', background: 'var(--warn-bg)' } : undefined}
          value={value ?? ''}
          onChange={(e) => run(async () => { await patchQuote(quoteId, { [field]: e.target.value || null }); await reload() })}>
          <option value="">—</option>
          {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          {value && !opts.some((o) => o.value === value) && <option value={value}>{value}</option>}
        </select>
      ) : (
        <input className="input" key={`${field}-${value ?? ''}`} defaultValue={value ?? ''}
          placeholder={hsOpts?.configured ? '…' : 'free text — HubSpot not connected'} style={{ width: 160 }}
          onBlur={(e) => (e.target.value.trim() || null) !== value && run(async () => {
            await patchQuote(quoteId, { [field]: e.target.value.trim() || null }); await reload()
          })}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />
      )
    return (
      <div key={field} className={`qfact${required && !value && !ro ? ' qfact--warn' : ''}`} title={title}>
        <span>{label}{required && !value && !ro ? ' · required to send' : ''}</span>
        <span>{control}</span>
      </div>
    )
  }

  // Status-driven banners — shared by the record (under the header) and, for
  // the ones about LINES, the overlay.
  const importedBanner = quote.status === 'imported' && (
    <div className="bbanner">
      ⇣ <span>
        <strong>Imported from HubSpot</strong> — the customer quote for this deal lives in Quotient;
        this record tracks it in the portal ({quote.importedAt ? `imported ${formatUKDate(quote.importedAt)}` : 'imported'}).
        To re-quote it here, use <strong>Supersede</strong> — the new draft keeps the same HubSpot deal.
      </span>
      {quote.quotientLink && (
        <a className="btn btn--sm" href={quote.quotientLink} target="_blank" rel="noreferrer">Open in Quotient →</a>
      )}
      {dealUrl && <a className="btn btn--sm" href={dealUrl} target="_blank" rel="noreferrer">Open deal →</a>}
    </div>
  )
  const lockedBanner = ro && (
    <div className="bbanner">
      <strong>This quote is {quote.status}</strong>
      <span>
        — sent quotes never change or reprice.
        {quote.status === 'superseded'
          ? ' A newer version exists — find it in the quotes list.'
          : ' Use “Supersede” to revise it as a new version.'}
      </span>
    </div>
  )
  const staleBanner = !ro && staleLineIds.length > 0 && (
    <div className="bbanner">
      ⚠ <span>
        <strong>The catalogue has moved under this draft</strong>
        {' — '}
        {[
          detail.stalePriceIds.length > 0 && `${detail.stalePriceIds.length} line${detail.stalePriceIds.length === 1 ? '' : 's'} with newer prices`,
          detail.staleDescriptionIds.length > 0 && `${detail.staleDescriptionIds.length} with newer descriptions`,
        ].filter(Boolean).join(' · ')}. Sent quotes never reprice.
      </span>
      {detail.stalePriceIds.length > 0 && (
        <button className="btn btn--sm" disabled={busy} onClick={() => run(async () => {
          const res = await refreshQuotePrices(quoteId, 'prices')
          await reload()
          showToast(`Refreshed prices on ${res.refreshed} line${res.refreshed === 1 ? '' : 's'} — descriptions untouched`)
        })}>Refresh prices</button>
      )}
      {detail.staleDescriptionIds.length > 0 && (
        <button className="btn btn--sm" disabled={busy} onClick={() => run(async () => {
          const res = await refreshQuotePrices(quoteId, 'descriptions')
          await reload()
          showToast(`Refreshed descriptions on ${res.refreshed} line${res.refreshed === 1 ? '' : 's'} — prices untouched`)
        })}>Refresh descriptions</button>
      )}
    </div>
  )
  const costVarBanner = !ro && mode === 'internal' && items.some((i) => i.costVariedAt && !i.costVariationId) && (
    <div className="bbanner">
      ⚠ <span>
        <strong>{items.filter((i) => i.costVariedAt && !i.costVariationId).length} cost change{items.filter((i) => i.costVariedAt && !i.costVariationId).length === 1 ? '' : 's'} without a variation entry</strong>
        {' '}— every cost override needs supporting evidence: a supplier quote or a note, logged with your name.
      </span>
      <button className="btn btn--sm btn--primary" disabled={busy} onClick={() => setSlide({ kind: 'costVariation' })}>
        Log cost variation
      </button>
    </div>
  )
  const errorBanner = error && <div className="bbanner" style={{ background: 'var(--stop-bg)', color: 'var(--stop)', borderColor: '#f2c4c4' }}>{error}</div>

  // Decision 191/217/218: the acceptance strip — the frozen record at a glance.
  const acceptanceStrip = quote.status === 'accepted' && detail.acceptance && acceptedQtyById && (
    <>
      <div className="bbanner bbanner--accepted" data-testid="acceptance-strip" style={{ borderRadius: 6, border: '1px solid #bfe3c8' }}>
        ✓ <span>
          <strong>Accepted {formatUKDate(detail.acceptance.acceptedAt)}</strong>
          {detail.acceptance.accepterName ? <> by {detail.acceptance.accepterName}</> : null}
          {detail.acceptance.onBehalf ? <> <span className="pill pill--info" title="Recorded by Wave9 staff on the customer's instruction">on the customer's instruction</span></> : null}
          {detail.acceptance.version > 1 && detail.acceptance.amendedAt ? <> · <strong title={detail.acceptance.amendAuthorityNote ?? undefined}>amended {formatUKDate(detail.acceptance.amendedAt)}{detail.acceptance.amendedByName ? ` by ${detail.acceptance.amendedByName}` : ''} (v{detail.acceptance.version})</strong></> : null}
          {' '}· {detail.acceptance.poReference ? `PO ${detail.acceptance.poReference}` : detail.acceptance.poToFollow ? 'PO to follow' : 'no PO'}
          {' '}· {formatGBPCompact(detail.totals.contractValueSell)} contract value
          {' '}· <strong>{acceptedQtyById.size} of {items.length} offered line{items.length === 1 ? '' : 's'} taken</strong>
          {acceptedQtyById.size < items.length ? <span className="muted"> — the rest are marked "not taken" in the editor</span> : null}
        </span>
        <button className="btn btn--sm btn--primary" onClick={() => setSlide({ kind: 'acceptance' })}
          title="The frozen acceptance record — who, when, on what terms, and every line taken or left">
          What was accepted
        </button>
        {/* Decision 217 (amended #240): the customer's own accepted page — a fresh
            personal link for the accepter (#118's mint), shown with Open / Copy;
            nothing emailed. Minting writes a token + event, so it is a click, not a load. */}
        {!customerLink && (
          <button className="btn btn--sm" disabled={busy} data-testid="customer-view-link"
            title="Mint the accepter's link to the customer version of the accepted quote — shown below with Open and Copy; no email is sent"
            onClick={() => {
              const accepterEmail = (detail.acceptance?.accepterEmail ?? '').toLowerCase()
              const rec = detail.recipients.find((r) => r.email.toLowerCase() === accepterEmail) ?? detail.recipients[0]
              if (!rec) { setError('This quote has no recipients to mint a link for.'); return }
              run(async () => {
                const r = await mintRecipientLink(quoteId, rec.id)
                setCustomerLink({ url: r.url, copied: false })
              })
            }}>
            Customer view
          </button>
        )}
        {/* Decision 218: revise the acceptance without a new version — link
            for the customer, or recorded on their instruction. Greyed with
            the reason once a split / PO derives from the record; a provisioning
            pack only warns (#247 — its HubSpot records are fixed by hand). */}
        {role !== null && !amending && (
          <button className="btn btn--sm" disabled={busy || detail.acceptance.amendBlockers.length > 0} data-testid="amend-acceptance"
            title={detail.acceptance.amendBlockers.length > 0
              ? `Cannot amend — ${detail.acceptance.amendBlockers.map((b) => b.label).join('; ')}. Supersede the quote instead.`
              : detail.acceptance.amendWarnings.length > 0
                ? `Amend the acceptance — same quote, same deal, no new version. ⚠ ${detail.acceptance.amendWarnings.map((w) => w.label).join('; ')}`
                : 'The customer forgot an option or needs a quantity changed? Amend the acceptance — same quote, same deal, no new version'}
            onClick={() => setSlide({ kind: 'amendAcceptance' })}>
            Amend acceptance
          </button>
        )}
      </div>
      {customerLink && (
        <div className="qfact" data-testid="customer-view-field" style={{ marginTop: 8 }}>
          <span>Customer view</span>
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
            <input className="input" style={{ flex: 1, fontSize: 12 }} readOnly value={customerLink.url} onFocus={(e) => e.target.select()} />
            <a className="btn btn--sm" href={customerLink.url} target="_blank" rel="noreferrer" title="Open the customer's accepted page in a new tab">Open ↗</a>
            <button className="btn btn--sm" title="Copy the link to paste into your own email"
              onClick={() => { void navigator.clipboard.writeText(customerLink.url).then(() => { setCustomerLink({ ...customerLink, copied: true }); showToast('Customer link copied') }).catch(() => {}) }}>
              {customerLink.copied ? '✓ Copied' : 'Copy'}
            </button>
            <button className="btn btn--sm btn--ghost" title="Hide the link" onClick={() => setCustomerLink(null)}>✕</button>
          </span>
        </div>
      )}
    </>
  )
  // Decision 218: amend mode — the preview is live, seeded from the record.
  const amendStrip = quote.status === 'accepted' && amending && detail.acceptance && (
    <div className="bbanner" data-testid="amend-strip">
      ✎ <span>
        <strong>Amending the acceptance (version {detail.acceptance.version})</strong>
        {' '}— change options and quantities in the customer preview below to what the customer now wants, then record it on their instruction. Prices never move; a new price or line is a Supersede.
      </span>
      <button className="btn btn--sm btn--primary" disabled={busy} onClick={() => setSlide({ kind: 'amendAcceptance' })}>Record amendment</button>
      <button className="btn btn--sm" disabled={busy} onClick={() => { setAmending(false); setPreview(false); setEditing(false) }}>Cancel</button>
    </div>
  )

  // The quote RECORD page (not the template editor).
  const recordPage = (
    <>
      {/* Header: the title (editable while draft — #128: it takes the whole bar
          bar the back button) and, underneath, reference · status · version ·
          HubSpot link, with the way into the line editor on the right. */}
      <div className="bbar">
        <span className={`bbar__title ${ro ? '' : 'bbar__title--edit'}`}>
          <input
            className="qtitle"
            key={quote.title}
            defaultValue={quote.title}
            disabled={ro}
            title="Wave9 standard: quote titles carry the site postcode"
            onBlur={(e) => e.target.value !== quote.title && commitTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          />
          {!ro && <span className="bbar__pencil" aria-hidden="true">✎</span>}
        </span>
        <button className="bbar__back" onClick={onBack}>← All quotes</button>
      </div>
      <div className="bbar2">
        <span className="bbar__ref">{quote.quoteNumber}</span>
        <span className="pill">{quote.status}</span>
        {quote.version > 1 && <span className="pill pill--info" title="Supersession version">v{quote.version}</span>}
        {quote.hsDealId && (
          dealUrl
            ? <a className="chip chip--action" href={dealUrl} target="_blank" rel="noreferrer" title="Open the deal in HubSpot">⛁ Deal {quote.hsDealId} ↗</a>
            : <span className="chip small">⛁ Deal {quote.hsDealId}</span>
        )}
        {detail.partner.isTest && <span className="pill pill--warn" title="Test partner — nothing here reaches HubSpot or a customer">test</span>}
      </div>
      {importedBanner}
      {lockedBanner}
      {errorBanner}

      <div className="qrec__grid">
        {/* 1 · Customer & sites -------------------------------------------- */}
        <section className="qcard" data-testid="card-customer">
          <div className="qcard__head">
            <h3><span className={stepClass(detail.customer != null, true)}>1</span> Customer &amp; sites</h3>
            <span className="qcard__hint">{detail.customer ? (multiSite ? `${sites.length} sites` : 'who the quote is for') : 'needed before send'}</span>
            <span className="qcard__spacer" />
            {!ro && (
              <button className="btn btn--sm" onClick={() => setStartOpen(true)}
                title={detail.customer ? 'Change the customer — the sites and title are re-seeded' : 'Pick the school or trust this quote is for'}>
                {detail.customer ? 'Change customer' : 'Pick customer'}
              </button>
            )}
          </div>
          <div className="qcard__body">
            {detail.customer ? (
              <>
                <div className="qfact"><span>Organisation</span><span><strong>{detail.customer.name}</strong></span></div>
                {detail.customer.urn && <div className="qfact"><span>{isRegisterUrn(detail.customer.urn) ? 'Site ref (not DfE)' : 'URN'}</span><span>{detail.customer.urn}</span></div>}
                {(detail.customer.contactName || detail.customer.contactEmail) && (
                  <div className="qfact"><span>Contact</span><span>{detail.customer.contactName ?? ''}{detail.customer.contactName && detail.customer.contactEmail ? ' · ' : ''}{detail.customer.contactEmail ?? ''}</span></div>
                )}
                <div className="qfact">
                  <span>{sites.length > 1 ? `Sites (${sites.length})` : 'Site'}</span>
                  <span>
                    {sites.length === 0 ? '—' : sites.map((s) => `${s.name}${s.postcode ? ` (${s.postcode})` : ''}${s.numberOnRoll != null ? ` · ${s.numberOnRoll} on roll` : ''}`).join(', ')}
                    {!ro && <> <button className="btn btn--sm btn--ghost" onClick={() => setSlide({ kind: 'sites' })} title="Add or remove the sites this quote covers">✎ Sites</button></>}
                  </span>
                </div>
              </>
            ) : (
              <div className="qempty">⚠ No customer yet — <b>pick one before sending</b>. The customer seeds the site and the title.</div>
            )}
            {detail.renewal && (
              <div className="qfact">
                <span>Renews</span>
                <span>
                  <button className="chip chip--action" onClick={() => setSlide({ kind: 'contract' })}
                    title="See the contract this renews — dates, values, owner and the lines the customer is on today.">
                    ↻ {detail.renewal.dealName ?? `deal ${detail.renewal.hsDealId}`}
                    {detail.renewal.contractEndDate ? ` · ends ${detail.renewal.contractEndDate.split('-').reverse().join('/')}` : ''}
                    {detail.renewal.lineItems && detail.renewal.lineItems.length > 0 ? ` · ${detail.renewal.lineItems.length} line${detail.renewal.lineItems.length === 1 ? '' : 's'}` : ''}
                  </button>
                </span>
              </div>
            )}
          </div>
        </section>

        {/* 2 · Deal ----------------------------------------------------------- */}
        <section className="qcard" data-testid="card-deal">
          <div className="qcard__head">
            <h3><span className={stepClass(!dealFieldsMissing && quote.ownerId != null, dealFieldsMissing)}>2</span> Deal</h3>
            <span className="qcard__hint">{mode === 'internal' ? 'what the HubSpot deal will carry' : 'owner and the HubSpot deal'}</span>
          </div>
          <div className="qcard__body">
            <div className="qfact">
              <span>HubSpot deal</span>
              <span>
                {mode === 'internal' ? (
                  <button className="chip chip--action" onClick={() => !ro && setStartOpen(true)} disabled={ro}
                    title="No HubSpot deal linked yet — sending will create one, or you can attach an existing direct deal here (update, never duplicate)">
                    {quote.hsDealId ? `⛁ Deal ${quote.hsDealId}` : '⛁ No deal yet'}
                  </button>
                ) : quote.hsDealId ? (
                  <span className="chip small" title="The HubSpot deal this quote reconciles to">⛁ Deal {quote.hsDealId}</span>
                ) : (
                  <span className="muted" title="The deal lands at send / on the first sweep once HubSpot writes are on">none yet</span>
                )}
                {dealUrl && <> <a className="chip chip--action" href={dealUrl} target="_blank" rel="noreferrer" title="Open the deal in HubSpot">HubSpot ↗</a></>}
              </span>
            </div>
            <div className="qfact">
              <span>Owner</span>
              <span>
                {people.length > 0 ? (
                  <select className="select" value={quote.ownerId ?? ''} disabled={busy}
                    title="the owner is reassignable even after send (holiday cover) — the HubSpot deal owner follows"
                    onChange={(e) => run(async () => { await patchQuote(quoteId, { ownerId: e.target.value || null }); await reload() })}>
                    <option value="">—</option>
                    {people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                ) : (detail.owner ? (detail.owner.name ?? detail.owner.email) : '—')}
              </span>
            </div>
            {mode === 'internal' && (['dealCategory', 'dealType', 'dealSource'] as const).map(dealField)}
            <div className="qfact" title="HubSpot Expected Booking Date — the close-date forecast">
              <span>Expected booking</span>
              <span>
                {ro ? (quote.expectedBookingDate ? formatUKDate(quote.expectedBookingDate) : '—') : (
                  <>
                    <input type="date" value={quote.expectedBookingDate ?? ''}
                      onChange={(e) => e.target.value && run(async () => { await patchQuote(quoteId, { expectedBookingDate: e.target.value }); await reload() })} />
                    {quote.expectedBookingDate && (
                      <button className="gline__x" title="Clear — the admin default (send date + N days) takes over at the next send"
                        onClick={() => run(async () => { await patchQuote(quoteId, { expectedBookingDate: null }); await reload() })}>×</button>
                    )}
                  </>
                )}
              </span>
            </div>
            <div className="qfact" title="Written to the HubSpot deal's Description — an internal note, never customer-visible" style={{ display: 'block' }}>
              <span style={{ display: 'block', marginBottom: 4 }}>Deal description</span>
              <span style={{ display: 'block', textAlign: 'left' }}>
                {ro ? (quote.dealDescription || <span className="muted">—</span>) : (
                  <input id="deal-desc" className="input" style={{ width: '100%' }}
                    key={`dd-${quote.dealDescription ?? ''}`}
                    defaultValue={quote.dealDescription ?? ''}
                    placeholder="Short internal description for the HubSpot deal…"
                    onBlur={(e) => (e.target.value.trim() || null) !== quote.dealDescription && run(async () => {
                      await patchQuote(quoteId, { dealDescription: e.target.value.trim() || null }); await reload()
                    })}
                    onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />
                )}
              </span>
            </div>
          </div>
        </section>

        {/* 3 · Lines & value --------------------------------------------------- */}
        <section className="qcard qcard--wide" data-testid="card-lines">
          <div className="qcard__head">
            <h3><span className={stepClass(items.length > 0)}>3</span> Lines &amp; value</h3>
            <span className="qcard__hint">
              {items.length === 0 ? 'nothing on the quote yet'
                : acceptedQtyById ? `what the customer accepted — ${acceptedQtyById.size} of ${items.length} offered line${items.length === 1 ? '' : 's'} taken${detail.totals.termMonths > 0 ? ` · longest term ${detail.totals.termMonths} months` : ''}`
                : `${items.length} line${items.length === 1 ? '' : 's'} in ${topSections.length} section${topSections.length === 1 ? '' : 's'}${detail.totals.termMonths > 0 ? ` · longest term ${detail.totals.termMonths} months` : ''}`}
            </span>
            <span className="qcard__spacer" />
            <button className="btn btn--primary" disabled={busy || editorBusy} data-testid="open-editor" onClick={openEditor}>{editorLabel}</button>
          </div>
          {quote.editSnapshotAt && !editing && (
            <div className="bbanner" data-testid="unsaved-edits">
              ✎ <span>
                <strong>
                  {quote.editSnapshotMine || !quote.editSnapshotBy ? 'Unsaved edits' : `${quote.editSnapshotBy} has unsaved edits`}
                  {' '}from {formatUKDate(quote.editSnapshotAt)} {new Date(quote.editSnapshotAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </strong>
                {' '}— the editor was left without saving or discarding. The lines below show the edited state.
                {!quote.editSnapshotMine && quote.editSnapshotBy ? ` Continuing picks up ${quote.editSnapshotBy}'s session; discarding throws their work away.` : ''}
              </span>
              <button className="btn btn--sm btn--primary" disabled={busy || editorBusy} onClick={openEditor}>Continue editing</button>
              <button className="btn btn--sm" disabled={busy} onClick={() => discardEditor(true)}>Discard those edits</button>
            </div>
          )}
          {staleBanner}
          {costVarBanner}
          <div className="qcard__body">
            {items.length === 0 ? (
              <div className="qempty">
                {ro ? 'This quote has no lines.' : <>No lines yet — open the editor to add products from the catalogue, in sections and option blocks.</>}
              </div>
            ) : (
              <table className="qlines">
                <thead>
                  <tr><th>Section</th><th className="num">{acceptedQtyById ? 'Taken / offered' : 'Lines'}</th><th className="num">Per year</th><th className="num">One-off</th></tr>
                </thead>
                <tbody>
                  {topSections.map((g) => {
                    const own = itemsByGroup.get(g.id) ?? []
                    const blocks = groups.filter((c) => c.parentGroupId === g.id)
                    const sub = recSubtotals.get(g.id)
                    return (
                      <Fragment key={g.id}>
                        <tr>
                          <td>
                            {g.name}
                            {g.choiceSet && <> <span className="pill pill--info tiny" title="Sections sharing a set are alternatives — the customer picks ONE">alternative · set {g.choiceSet}</span></>}
                            {g.optional && !g.choiceSet && <> <span className="pill tiny" title="The customer can include or exclude this whole section">optional{g.preselected ? '' : ' · starts excluded'}</span></>}
                          </td>
                          <td className="num">
                            {acceptedQtyById
                              ? `${takenIn(g.id) + blocks.reduce((n, b) => n + takenIn(b.id), 0)} / ${own.length + blocks.reduce((n, b) => n + (itemsByGroup.get(b.id)?.length ?? 0), 0)}`
                              : own.length + blocks.reduce((n, b) => n + (itemsByGroup.get(b.id)?.length ?? 0), 0)}
                          </td>
                          <td className="num">{sub ? formatGBP(sub.annualSell) : acceptedQtyById ? <span className="muted">not taken</span> : '—'}</td>
                          <td className="num">{sub ? formatGBP(sub.oneOffSell) : acceptedQtyById ? '' : '—'}</td>
                        </tr>
                        {blocks.map((b) => (
                          <tr key={b.id} className="qlines__opt">
                            <td>↳ {b.name} <span className="tiny muted">{acceptedQtyById
                              ? (() => { const t = (itemsByGroup.get(b.id) ?? []).find((it) => acceptedQtyById.has(it.id)); return t ? `customer chose ${t.name}` : 'no option taken' })()
                              : `customer picks one of ${itemsByGroup.get(b.id)?.length ?? 0}`}</span></td>
                            <td className="num">{acceptedQtyById ? `${takenIn(b.id)} / ${itemsByGroup.get(b.id)?.length ?? 0}` : itemsByGroup.get(b.id)?.length ?? 0}</td>
                            <td className="num"></td><td className="num"></td>
                          </tr>
                        ))}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
          {items.length > 0 && <TotalsBar detail={detail} mode={mode} onEditFee={() => setSlide({ kind: 'send' })} />}
          <div className="qcard__actions">
            <button className="btn" onClick={() => setSlide({ kind: 'attachments' })}>
              Attachments <span className="pill pill--info">{detail.attachments.length}</span>
            </button>
            {/* Decision 212: apply a (new) distributor quote to this DRAFT. */}
            {!ro && mode === 'internal' && role !== null && (
              <button className="btn" onClick={() => setSlide({ kind: 'supplierApply' })} data-testid="supplier-apply"
                title="Upload a distributor quote (Ingram .xlsx): matched SKUs take the new cost, new SKUs are added, and you decide whether sell prices move.">
                ↑ Apply distributor quote…
              </button>
            )}
            {/* Decision 211: the distributor quote this was imported from. */}
            {detail.supplierQuote && (() => {
              const sq = detail.supplierQuote
              const lapsed = sq.priceProtectionUntil != null && sq.priceProtectionUntil < new Date().toISOString().slice(0, 10)
              const past = sq.priceProtectionUntil != null && quote.validUntil != null && quote.validUntil.slice(0, 10) > sq.priceProtectionUntil
              return (
                <a className={`chip chip--action${lapsed || past ? ' chip--warn' : ''}`} href={supplierQuoteFileUrl(sq.id)} target="_blank" rel="noreferrer" data-testid="supplier-chip"
                  title={`Imported from ${sq.distributor} quote ${sq.reference} at ${sq.marginPct}% margin — click to download ${sq.filename}.${sq.priceProtectionText ? ` Price protection: ${sq.priceProtectionText}.` : ''}${past ? ' ⚠ This quote is valid beyond the distributor\'s price protection.' : ''}`}>
                  ↑ {sq.distributor} {sq.reference}
                  {sq.priceProtectionUntil ? ` · price held to ${formatUKDate(sq.priceProtectionUntil)}` : ''}
                  {lapsed ? ' ⚠ lapsed' : past ? ' ⚠ quote outlives it' : ''}
                </a>
              )
            })()}
            {/* Decision 213: the Xero draft PO for the distributor quote (staff only — cost). */}
            {detail.supplierQuote && role !== null && (
              detail.supplierQuote.po
                ? <a className="chip chip--action" href={detail.supplierQuote.po.url} target="_blank" rel="noreferrer" data-testid="supplier-po-chip"
                    title={`Xero purchase order ${detail.supplierQuote.po.number ?? ''} — ${detail.supplierQuote.po.status}. Click to open in Xero; update it from Supplier quotes.`}>
                    ⌂ {detail.supplierQuote.po.number ?? 'PO'} · {detail.supplierQuote.po.status}
                  </a>
                : <button className="chip chip--action" onClick={() => setSlide({ kind: 'raisePo' })} data-testid="supplier-po-raise"
                    title="Raise a DRAFT purchase order in Xero for this distributor's lines at the quote's current quantities and costs.">
                    ⌂ Raise PO…
                  </button>
            )}
            <span className="qcard__spacer" />
            <button className="btn" disabled={busy || items.length === 0}
              title="saves the structure — sections, options, lines, cover note, terms, attachments — never prices. New quotes from it reprice from today's catalogue."
              onClick={() => {
                setTplName(quote.title || 'New template'); setTplGlobal(false); setTplTarget('')
                setTplCover(quote.coverNote ?? ''); setTplFooter(quote.footerText ?? ''); setTplTerms(quote.termsSetId ?? '')
                fetchTemplates().then((r) => setTplList(r.templates.filter((t) => t.canManage))).catch(() => setTplList([]))
                setTplModal(true)
              }}>Save as template</button>
            <button className="btn" disabled={busy}
              title="clone as a fresh draft, repriced from today's catalogue — frozen prices never copy to a new deal"
              onClick={() => run(async () => {
                const res = await duplicateQuote(quoteId)
                showToast(res.skipped.length > 0
                  ? `Created ${res.quoteNumber} — skipped ${res.skipped.length} inactive product(s): ${res.skipped.join(', ')}`
                  : `Created ${res.quoteNumber} (repriced from today's catalogue)`)
                if (onOpen) onOpen(res.id)
              })}>Duplicate</button>
          </div>
        </section>

        {/* 4 · Send & decision ------------------------------------------------- */}
        <section className="qcard qcard--wide qcard--form" data-testid="card-decision">
          <div className="qcard__head">
            <h3><span className={stepClass(quote.status === 'accepted', quote.status === 'expired' || quote.status === 'declined' || quote.status === 'withdrawn')}>4</span> Send &amp; decision</h3>
            <span className="qcard__hint">
              {quote.status === 'draft' ? 'send opens the cover note, recipients and T&Cs'
                : quote.status === 'sent' || quote.status === 'viewed' ? 'with the customer — waiting on their decision'
                : quote.status === 'accepted' ? 'the customer has accepted'
                : quote.status === 'imported' ? 'a Quotient-era quote tracked here'
                : `this quote is ${quote.status}`}
            </span>
            <span className="qcard__spacer" />
            <span className="qfact" style={{ border: 0, padding: 0 }} title="How long the quotation stands — the customer page and nightly expiry both respect it">
              <span>Valid until</span>
              <span>
                {ro
                  ? (quote.validUntil ? formatUKDate(quote.validUntil) : '—')
                  : <input type="date" value={quote.validUntil ? quote.validUntil.slice(0, 10) : ''}
                      onChange={(e) => e.target.value && run(async () => { await patchQuote(quoteId, { validUntil: e.target.value }); await reload() })} />}
              </span>
            </span>
          </div>
          <div className="qcard__body">
            {acceptanceStrip}
            {/* Decision 246: on an ACCEPTED quote the cost variations logged against it
                (incl. entries inherited through its supersede chain, #159) sit with the
                decision — the evidence behind the frozen cost basis, without opening the
                editor. Staff on internal quotes only: cost never reaches a partner (#53). */}
            {quote.status === 'accepted' && role !== null && mode === 'internal' && detail.costVariations.length > 0 && (
              <div className="qfact" data-testid="accepted-cost-variations" style={{ alignItems: 'flex-start' }}
                title="Every cost-variation entry logged against this quote — description, who, when, the supporting document and each line's old → new unit cost">
                <span>Cost variations</span>
                <span style={{ textAlign: 'left', flex: 1 }}>
                  {detail.costVariations.map((v) => (
                    <div key={v.id} className="small" style={{ padding: '3px 0' }}>
                      <strong>{formatUKDate(v.createdAt)}</strong> · {v.createdBy}
                      {v.inherited && <> <span className="pill pill--info tiny" title="Logged on a quote this one supersedes — the varied costs carried forward.">logged on {v.loggedOnNumber}</span></>}
                      {v.evidenceFilename && <> · <a href={costEvidenceUrl(v.id)} target="_blank" rel="noreferrer" title="Open the supporting document">{v.evidenceFilename}</a></>}
                      <div className="muted">{v.description}</div>
                      <div className="tiny muted">{v.lines.map((l) => `${l.lineName}: £${l.oldCost.toFixed(2)} → £${l.newCost.toFixed(2)}`).join(' · ')}</div>
                    </div>
                  ))}
                </span>
              </div>
            )}
            <div className="qfact" title="Recipients are set in the Send panel — each gets a personal link">
              <span>Recipients</span>
              <span>{detail.recipients.length > 0 ? detail.recipients.map((r) => r.name || r.email).join(', ') : <span className="muted">{ro ? '—' : 'added in the Send panel'}</span>}</span>
            </div>
            {quote.status === 'draft' && (
              <div className="qfact" style={{ border: 0 }}>
                <span>Before send</span>
                <span className="small">
                  {[
                    !detail.customer && 'customer',
                    mode === 'internal' && !quote.dealCategory && 'category',
                    mode === 'internal' && !quote.dealType && 'deal type',
                    mode === 'internal' && !quote.ownerId && 'owner',
                    items.length === 0 && 'at least one line',
                  ].filter(Boolean).length === 0
                    ? <span className="pill pill--ok">ready — recipients, cover note and T&Cs are set in the Send panel</span>
                    : <span className="pill pill--warn">still needs: {[
                        !detail.customer && 'customer',
                        mode === 'internal' && !quote.dealCategory && 'category',
                        mode === 'internal' && !quote.dealType && 'deal type',
                        mode === 'internal' && !quote.ownerId && 'owner',
                        items.length === 0 && 'at least one line',
                      ].filter(Boolean).join(' · ')}</span>}
                </span>
              </div>
            )}
            {(quote.status === 'sent' || quote.status === 'viewed') && (
              <div className="qfact" style={{ border: 0 }}>
                <span>Sent</span>
                <span>{quote.sentAt ? formatUKDate(quote.sentAt) : '—'} to {detail.recipients.length} recipient{detail.recipients.length === 1 ? '' : 's'}{quote.status === 'viewed' ? ' · viewed by the customer' : ''}</span>
              </div>
            )}
            {(quote.status === 'declined' || quote.status === 'withdrawn' || quote.status === 'expired' || quote.status === 'superseded') && (
              <div className="qfact" style={{ border: 0 }}>
                <span>{quote.status === 'expired' ? 'Expired' : quote.status === 'superseded' ? 'Superseded' : 'Decided'}</span>
                <span>{quote.decidedAt ? formatUKDate(quote.decidedAt) : quote.validUntil && quote.status === 'expired' ? formatUKDate(quote.validUntil) : '—'}{quote.status === 'expired' ? ' — extend to reinstate it; it was never a lost deal' : ''}</span>
              </div>
            )}
          </div>
          <div className="qcard__actions">
            {quote.status === 'draft' && (
              <>
                <button className="btn btn--primary" onClick={() => setSlide({ kind: 'send' })}>Send quote →</button>
                <span className="qcard__spacer" />
                <button className="btn btn--danger" disabled={busy}
                  title="drafts only — a draft was never customer-visible, so a full delete is safe"
                  onClick={() => {
                    if (!window.confirm(`Delete draft ${quote.quoteNumber} completely? This cannot be undone.`)) return
                    run(async () => { await deleteQuoteFully(quoteId); onBack() })
                  }}>Delete draft</button>
              </>
            )}
            {/* Decision 165: an imported deal can be RECORDED DECLINED like any other quote. */}
            {quote.status === 'imported' && role !== null && (
              <button className="btn btn--danger" onClick={() => setSlide({ kind: 'declineOnBehalf' })}
                title="Record that this imported deal was declined — reason logged, the HubSpot deal moves to closed-lost, nothing is emailed">Record decline</button>
            )}
            {/* Decision 208: a later valid-until without a new version or a resend. */}
            {(quote.status === 'sent' || quote.status === 'viewed' || quote.status === 'expired') && (
              <button className="btn" disabled={busy} data-testid="extend-validity"
                title={quote.status === 'expired'
                  ? 'Give this quote a new valid-until date — it returns to sent/viewed, the customer\'s links work again, nothing is emailed'
                  : 'Push the valid-until date out — no new version, nothing emailed, the customer\'s links carry on'}
                onClick={() => {
                  const v = askExtensionDate(quote.quoteNumber, staleLineIds.length)
                  if (!v) return
                  run(async () => {
                    const r = await extendQuoteValidity(quoteId, v)
                    showToast(r.reinstated ? `Quote reinstated — valid until ${formatUKDate(r.validUntil)}` : `Valid until ${formatUKDate(r.validUntil)}`)
                    await reload()
                  })
                }}>{quote.status === 'expired' ? 'Extend & reinstate' : 'Extend validity'}</button>
            )}
            {(quote.status === 'sent' || quote.status === 'viewed') && (
              <>
                <button className="btn" onClick={() => setSlide({ kind: 'links' })}
                  title="fresh links per recipient — emailed, or copy-paste while mail is down">Links / resend</button>
                {role !== null && (
                  <button className="btn" onClick={() => setSlide({ kind: 'acceptOnBehalf' })}
                    title="Wave9 staff: record an acceptance the customer gave by phone, email or PO — uses the selections currently set in the customer preview (open the editor to change them)">Accept on behalf</button>
                )}
                {role !== null && (
                  <button className="btn btn--danger" onClick={() => setSlide({ kind: 'declineOnBehalf' })}
                    title="Wave9 staff: record a decline the customer gave by phone or email — reason logged, deal moves to closed-lost, nothing is emailed">Record decline</button>
                )}
                <button className="btn btn--danger" disabled={busy}
                  title="pull this quote before the customer decides — the page shows a withdrawn notice; supersede revives it"
                  onClick={() => {
                    const note = window.prompt('Withdraw this quote? The customer page will show a neutral "withdrawn by the issuer" notice.\n\nOptional internal note (kept in the activity trail):', '')
                    if (note === null) return
                    run(async () => {
                      await withdrawQuote(quoteId, note.trim() || undefined)
                      showToast('Quote withdrawn — supersede it to issue a revised version')
                      await reload()
                    })
                  }}>Withdraw</button>
              </>
            )}
            {ro && (
              <>
                <span className="qcard__spacer" />
                {role === 'admin' && !quote.hsDealId && (
                  <button className="btn btn--danger" disabled={busy}
                    title="Admin only: this quote has never synced to HubSpot, so it can be hard-deleted — customer links die, nothing will ever reach the pipeline. Made for clearing test quotes before go-live."
                    onClick={() => {
                      if (!window.confirm(`Delete ${quote.status} quote ${quote.quoteNumber} completely?\n\nIts customer links stop working and its queued HubSpot sync is dropped. This cannot be undone.`)) return
                      run(async () => { await deleteQuoteFully(quoteId); onBack() })
                    }}>Delete (test)</button>
                )}
                {quote.status !== 'superseded' && (
                  <button className="btn btn--primary" disabled={busy}
                    title="clone into a new editable draft (v+1) — any line addable, incl. post-survey ECC"
                    onClick={() => run(async () => {
                      const res = await supersedeQuote(quoteId)
                      showToast(res.existing ? `Opening existing draft ${res.quoteNumber}` : `Created ${res.quoteNumber} (v+1)`)
                      if (onOpen) onOpen(res.id)
                      else await reload()
                    })}>Supersede →</button>
                )}
              </>
            )}
          </div>
        </section>

        {/* 5 · After acceptance — staff, internal (cost + HubSpot writes), or
            whenever a derived record already exists to show. */}
        {quote.status === 'accepted' && ((role !== null && mode === 'internal') || quote.splitAt || quote.provisioningPackAt || (detail.adhocPurchaseOrders?.length ?? 0) > 0) && (
          <section className="qcard qcard--wide qcard--form" data-testid="card-after">
            <div className="qcard__head">
              <h3><span className={stepClass(Boolean(quote.splitAt || quote.provisioningPackAt || (detail.adhocPurchaseOrders?.length ?? 0) > 0))}>5</span> After acceptance</h3>
              <span className="qcard__hint">deals, assets and purchase orders derived from the frozen record — split first, then pack</span>
            </div>
            <div className="qcard__body">
              <div className="qfact">
                <span>HubSpot deals</span>
                <span>
                  {quote.splitAt ? (
                    <button className="chip chip--action" onClick={() => setSlide({ kind: 'splitDeals' })}
                      title={quote.splitMode === 'sites'
                        ? 'This accepted quote has one £0 HubSpot deal per site for that site\'s assets — the master deal keeps the value, line items and renewal.'
                        : quote.splitMode === 'phases'
                          ? 'This accepted quote was split into phase deals — the parent deal is a zeroed shell; each phase deal carries its lines and value on its own expected booking date.'
                          : 'This accepted quote was split into per-site HubSpot deals — the parent deal is a zeroed shell; the site deals carry the value.'}>
                      ⑂ {quote.splitMode === 'sites' ? `${detail.splits.length} site deal${detail.splits.length === 1 ? '' : 's'} (£0)` : quote.splitMode === 'phases' ? `Split into ${detail.splits.length} phase${detail.splits.length === 1 ? '' : 's'}` : `Split into ${detail.splits.length} site deal${detail.splits.length === 1 ? '' : 's'}`}
                    </button>
                  ) : role !== null && mode === 'internal' && (sites.length > 1 || detail.groups.filter((g) => !g.parentGroupId && g.kind === 'section').length > 1) ? (
                    <button className="btn btn--sm" disabled={busy}
                      title="create HubSpot deals from the accepted lines — per site (moving the value down, or £0 site deals for assets) or per phase (one deal per section, each with its own expected booking date; the parent becomes a zeroed shell)"
                      onClick={() => setSlide({ kind: 'splitDeals' })}>Split deal…</button>
                  ) : <span className="muted">one deal — nothing to split</span>}
                </span>
              </div>
              <div className="qfact">
                <span>Provisioning pack</span>
                <span>
                  {quote.provisioningPackAt ? (
                    <button className="chip chip--action" onClick={() => setSlide({ kind: 'provisioningPack' })}
                      title="The provisioning pack created from this quote — real pre-filled asset records, plus a stock-allocation task for anything coming from stock.">
                      🧰 Pack · {detail.provisioningPack.filter((r) => r.kind === 'asset').length} asset{detail.provisioningPack.filter((r) => r.kind === 'asset').length === 1 ? '' : 's'}
                      {detail.provisioningPack.some((r) => r.kind === 'task') ? ` · ${detail.provisioningPack.filter((r) => r.kind === 'task').length} stock task${detail.provisioningPack.filter((r) => r.kind === 'task').length === 1 ? '' : 's'}` : ''}
                    </button>
                  ) : role !== null && mode === 'internal' ? (
                    <button className="btn btn--sm" disabled={busy}
                      title="create real pre-filled asset records (at To Order) from the accepted lines — lines coming from stock get an allocation task on the deal instead. Projects & checklists come from the HubSpot workflows."
                      onClick={() => setSlide({ kind: 'provisioningPack' })}>Create pack…</button>
                  ) : <span className="muted">—</span>}
                </span>
              </div>
              {role !== null && mode === 'internal' && (
                <div className="qfact">
                  <span>Purchase orders</span>
                  <span>
                    {(detail.adhocPurchaseOrders?.length ?? 0) > 0 && (
                      <button className="chip chip--action" onClick={() => setSlide({ kind: 'adhocPo' })} data-testid="adhoc-po-chip"
                        title={detail.adhocPurchaseOrders!.map((p) => `${p.number ?? 'draft'} · ${p.supplier} · ${p.status}`).join('\n')}>
                        ⌂ {detail.adhocPurchaseOrders!.length === 1
                          ? `${detail.adhocPurchaseOrders![0].number ?? 'PO'} · ${detail.adhocPurchaseOrders![0].supplier}`
                          : `${detail.adhocPurchaseOrders!.length} purchase orders`}
                      </button>
                    )}
                    {' '}
                    <button className="btn btn--sm" disabled={busy} data-testid="adhoc-po-open"
                      title="raise a DRAFT purchase order in Xero from the lines the customer accepted — tick the lines, pick the Xero supplier, adjust qty or cost if needed"
                      onClick={() => setSlide({ kind: 'adhocPo' })}>{(detail.adhocPurchaseOrders?.length ?? 0) > 0 ? 'Another purchase order…' : 'Purchase order…'}</button>
                  </span>
                </div>
              )}
              {/* Decision 248: contract variations — what has drifted from the accepted
                  record. The deal's line items NOW against the acceptance; the #224 billing
                  variations alongside, so a change with a reason shows it and one made by
                  hand in HubSpot shows as unexplained. Staff, internal. Read-only. */}
              {role !== null && mode === 'internal' && (
                <div className="qfact" data-testid="contract-variations" style={{ alignItems: 'flex-start' }}
                  title="The HubSpot deal's line items right now, compared with what the customer accepted. A billing variation (recorded on the Billing page with a reason) explains a row; a row with no reason was changed outside the portal">
                  <span>Contract variations</span>
                  <span style={{ textAlign: 'left', flex: 1 }}>
                    {!contractVar || (!contractVar.data && !contractVar.error) ? <span className="muted small">reading the deal's line items…</span>
                    : contractVar.error ? <span className="pill pill--warn" title={contractVar.error}>could not read the deal — {contractVar.error}</span>
                    : contractVar.data!.state === 'no_deal' ? <span className="muted small">no HubSpot deal linked{contractVar.data!.ledger.length > 0 ? ` · ledger: ${contractVar.data!.ledger.length}` : ''}</span>
                    : contractVar.data!.state === 'deal_missing' ? <span className="pill pill--warn">the HubSpot deal no longer exists</span>
                    : contractVar.data!.state === 'split' ? <span className="muted small">the deal was split into {contractVar.data!.children} {contractVar.data!.mode === 'phases' ? 'phase' : 'site'} deals — the lines live on the children; compare there{contractVar.data!.ledger.length > 0 ? ` · ledger: ${contractVar.data!.ledger.length} entr${contractVar.data!.ledger.length === 1 ? 'y' : 'ies'}` : ''}</span>
                    : (() => {
                        const d = contractVar.data!
                        if (d.state !== 'ok') return null
                        const gbp = (n: number) => `£${n.toFixed(2)}`
                        const unexplained = d.rows.filter((r) => r.recorded.length === 0).length
                        return (
                          <>
                            {d.rows.length === 0
                              ? <span className="pill pill--ok" title={`${d.matched} accepted line${d.matched === 1 ? '' : 's'} on the deal as accepted`}>as accepted — {d.matched} line{d.matched === 1 ? '' : 's'} match</span>
                              : <span>
                                  <span className={`pill ${unexplained > 0 ? 'pill--warn' : 'pill--info'}`}>
                                    {d.rows.length} change{d.rows.length === 1 ? '' : 's'} since acceptance{unexplained > 0 ? ` · ${unexplained} with no reason on record` : ' · all recorded'}
                                  </span>
                                  <span className="tiny muted"> · {d.matched} as accepted</span>
                                </span>}
                            {d.rows.length > 0 && (
                              <table className="small" style={{ marginTop: 6, width: '100%' }}>
                                <thead><tr><th style={{ textAlign: 'left' }}>Change</th><th style={{ textAlign: 'left' }}>Line</th><th style={{ textAlign: 'left' }}>Accepted → now</th><th style={{ textAlign: 'left' }}>Reason on record</th></tr></thead>
                                <tbody>
                                  {d.rows.map((r, i) => {
                                    const line = r.onDeal ?? r.accepted!
                                    return (
                                      <tr key={i}>
                                        <td><span className={`pill ${r.kind === 'added' ? 'pill--ok' : r.kind === 'removed' ? 'pill--stop' : 'pill--warn'}`}>{r.kind}</span></td>
                                        <td>{line.name}{line.sku && <div className="tiny muted">{line.sku}</div>}</td>
                                        <td>
                                          {r.kind === 'added' && <>— → {r.onDeal!.qty} × {gbp(r.onDeal!.unitPrice)}</>}
                                          {r.kind === 'removed' && <>{r.accepted!.qty} × {gbp(r.accepted!.unitPrice)} → —</>}
                                          {r.kind === 'changed' && <>{r.accepted!.qty} × {gbp(r.accepted!.unitPrice)} → {r.onDeal!.qty} × {gbp(r.onDeal!.unitPrice)}</>}
                                        </td>
                                        <td>
                                          {r.recorded.length === 0
                                            ? <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <span className="pill pill--warn tiny" title="Nothing on the ledger explains this line — it was changed in HubSpot, or in a way the portal did not record">no reason on record</span>
                                                {/* Decision 249: record the explanation on the quote-side ledger. */}
                                                <button className="btn btn--ghost btn--sm" disabled={busy} data-testid="record-contract-variation"
                                                  title="Record why this line was added, removed or changed — the reason, your name and the time go on the quote's contract-variations ledger"
                                                  onClick={() => {
                                                    const reason = window.prompt(`Why was "${line.name}" ${r.kind}?\n\nThe reason is the audit record — it stays on this quote's ledger with your name and the time.`, '')
                                                    if (reason === null) return
                                                    if (reason.trim().length < 3) { setError('Give the reason — it is the audit record.'); return }
                                                    run(async () => {
                                                      const res = await recordContractVariation(quoteId, {
                                                        kind: r.kind, lineName: line.name, sku: line.sku, hsProductId: line.hsProductId, hsLineItemId: line.hsLineItemId,
                                                        before: r.accepted ? { qty: r.accepted.qty, unitPrice: r.accepted.unitPrice } : null,
                                                        after: r.onDeal ? { qty: r.onDeal.qty, unitPrice: r.onDeal.unitPrice } : null,
                                                        reason: reason.trim(),
                                                      })
                                                      setContractVar((c) => (c ? { ...c, data: res.variations, error: null } : c))
                                                      showToast('Contract variation recorded')
                                                    })
                                                  }}>Record reason…</button>
                                              </span>
                                            : r.recorded.map((v) => <div key={v.id} className="tiny"><strong>{formatUKDate(v.createdAt)}</strong> · {v.createdBy} · {v.reason} <span className="muted">({v.source === 'billing' ? 'Billing page' : v.source === 'variation_quote' ? 'variation quote' : 'recorded here'})</span></div>)}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            )}
                            {d.unmatchedRecorded.length > 0 && (
                              <div className="tiny muted" style={{ marginTop: 4 }} data-testid="contract-variations-unmatched">
                                On the ledger but no longer visible on the deal: {d.unmatchedRecorded.map((v) => `${v.kind} ${v.name} — ${v.reason} (${formatUKDate(v.createdAt)}, ${v.createdBy})`).join(' · ')}
                              </div>
                            )}
                            {d.ledger.length > 0 && (
                              <div className="tiny muted" style={{ marginTop: 4 }} title="Every explanation recorded against this quote — from this card, the Billing page, or a variation quote">
                                Ledger: {d.ledger.length} entr{d.ledger.length === 1 ? 'y' : 'ies'}
                              </div>
                            )}
                          </>
                        )
                      })()}
                  </span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* 6 · History ---------------------------------------------------------- */}
        <section className="qcard qcard--wide" data-testid="card-history">
          <div className="qcard__head">
            <h3><span className="qcard__step" style={{ background: 'var(--ink-soft)' }}>{quote.status === 'accepted' ? 6 : 5}</span> History</h3>
            <span className="qcard__hint">dates on the record — the full trail is under Activity</span>
            <span className="qcard__spacer" />
            <button className="btn" onClick={() => setSlide({ kind: 'activity' })} title="The quote's event trail — views, decisions and every email attempt">Activity</button>
          </div>
          <div className="qcard__body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0 24px' }}>
            {quote.createdAt && <div className="qfact"><span>Created</span><span>{formatUKDate(quote.createdAt)}</span></div>}
            {quote.sentAt && <div className="qfact"><span>Last sent</span><span>{formatUKDate(quote.sentAt)}</span></div>}
            {quote.validUntil && <div className="qfact"><span>Valid until</span><span>{formatUKDate(quote.validUntil)}</span></div>}
            {detail.acceptance
              ? <div className="qfact"><span>Accepted</span><span>{formatUKDate(detail.acceptance.acceptedAt)}{detail.acceptance.accepterName ? ` by ${detail.acceptance.accepterName}` : ''}</span></div>
              : quote.decidedAt && <div className="qfact"><span>Decided</span><span>{formatUKDate(quote.decidedAt)}</span></div>}
            {quote.importedAt && <div className="qfact"><span>Imported</span><span>{formatUKDate(quote.importedAt)}</span></div>}
          </div>
        </section>
      </div>
    </>
  )

  // The builder OVERLAY: Build / Customer preview and, for a draft, the way out.
  const editorOverlay = editing && (
    <div className="bover" data-testid="builder-overlay">
      <div className="bover__head">
        <span className="bover__ref">{quote.quoteNumber}</span>
        <div className="bover__id">
          <strong>{quote.title || 'Untitled quote'}</strong>
          <span>
            {ro
              ? `${quote.status} — read-only; the customer preview is the screen-share surface`
              : editSession?.resumed && editSession.since
                ? `Resumed ${editSession.mine || !editSession.by ? 'the' : `${editSession.by}'s`} edits from ${new Date(editSession.since).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} — changes save as you go; Save keeps them, Discard puts the lines back as they were then`
                : 'Changes save as you go — Save keeps them, Discard puts the lines back as they were when you opened the editor'}
          </span>
        </div>
        <div className="toggle">
          <button aria-pressed={!preview} onClick={() => setPreview(false)}>Build</button>
          <button aria-pressed={preview} onClick={() => setPreview(true)}>Customer preview</button>
        </div>
        {ro ? (
          <button className="btn btn--primary" onClick={closeEditor} data-testid="editor-close">Close</button>
        ) : (
          <>
            <button className="btn" disabled={busy} onClick={() => discardEditor(false)} data-testid="editor-discard"
              title="Throw away every change since the editor opened — the lines go back exactly as they were; the draft is kept">Discard changes</button>
            <button className="btn btn--primary" disabled={busy} onClick={saveEditor} data-testid="editor-save"
              title="Keep the changes and return to the quote">Save to draft</button>
          </>
        )}
      </div>
      {staleBanner}
      {costVarBanner}
      {amendStrip}
      {errorBanner}
      {workArea}
    </div>
  )

  // ---- render ----------------------------------------------------------------
  return (
    <div className={templateMode ? 'builder' : 'builder qrec'}>
      {templateMode ? (
        <>
      {/* the template editor's original full-workspace layout (decision 125) */}
      <div className="bbar">
        {/* Editable title: lighter-blue field + pencil so editing is obvious
            (Mark, 2026-08-06). Read-only quotes drop both — the title is fact. */}
        <span className={`bbar__title ${ro ? '' : 'bbar__title--edit'}`}>
          <input
            className="qtitle"
            key={quote.title}
            defaultValue={quote.title}
            disabled={ro}
            title="Wave9 standard: quote titles carry the site postcode"
            onBlur={(e) => e.target.value !== quote.title && commitTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          />
          {!ro && <span className="bbar__pencil" aria-hidden="true">✎</span>}
        </span>
        {/* Decision 128 (Mark, UAT): the title takes the whole bar except the
            back button — quote titles carry customer + postcode + site and were
            truncating. Reference, status and the rest moved to the row below. */}
        {templateMode ? (
          <>
            {/* Decision 125: never show a TPL- workspace number or a draft
                status — this is a template, and saying otherwise is the exact
                confusion #122 created. */}
            <span className="pill pill--brand">Editing template</span>
            <div className="bbar__spacer" />
            <button className="btn btn--primary" disabled={busy || tplEditSaving || items.length === 0}
              title="Save these changes back over the template. Quotes made from it will use this from now on; quotes already sent are untouched."
              onClick={() => void saveTemplateEdit()}>
              {tplEditSaving ? 'Saving…' : 'Save template'}
            </button>
            <button className="btn btn--danger" disabled={busy || tplEditSaving}
              title="Throw these changes away — the template stays exactly as it was last saved."
              onClick={() => {
                if (!window.confirm('Discard these changes? The template stays as it was last saved.')) return
                run(async () => { await discardTemplateWorkspace(templateMode.templateId); templateMode.onDiscarded() })
              }}>Discard</button>
          </>
        ) : (
          // No spacer: the title itself flexes to fill, so the back button is
          // the only other thing on the bar (#128).
          <button className="bbar__back" onClick={onBack}>← All quotes</button>
        )}
      </div>
      {!templateMode && (
        <div className="bbar2">
          <span className="bbar__ref">{quote.quoteNumber}</span>
          <span className="pill">{quote.status}</span>
          {quote.version > 1 && <span className="pill pill--info" title="Supersession version">v{quote.version}</span>}
          {/* #128 amended (Mark, UAT): the HubSpot deal, validity, expected
              booking, owner and sites moved up from the chips row below, which
              now carries only the customer and the contract being renewed. */}
          {mode === 'internal' && (
            <button className="chip chip--action" onClick={() => !ro && setStartOpen(true)} title="No HubSpot deal linked yet — sending will create one, or you can attach an existing direct deal here (update, never duplicate)">
              {/* #128: short label — the long form was eating row 2's width and
                  pushing Category/Deal type/Source onto a second line. */}
              {quote.hsDealId ? `⛁ Deal ${quote.hsDealId}` : '⛁ No deal yet'}
            </button>
          )}
          {mode !== 'internal' && quote.hsDealId && (
            <span className="chip small" title="The HubSpot deal this quote reconciles to">⛁ Deal {quote.hsDealId}</span>
          )}
          {quote.hsDealId && hubspotDealUrl(hsOpts?.portalId ?? null, quote.hsDealId) && (
            <a className="chip chip--action" href={hubspotDealUrl(hsOpts?.portalId ?? null, quote.hsDealId)!}
              target="_blank" rel="noreferrer" title="Open the deal in HubSpot">HubSpot ↗</a>
          )}
          <span className="chip small" title="How long the quotation stands — the customer page and nightly expiry both respect it">
            Valid until{' '}
            <input type="date" disabled={ro}
              value={quote.validUntil ? quote.validUntil.slice(0, 10) : ''}
              onChange={(e) => e.target.value && run(async () => {
                await patchQuote(quoteId, { validUntil: e.target.value })
                await reload()
              })}
            />
          </span>
          <span className="chip small" title="HubSpot Expected Booking Date — the close-date forecast">
            Exp. booking{' '}
            <input type="date" disabled={ro}
              value={quote.expectedBookingDate ?? ''}
              onChange={(e) => e.target.value && run(async () => {
                await patchQuote(quoteId, { expectedBookingDate: e.target.value })
                await reload()
              })}
            />
            {quote.expectedBookingDate && !ro && (
              <button className="gline__x" title="Clear — the admin default (send date + N days) takes over at the next send"
                onClick={() => run(async () => {
                  await patchQuote(quoteId, { expectedBookingDate: null })
                  await reload()
                })}>×</button>
            )}
          </span>
          {people.length > 0 && (
            <span className="chip small">Owner{' '}
              <select
                className="select" style={{ border: 0, padding: 0, fontSize: 12 }}
                value={quote.ownerId ?? ''}
                disabled={busy}
                title="the owner is reassignable even after send (holiday cover) — the HubSpot deal owner follows"
                onChange={(e) => run(async () => {
                  await patchQuote(quoteId, { ownerId: e.target.value || null })
                  await reload()
                })}
              >
                <option value="">—</option>
                {people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </span>
          )}
          {detail.customer && !ro && (
            <button className="chip chip--action" title="Add or remove the sites this quote covers"
              onClick={() => setSlide({ kind: 'sites' })}>
              ✎ Sites ({sites.length})
            </button>
          )}
          {/* #128 amended again (Mark): Category / Deal type / Source join
              row 2 beside Sites, leaving the deal description a full row. */}
          {mode === 'internal' && (['dealCategory', 'dealType', 'dealSource'] as const).map((field) => {
            const opts = field === 'dealCategory' ? hsOpts?.dealCategory.options
              : field === 'dealType' ? hsOpts?.dealType?.options
              : hsOpts?.dealSource.options
            const value = quote[field]
            const required = field === 'dealCategory' || field === 'dealType'   // decision 89: Deal Type gates send too
            return (
              <div key={field} className="bfields__pick"
                title={field === 'dealCategory'
                  ? 'HubSpot Deal Category — REQUIRED before an internal quote can send. Options are edited in HubSpot.'
                  : field === 'dealType'
                    ? 'HubSpot Deal Type — REQUIRED before an internal quote can send. Options are edited in HubSpot.'
                    : 'HubSpot Deal Source — marketing attribution. Options are edited in HubSpot.'}>
                <label style={required && !value ? { color: 'var(--warn)' } : undefined}>
                  {field === 'dealCategory' ? 'Category' : field === 'dealType' ? 'Deal type' : 'Source'}
                </label>
                {field === 'dealType' && opts && opts.length > 0 && opts.length <= 4 ? (
                  // HubSpot's dealtype IS a radio — render it as one (decision 89).
                  <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
                    {opts.map((o) => (
                      <label key={o.value} className="small" style={{ display: 'inline-flex', gap: 4, alignItems: 'center', cursor: ro ? 'default' : 'pointer' }}>
                        <input type="radio" name="deal-type" value={o.value} disabled={ro}
                          checked={value === o.value}
                          onChange={() => run(async () => {
                            await patchQuote(quoteId, { dealType: o.value })
                            await reload()
                          })} />
                        {o.label}
                      </label>
                    ))}
                  </span>
                ) : opts && opts.length > 0 ? (
                  <select className="select" disabled={ro}
                    style={required && !value ? { borderColor: 'var(--warn)', background: 'var(--warn-bg)' } : undefined}
                    value={value ?? ''}
                    onChange={(e) => run(async () => {
                      await patchQuote(quoteId, { [field]: e.target.value || null })
                      await reload()
                    })}>
                    <option value="">—</option>
                    {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    {value && !opts.some((o) => o.value === value) && <option value={value}>{value}</option>}
                  </select>
                ) : (
                  <input
                    className="input"
                    key={`${field}-${value ?? ''}`}
                    defaultValue={value ?? ''}
                    placeholder={hsOpts?.configured ? '…' : 'free text — HubSpot not connected'}
                    disabled={ro}
                    style={{ width: 140 }}
                    onBlur={(e) => (e.target.value.trim() || null) !== value && run(async () => {
                      await patchQuote(quoteId, { [field]: e.target.value.trim() || null })
                      await reload()
                    })}
                    onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                  />
                )}
              </div>
            )
          })}
        </div>
      )}
      {templateMode && (
        <div className="banner">
          <strong>{templateMode.resumed ? 'Unsaved changes restored.' : 'You are editing a template.'}</strong>
          <span>
            Build the pack exactly as you want it to arrive. <b>Prices are today's, for reference only</b> —
            a template never stores them, and every quote made from this reprices on use. Use
            <b> Cover note, footer &amp; T&amp;Cs</b> for what wraps around the lines. Nothing here is sent
            to anyone until someone starts a quote from it.
          </span>
        </div>
      )}
      <div className="bhead">
        <div className="chips" style={templateMode ? { display: 'none' } : undefined}>
          <button className={`chip chip--action ${detail.customer ? '' : 'chip--warn'}`} onClick={() => !ro && setStartOpen(true)}>
            {detail.customer
              ? <>⌂ {detail.customer.name}{' '}
                  {multiSite
                    ? <span className="pill pill--info">{sites.length} sites</span>
                    : (sites[0]?.postcode || sites[0]?.numberOnRoll != null)
                      ? <span className="muted tiny">· site {sites[0]?.postcode ?? ''}{sites[0]?.numberOnRoll != null ? `${sites[0]?.postcode ? ' · ' : ''}${sites[0].numberOnRoll} on roll` : ''}</span>
                      : null}
                </>
              : <>⚠ No recipient yet — <b>add before send</b></>}
          </button>
          {/* Decision 127: the chip OPENS the contract (it was a HubSpot link;
              the deal link now lives inside the panel, where the rest of the
              contract's facts are). */}
          {detail.renewal && (
            <button className="chip chip--action" onClick={() => setSlide({ kind: 'contract' })}
              title="See the contract this renews — dates, values, owner and the lines the customer is on today.">
              ↻ Renews {detail.renewal.dealName ?? `deal ${detail.renewal.hsDealId}`}
              {detail.renewal.contractEndDate ? ` · ends ${detail.renewal.contractEndDate.split('-').reverse().join('/')}` : ''}
            </button>
          )}
          {/* Decision 211: the distributor quote this was imported from — the
              chip downloads the original workbook; the price-protection date
              is the one that bites (Ingram's price holds only until then). */}
          {detail.supplierQuote && (() => {
            const sq = detail.supplierQuote
            const lapsed = sq.priceProtectionUntil != null && sq.priceProtectionUntil < new Date().toISOString().slice(0, 10)
            const past = sq.priceProtectionUntil != null && quote.validUntil != null && quote.validUntil.slice(0, 10) > sq.priceProtectionUntil
            return (
              <a className={`chip chip--action${lapsed || past ? ' chip--warn' : ''}`} href={supplierQuoteFileUrl(sq.id)} target="_blank" rel="noreferrer" data-testid="supplier-chip"
                title={`Imported from ${sq.distributor} quote ${sq.reference} at ${sq.marginPct}% margin — click to download ${sq.filename}.${sq.priceProtectionText ? ` Price protection: ${sq.priceProtectionText}.` : ''}${past ? ' ⚠ This quote is valid beyond the distributor\'s price protection.' : ''}`}>
                ↑ {sq.distributor} {sq.reference}
                {sq.priceProtectionUntil ? ` · price held to ${formatUKDate(sq.priceProtectionUntil)}` : ''}
                {lapsed ? ' ⚠ lapsed' : past ? ' ⚠ quote outlives it' : ''}
              </a>
            )
          })()}
          {/* Decision 213: the Xero draft PO raised for this distributor quote — a
              link to it, or the panel to raise one (staff only; the API refuses
              others). Cost, so never on a partner session. */}
          {detail.supplierQuote && role !== null && (
            detail.supplierQuote.po
              ? <a className="chip chip--action" href={detail.supplierQuote.po.url} target="_blank" rel="noreferrer" data-testid="supplier-po-chip"
                  title={`Xero purchase order ${detail.supplierQuote.po.number ?? ''} — ${detail.supplierQuote.po.status}. Click to open in Xero; update it from Supplier quotes.`}>
                  ⌂ {detail.supplierQuote.po.number ?? 'PO'} · {detail.supplierQuote.po.status}
                </a>
              : <button className="chip chip--action" onClick={() => setSlide({ kind: 'raisePo' })} data-testid="supplier-po-raise"
                  title="Raise a DRAFT purchase order in Xero for this distributor's lines at the quote's current quantities and costs.">
                  ⌂ Raise PO…
                </button>
          )}
          {/* Decision 212: apply a (new) distributor quote to this DRAFT — costs
              update, new SKUs are added, and the reprice question is asked.
              Any internal draft, whether or not it came from an import. */}
          {!ro && mode === 'internal' && role !== null && !templateMode && (
            <button className="chip chip--action" onClick={() => setSlide({ kind: 'supplierApply' })} data-testid="supplier-apply"
              title="Upload a distributor quote (Ingram .xlsx): matched SKUs take the new cost, new SKUs are added, and you decide whether sell prices move.">
              ↑ Apply distributor quote…
            </button>
          )}
          {/* Decision 171: the frozen split record — one deal per site, the
              parent shell zeroed. Opens the read-only split panel. */}
          {quote.splitAt && (
            <button className="chip chip--action" onClick={() => setSlide({ kind: 'splitDeals' })}
              title={quote.splitMode === 'sites'
                ? 'This accepted quote has one £0 HubSpot deal per site for that site\'s assets — the master deal keeps the value, line items and renewal.'
                : quote.splitMode === 'phases'
                  ? 'This accepted quote was split into phase deals — the parent deal is a zeroed shell; each phase deal carries its lines and value on its own expected booking date.'
                  : 'This accepted quote was split into per-site HubSpot deals — the parent deal is a zeroed shell; the site deals carry the value.'}>
              ⑂ {quote.splitMode === 'sites' ? `${detail.splits.length} site deal${detail.splits.length === 1 ? '' : 's'} (£0)` : quote.splitMode === 'phases' ? `Split into ${detail.splits.length} phase${detail.splits.length === 1 ? '' : 's'}` : `Split into ${detail.splits.length} site deal${detail.splits.length === 1 ? '' : 's'}`}
            </button>
          )}
          {/* Decision 236: ad hoc Xero POs raised from the accepted lines. */}
          {role !== null && (detail.adhocPurchaseOrders?.length ?? 0) > 0 && (
            <button className="chip chip--action" onClick={() => setSlide({ kind: 'adhocPo' })} data-testid="adhoc-po-chip"
              title={detail.adhocPurchaseOrders!.map((p) => `${p.number ?? 'draft'} · ${p.supplier} · ${p.status}`).join('\n')}>
              ⌂ {detail.adhocPurchaseOrders!.length === 1
                ? `${detail.adhocPurchaseOrders![0].number ?? 'PO'} · ${detail.adhocPurchaseOrders![0].supplier}`
                : `${detail.adhocPurchaseOrders!.length} purchase orders`}
            </button>
          )}
          {/* Decision 178: the frozen provisioning-pack record — assets + stock
              tasks created from the accepted lines. Opens the read-only panel. */}
          {quote.provisioningPackAt && (
            <button className="chip chip--action" onClick={() => setSlide({ kind: 'provisioningPack' })}
              title="The provisioning pack created from this quote — real pre-filled asset records, plus a stock-allocation task for anything coming from stock.">
              🧰 Pack · {detail.provisioningPack.filter((r) => r.kind === 'asset').length} asset{detail.provisioningPack.filter((r) => r.kind === 'asset').length === 1 ? '' : 's'}
              {detail.provisioningPack.some((r) => r.kind === 'task') ? ` · ${detail.provisioningPack.filter((r) => r.kind === 'task').length} stock task${detail.provisioningPack.filter((r) => r.kind === 'task').length === 1 ? '' : 's'}` : ''}
            </button>
          )}
        </div>
        <div className="toggle">
          <button aria-pressed={!preview} onClick={() => setPreview(false)}>Build</button>
          <button aria-pressed={preview} onClick={() => setPreview(true)}>Customer preview</button>
        </div>
        <button className="btn" onClick={() => setSlide({ kind: 'attachments' })}>
          Attachments <span className="pill pill--info">{detail.attachments.length}</span>
        </button>
        {detail.renewal && (
          <button className="btn" onClick={() => setSlide({ kind: 'contract' })}
            title="The contract being renewed — including the lines the customer is on today">
            Contract
            {detail.renewal.lineItems && detail.renewal.lineItems.length > 0 && (
              <span className="pill pill--info">{detail.renewal.lineItems.length} line{detail.renewal.lineItems.length === 1 ? '' : 's'}</span>
            )}
          </button>
        )}
        {templateMode && (
          // Decision 125: the whole point of the editor — these three were
          // reachable only from the Send panel before, so nobody found them.
          <button className="btn btn--primary" onClick={() => setSlide({ kind: 'presentation' })}
            title="The cover note, footer and terms every quote made from this template arrives with">
            Cover note, footer &amp; T&amp;Cs{' '}
            <span className="pill pill--info">
              {[quote.coverNote?.trim() ? 'note' : null, quote.footerText?.trim() ? 'footer' : null,
                quote.termsSetId ? 'T&Cs' : null].filter(Boolean).join(' · ') || 'none set'}
            </span>
          </button>
        )}
        {!templateMode && <button className="btn" disabled={busy || items.length === 0}
          title="saves the structure — sections, options, lines, cover note, terms, attachments — never prices. New quotes from it reprice from today's catalogue."
          onClick={() => {
            setTplName(quote.title || 'New template'); setTplGlobal(false); setTplTarget('')
            setTplCover(quote.coverNote ?? ''); setTplFooter(quote.footerText ?? ''); setTplTerms(quote.termsSetId ?? '')
            fetchTemplates().then((r) => setTplList(r.templates.filter((t) => t.canManage))).catch(() => setTplList([]))
            setTplModal(true)
          }}>Save as template</button>}
        {!templateMode && <button className="btn" disabled={busy}
          title="clone as a fresh draft, repriced from today's catalogue — frozen prices never copy to a new deal"
          onClick={() => run(async () => {
            const res = await duplicateQuote(quoteId)
            showToast(res.skipped.length > 0
              ? `Created ${res.quoteNumber} — skipped ${res.skipped.length} inactive product(s): ${res.skipped.join(', ')}`
              : `Created ${res.quoteNumber} (repriced from today's catalogue)`)
            if (onOpen) onOpen(res.id)
          })}>Duplicate</button>}
        {!templateMode && ro && (
          <button className="btn" onClick={() => setSlide({ kind: 'activity' })} title="The quote's event trail">Activity</button>
        )}
        {/* Decision 165: an imported deal can be RECORDED DECLINED like any other
            quote — the deal moves to closed-lost with the reason. Decline only:
            accept-on-behalf stays sent/viewed (re-quote a won import via Supersede). */}
        {!templateMode && quote.status === 'imported' && role !== null && (
          <button className="btn btn--danger" onClick={() => setSlide({ kind: 'declineOnBehalf' })}
            title="Record that this imported deal was declined — reason logged, the HubSpot deal moves to closed-lost, nothing is emailed">Record decline</button>
        )}
        {/* Decision 208: a later valid-until without a new version or a resend —
            for an expired quote this is the way back (it was never a lost deal). */}
        {!templateMode && (quote.status === 'sent' || quote.status === 'viewed' || quote.status === 'expired') && (
          <button className="btn" disabled={busy} data-testid="extend-validity"
            title={quote.status === 'expired'
              ? 'Give this quote a new valid-until date — it returns to sent/viewed, the customer\'s links work again, nothing is emailed'
              : 'Push the valid-until date out — no new version, nothing emailed, the customer\'s links carry on'}
            onClick={() => {
              const v = askExtensionDate(quote.quoteNumber, staleLineIds.length)
              if (!v) return
              run(async () => {
                const r = await extendQuoteValidity(quoteId, v)
                showToast(r.reinstated ? `Quote reinstated — valid until ${formatUKDate(r.validUntil)}` : `Valid until ${formatUKDate(r.validUntil)}`)
                await reload()
              })
            }}>{quote.status === 'expired' ? 'Extend & reinstate' : 'Extend validity'}</button>
        )}
        {!templateMode && (quote.status === 'sent' || quote.status === 'viewed') && (
          <>
            {role !== null && (
              <button className="btn" onClick={() => setSlide({ kind: 'acceptOnBehalf' })}
                title="Wave9 staff: record an acceptance the customer gave by phone, email or PO — uses the selections currently set in Preview">Accept on behalf</button>
            )}
            {role !== null && (
              <button className="btn btn--danger" onClick={() => setSlide({ kind: 'declineOnBehalf' })}
                title="Wave9 staff: record a decline the customer gave by phone or email — reason logged, deal moves to closed-lost, nothing is emailed">Record decline</button>
            )}
            <button className="btn" onClick={() => setSlide({ kind: 'links' })}
              title="fresh links per recipient — emailed, or copy-paste while mail is down">Links / resend</button>
            <button className="btn btn--danger" disabled={busy}
              title="pull this quote before the customer decides — the page shows a withdrawn notice; supersede revives it"
              onClick={() => {
                const note = window.prompt('Withdraw this quote? The customer page will show a neutral "withdrawn by the issuer" notice.\n\nOptional internal note (kept in the activity trail):', '')
                if (note === null) return
                run(async () => {
                  await withdrawQuote(quoteId, note.trim() || undefined)
                  showToast('Quote withdrawn — supersede it to issue a revised version')
                  await reload()
                })
              }}>Withdraw</button>
          </>
        )}
        {templateMode ? null : !ro ? (
          <>
            <button className="btn btn--danger" disabled={busy}
              title="drafts only — a draft was never customer-visible, so a full delete is safe"
              onClick={() => {
                if (!window.confirm(`Delete draft ${quote.quoteNumber} completely? This cannot be undone.`)) return
                run(async () => {
                  await deleteQuoteFully(quoteId)
                  onBack()
                })
              }}>Delete draft</button>
            <button className="btn btn--primary" onClick={() => setSlide({ kind: 'send' })}>Send quote →</button>
          </>
        ) : (
          <>
            {role === 'admin' && !quote.hsDealId && (
              <button className="btn btn--danger" disabled={busy}
                title="Admin only: this quote has never synced to HubSpot, so it can be hard-deleted — customer links die, nothing will ever reach the pipeline. Made for clearing test quotes before go-live."
                onClick={() => {
                  if (!window.confirm(`Delete ${quote.status} quote ${quote.quoteNumber} completely?\n\nIts customer links stop working and its queued HubSpot sync is dropped. This cannot be undone.`)) return
                  run(async () => {
                    await deleteQuoteFully(quoteId)
                    onBack()
                  })
                }}>Delete (test)</button>
            )}
            {/* Decision 171: split an accepted multi-site quote into one HubSpot
                deal per site — optional, post-acceptance, staff + internal only. */}
            {quote.status === 'accepted' && role !== null && mode === 'internal' && !quote.splitAt
              && (sites.length > 1 || detail.groups.filter((g) => !g.parentGroupId && g.kind === 'section').length > 1) && (
              <button className="btn" disabled={busy}
                title="create HubSpot deals from the accepted lines — per site (moving the value down, or £0 site deals for assets) or per phase (decision: one deal per section, each with its own expected booking date; the parent becomes a zeroed shell)"
                onClick={() => setSlide({ kind: 'splitDeals' })}>Split deal</button>
            )}
            {/* Decision 178: the provisioning pack — asset records (+ stock
                tasks) from the frozen accepted lines. Split first, then pack. */}
            {quote.status === 'accepted' && role !== null && mode === 'internal' && !quote.provisioningPackAt && (
              <button className="btn" disabled={busy}
                title="create real pre-filled asset records (at To Order) from the accepted lines — lines coming from stock get an allocation task on the deal instead. Projects & checklists come from the HubSpot workflows."
                onClick={() => setSlide({ kind: 'provisioningPack' })}>Provisioning pack</button>
            )}
            {/* Decision 236: ad hoc Xero purchase orders from the accepted lines —
                any supplier, picked per PO. Staff + internal only (cost). */}
            {quote.status === 'accepted' && role !== null && mode === 'internal' && (
              <button className="btn" disabled={busy} data-testid="adhoc-po-open"
                title="raise a DRAFT purchase order in Xero from the lines the customer accepted — tick the lines, pick the Xero supplier, adjust qty or cost if needed"
                onClick={() => setSlide({ kind: 'adhocPo' })}>Purchase order…</button>
            )}
            {quote.status !== 'superseded' && (
              <button className="btn btn--primary" disabled={busy}
                title="clone into a new editable draft (v+1) — any line addable, incl. post-survey ECC"
                onClick={() => run(async () => {
                  const res = await supersedeQuote(quoteId)
                  showToast(res.existing ? `Opening existing draft ${res.quoteNumber}` : `Created ${res.quoteNumber} (v+1)`)
                  if (onOpen) onOpen(res.id)
                  else await reload()
                })}>Supersede →</button>
            )}
          </>
        )}
      </div>

      {/* Decision 160: an imported deal — the tracking record for a Quotient-era
          quote. The real document lives in Quotient; Supersede (above) is the
          re-quote path into the portal, keeping the same HubSpot deal. */}
      {quote.status === 'imported' && (
        <div className="bbanner">
          ⇣ <span>
            <strong>Imported from HubSpot</strong> — the customer quote for this deal lives in Quotient;
            this record tracks it in the portal ({quote.importedAt ? `imported ${formatUKDate(quote.importedAt)}` : 'imported'}).
            To re-quote it here, use <strong>Supersede</strong> — the new draft keeps the same HubSpot deal.
          </span>
          {quote.quotientLink && (
            <a className="btn btn--sm" href={quote.quotientLink} target="_blank" rel="noreferrer">Open in Quotient →</a>
          )}
          {hsOpts?.portalId && quote.hsDealId && (
            <a className="btn btn--sm" href={hubspotDealUrl(hsOpts.portalId, quote.hsDealId) ?? '#'} target="_blank" rel="noreferrer">Open deal →</a>
          )}
        </div>
      )}

      {/* Decision-86 fields line (Mark, 2026-08-06): the deal description gets
          half the width LHS; the internal Category/Source picklists sit RHS on
          the same line. Options mirror LIVE from HubSpot via /api/hs-options. */}
      <div className="bfields" style={templateMode ? { display: 'none' } : undefined}>
        <div className="bfields__desc"
          title="Written to the HubSpot deal's Description — an internal note, never customer-visible">
          <label htmlFor="deal-desc">Deal description</label>
          <input
            id="deal-desc"
            className="input"
            key={`dd-${quote.dealDescription ?? ''}`}
            defaultValue={quote.dealDescription ?? ''}
            placeholder="Short internal description for the HubSpot deal…"
            disabled={ro}
            onBlur={(e) => (e.target.value.trim() || null) !== quote.dealDescription && run(async () => {
              await patchQuote(quoteId, { dealDescription: e.target.value.trim() || null })
              await reload()
            })}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          />
        </div>
      </div>

      {ro && (
        <div className="bbanner">
          <strong>This quote is {quote.status}</strong>
          <span>
            — sent quotes never change or reprice.
            {quote.status === 'superseded'
              ? ' A newer version exists — find it in the quotes list.'
              : ' Use “Supersede” to revise it as a new version.'}
          </span>
        </div>
      )}
      {!ro && staleLineIds.length > 0 && (
        <div className="bbanner">
          ⚠ <span>
            <strong>The catalogue has moved under this draft</strong>
            {' — '}
            {[
              detail.stalePriceIds.length > 0 && `${detail.stalePriceIds.length} line${detail.stalePriceIds.length === 1 ? '' : 's'} with newer prices`,
              detail.staleDescriptionIds.length > 0 && `${detail.staleDescriptionIds.length} with newer descriptions`,
            ].filter(Boolean).join(' · ')}. Sent quotes never reprice.
          </span>
          {detail.stalePriceIds.length > 0 && (
            <button className="btn btn--sm" disabled={busy} onClick={() => run(async () => {
              const res = await refreshQuotePrices(quoteId, 'prices')
              await reload()
              showToast(`Refreshed prices on ${res.refreshed} line${res.refreshed === 1 ? '' : 's'} — descriptions untouched`)
            })}>Refresh prices</button>
          )}
          {detail.staleDescriptionIds.length > 0 && (
            <button className="btn btn--sm" disabled={busy} onClick={() => run(async () => {
              const res = await refreshQuotePrices(quoteId, 'descriptions')
              await reload()
              showToast(`Refreshed descriptions on ${res.refreshed} line${res.refreshed === 1 ? '' : 's'} — prices untouched`)
            })}>Refresh descriptions</button>
          )}
        </div>
      )}
      {!ro && mode === 'internal' && items.some((i) => i.costVariedAt && !i.costVariationId) && (
        <div className="bbanner">
          ⚠ <span>
            <strong>{items.filter((i) => i.costVariedAt && !i.costVariationId).length} cost change{items.filter((i) => i.costVariedAt && !i.costVariationId).length === 1 ? '' : 's'} without a variation entry</strong>
            {' '}— every cost override needs supporting evidence: a supplier quote or a note, logged with your name.
          </span>
          <button className="btn btn--sm btn--primary" disabled={busy} onClick={() => setSlide({ kind: 'costVariation' })}>
            Log cost variation
          </button>
        </div>
      )}
      {/* Decision 191: an accepted quote says WHAT was accepted, at a glance —
          the deal link lands people here, and the total alone never told them. */}
      {quote.status === 'accepted' && detail.acceptance && acceptedQtyById && (
        <div className="bbanner bbanner--accepted" data-testid="acceptance-strip">
          ✓ <span>
            <strong>Accepted {formatUKDate(detail.acceptance.acceptedAt)}</strong>
            {detail.acceptance.accepterName ? <> by {detail.acceptance.accepterName}</> : null}
            {detail.acceptance.onBehalf ? <> <span className="pill pill--info" title="Recorded by Wave9 staff on the customer's instruction">on the customer's instruction</span></> : null}
            {detail.acceptance.version > 1 && detail.acceptance.amendedAt ? <> · <strong title={detail.acceptance.amendAuthorityNote ?? undefined}>amended {formatUKDate(detail.acceptance.amendedAt)}{detail.acceptance.amendedByName ? ` by ${detail.acceptance.amendedByName}` : ''} (v{detail.acceptance.version})</strong></> : null}
            {' '}· {detail.acceptance.poReference ? `PO ${detail.acceptance.poReference}` : detail.acceptance.poToFollow ? 'PO to follow' : 'no PO'}
            {' '}· {formatGBPCompact(detail.totals.contractValueSell)} contract value
            {' '}· <strong>{acceptedQtyById.size} of {items.length} offered line{items.length === 1 ? '' : 's'} taken</strong>
            {acceptedQtyById.size < items.length ? <span className="muted"> — the rest are marked "not taken" below</span> : null}
          </span>
          <button className="btn btn--sm btn--primary" onClick={() => setSlide({ kind: 'acceptance' })}
            title="The frozen acceptance record — who, when, on what terms, and every line taken or left">
            What was accepted
          </button>
          {/* Decision 217: the customer's own accepted page — a fresh personal
              link for the accepter (#118's mint), copied, nothing emailed. */}
          <button className="btn btn--sm" disabled={busy} data-testid="customer-view-link"
            title="Mint the accepter's link to the customer version of the accepted quote and copy it — no email is sent"
            onClick={() => {
              const accepterEmail = (detail.acceptance?.accepterEmail ?? '').toLowerCase()
              const rec = detail.recipients.find((r) => r.email.toLowerCase() === accepterEmail) ?? detail.recipients[0]
              if (!rec) { setError('This quote has no recipients to mint a link for.'); return }
              // #147: the clipboard write starts inside the click gesture.
              const urlPromise = mintRecipientLink(quoteId, rec.id).then((r) => r.url)
              const wrote = copyTextFromPromise(urlPromise)
              run(async () => {
                const url = await urlPromise
                const ok = await wrote
                setCustomerLink({ url, copied: ok })
                showToast(ok ? `Customer link copied — ${rec.name || rec.email}` : 'Customer link minted — copy it from the field below')
              })
            }}>
            {customerLink?.copied ? '✓ Customer link copied' : 'Customer view'}
          </button>
          {/* Decision 218: revise the acceptance without a new version — link
              for the customer, or recorded on their instruction. Greyed with
              the reason once a split / PO derives from the record; a provisioning
              pack only warns (#247 — its HubSpot records are fixed by hand). */}
          {role !== null && !amending && (
            <button className="btn btn--sm" disabled={busy || detail.acceptance.amendBlockers.length > 0} data-testid="amend-acceptance"
              title={detail.acceptance.amendBlockers.length > 0
                ? `Cannot amend — ${detail.acceptance.amendBlockers.map((b) => b.label).join('; ')}. Supersede the quote instead.`
                : detail.acceptance.amendWarnings.length > 0
                  ? `Amend the acceptance — same quote, same deal, no new version. ⚠ ${detail.acceptance.amendWarnings.map((w) => w.label).join('; ')}`
                  : 'The customer forgot an option or needs a quantity changed? Amend the acceptance — same quote, same deal, no new version'}
              onClick={() => setSlide({ kind: 'amendAcceptance' })}>
              Amend acceptance
            </button>
          )}
        </div>
      )}
      {quote.status === 'accepted' && customerLink && (
        <div className="bbanner" data-testid="customer-view-field">
          <span className="small muted">Customer view</span>
          <input className="input" style={{ flex: 1, fontSize: 11.5 }} readOnly value={customerLink.url} onFocus={(e) => e.target.select()} />
          <button className="btn btn--sm" onClick={() => { void navigator.clipboard.writeText(customerLink.url).then(() => setCustomerLink({ ...customerLink, copied: true })).catch(() => {}) }}>Copy</button>
          <button className="btn btn--sm btn--ghost" onClick={() => setCustomerLink(null)}>✕</button>
        </div>
      )}
      {/* Decision 218: amend mode — the preview is live, seeded from the record. */}
      {quote.status === 'accepted' && amending && detail.acceptance && (
        <div className="bbanner" data-testid="amend-strip">
          ✎ <span>
            <strong>Amending the acceptance (version {detail.acceptance.version})</strong>
            {' '}— change options and quantities in the customer preview below to what the customer now wants, then record it on their instruction. Prices never move; a new price or line is a Supersede.
          </span>
          <button className="btn btn--sm btn--primary" disabled={busy} onClick={() => setSlide({ kind: 'amendAcceptance' })}>Record amendment</button>
          <button className="btn btn--sm" disabled={busy} onClick={() => { setAmending(false); setPreview(false) }}>Cancel</button>
        </div>
      )}
      {error && <div className="bbanner" style={{ background: 'var(--stop-bg)', color: 'var(--stop)', borderColor: '#f2c4c4' }}>{error}</div>}
          {workArea}
        </>
      ) : (
        <>
          {recordPage}
          {editorOverlay}
        </>
      )}

      {toast && <div className="toast">✓ <span>{toast}</span></div>}

      {tplModal && (
        <div className="modal" onClick={() => !tplSaving && setTplModal(false)}>
          <div className="modal__card" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal__head">
              <strong>Save as template</strong>
              <button className="btn btn--sm" onClick={() => setTplModal(false)} disabled={tplSaving}>✕</button>
            </div>
            <div className="modal__body">
              <p className="small muted" style={{ marginTop: 0 }}>
                Saves the structure — sections, option blocks, lines, cover note, terms and attachments —
                <strong> never prices</strong>. New quotes from it reprice from today's catalogue;
                ad-hoc lines carry their current prices and are flagged for review.
              </p>
              {tplList.length > 0 && (
                <div className="field">
                  <label>Save as</label>
                  <select className="select" style={{ width: '100%' }} value={tplTarget}
                    onChange={(e) => {
                      const id = e.target.value
                      setTplTarget(id)
                      const t = tplList.find((x) => x.id === id)
                      if (t) setTplName(t.name)
                    }}>
                    <option value="">New template…</option>
                    {tplList.map((t) => (
                      <option key={t.id} value={t.id}>Update: {t.name}{t.partnerId ? '' : ' (Wave9-global)'}</option>
                    ))}
                  </select>
                  {tplTarget && (
                    <div className="hint">Replaces that template's structure with THIS quote's — its scope
                      ({tplList.find((t) => t.id === tplTarget)?.partnerId ? partner.name : 'Wave9-global'}) stays as it is.</div>
                  )}
                </div>
              )}
              <div className="field">
                <label>Template name</label>
                <input className="input" style={{ width: '100%' }} value={tplName}
                  onChange={(e) => setTplName(e.target.value)} autoFocus />
              </div>
              {/* Decision 124 — the UAT finding. These three have ridden the
                  payload since #96/#104 and always landed on quotes made from
                  the template, but the ONLY place to author them was the Send
                  panel, so a template saved from a fresh draft carried nothing
                  and nobody could see that. They are editable here, and what is
                  in these boxes is what the template stores. */}
              <div className="field" style={{ marginTop: 10 }}>
                <label>Terms &amp; conditions this template uses</label>
                <select className="select" style={{ width: '100%' }} value={tplTerms}
                  onChange={(e) => setTplTerms(e.target.value)}>
                  <option value="">None — pick per quote</option>
                  {termsSets.filter((t) => t.currentVersion).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Cover note <span className="tiny muted">— opens every quote made from this</span></label>
                <textarea className="input" style={{ width: '100%' }} rows={3} value={tplCover}
                  placeholder="Leave blank to write one per quote"
                  onChange={(e) => setTplCover(e.target.value)} />
              </div>
              <div className="field">
                <label>Footer <span className="tiny muted">— after the contract</span></label>
                <textarea className="input" style={{ width: '100%' }} rows={2} value={tplFooter}
                  placeholder="Leave blank for none"
                  onChange={(e) => setTplFooter(e.target.value)} />
              </div>
              <p className="tiny muted">
                These belong to the <strong>template</strong> — editing them here never changes this quote.
                Snippets and tokens still expand per quote in the Send panel.
              </p>
              {role !== null && !tplTarget && (
                <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
                  <input type="checkbox" checked={tplGlobal} onChange={(e) => setTplGlobal(e.target.checked)} />
                  Wave9-global — every partner sees it
                  <span className="tiny muted">(unticked = {partner.name} only)</span>
                </label>
              )}
              {tplError && <p className="pill pill--stop" style={{ whiteSpace: 'pre-wrap' }}>{tplError}</p>}
              <div className="rowflex" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                <button className="btn btn--primary" disabled={tplSaving || !tplName.trim()}
                  onClick={async () => {
                    setTplSaving(true); setTplError(null)
                    try {
                      const res = await saveQuoteTemplate({
                        quoteId, name: tplName.trim(),
                        ...(tplTarget ? { templateId: tplTarget } : { global: tplGlobal }),
                        coverNote: tplCover.trim() || null,
                        footerText: tplFooter.trim() || null,
                        termsSetId: tplTerms || null,
                      })
                      setTplModal(false)
                      showToast(`${res.updated ? 'Updated' : 'Saved'} template “${res.name}” (${res.summary} · ${res.carries.label.toLowerCase()})${res.adhocLines > 0 ? ' — ad-hoc prices ride along, flagged on use' : ''}`)
                    } catch (e) { setTplError((e as Error).message) } finally { setTplSaving(false) }
                  }}>{tplSaving ? 'Saving…' : tplTarget ? 'Update template' : 'Save template'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {slide?.kind === 'line' && (() => {
        const it = items.find((x) => x.id === slide.itemId)
        if (!it) return null
        return (
          <LineSlideOver
            item={it} mode={mode} group={groups.find((g) => g.id === it.groupId)}
            product={it.hsProductId ? productById.get(it.hsProductId) : undefined}
            disabled={ro}
            onClose={() => setSlide(null)}
            onPatch={(patch) => run(async () => {
              await patchQuoteItem(quoteId, { id: it.id, ...patch })
              await reload()
            })}
            onRemove={() => { setSlide(null); run(async () => { await deleteQuoteItem(quoteId, it.id); await reload() }) }}
          />
        )
      })()}

      {/* Decision 125: the presentation panel — the three things a template
          carries, in the editor rather than buried in the send panel. */}
      {slide?.kind === 'presentation' && (
        <TemplatePresentationSlideOver
          quote={quote} termsSets={termsSets}
          onClose={() => setSlide(null)}
          onPatch={(patch) => run(async () => { await patchQuote(quoteId, patch); await reload() })}
        />
      )}

      {/* Decision 127: the contract being renewed — read-only reference while
          the new quote is written. No outcome actions: those live on the
          Renewals page, and a half-set of them here would be worse than none. */}
      {slide?.kind === 'contract' && detail.renewal && (
        <ContractSlideOver
          renewal={detail.renewal} portalId={hsOpts?.portalId ?? null}
          onClose={() => setSlide(null)}
        />
      )}

      {slide?.kind === 'attachments' && (
        <AttachmentsSlideOver
          assets={assets} attachments={detail.attachments} attached={detail.attachments.map((a) => a.assetId)}
          mode={mode} staffish={role !== null}
          disabled={ro}
          onClose={() => setSlide(null)}
          onToggle={(assetId, on) => run(async () => {
            if (on) await addQuoteAttachment(quoteId, assetId)
            else await deleteQuoteAttachment(quoteId, assetId)
            await reload()
          })}
          onReorder={(body) => run(async () => {
            await updateQuoteAttachment(quoteId, body)
            await reload()
          })}
          onUpload={async (file, title, scope) => {
            await uploadQuoteAttachment(quoteId, file, title, scope)
            await reload()
            if (scope === 'library') setAssets((await fetchAssets(quoteId)).assets)
          }}
        />
      )}

      {slide?.kind === 'send' && (
        <SendSlideOver
          detail={detail} partner={partner} people={people} snippets={snippets} snippetTokens={snippetTokens} termsSets={termsSets}
          quoteCategories={quoteCategories}
          disabled={ro}
          onClose={() => setSlide(null)}
          onPatch={(patch) => run(async () => { await patchQuote(quoteId, patch); await reload() })}
          onAddRecipient={(r) => run(async () => { await addQuoteRecipient(quoteId, r); await reload() })}
          onRemoveRecipient={(id) => run(async () => { await deleteQuoteRecipient(quoteId, id); await reload() })}
          onSaveSnippet={(title, body) => run(async () => {
            await createSnippet({ title, body, partnerId: partner.id })
            setSnippets((await fetchSnippets()).snippets)
            showToast('Saved to your snippet library')
          })}
          onSend={async () => {
            const res = await sendQuote(quoteId)
            await reload()
            return res
          }}
          onMarkSent={async () => {
            // Decision 95: full send minus the email — for quotes whose link
            // travels inside another document.
            const res = await sendQuote(quoteId, { withoutEmail: true })
            await reload()
            return res
          }}
          onPreviewLink={async () => {
            // Decision 147: start the clipboard write INSIDE the gesture —
            // awaiting the mint first made Safari refuse the write.
            const urlPromise = createPreviewLink(quoteId).then((r) => r.url)
            const wrote = copyTextFromPromise(urlPromise)
            const url = await urlPromise
            if (await wrote) showToast('Preview link copied — watermarked, non-binding')
            else showToast('Preview link minted — copy it from the field shown')
            return url
          }}
        />
      )}

      {slide?.kind === 'costVariation' && (
        <CostVariationSlideOver
          detail={detail}
          onClose={() => setSlide(null)}
          onLog={async (payload) => {
            const res = await logCostVariation({ quoteId, ...payload })
            await reload()
            showToast(`Cost variation logged by ${res.createdBy} — ${res.lines.length} line(s)`)
            return res
          }}
        />
      )}

      {slide?.kind === 'supplierApply' && (
        <SupplierQuoteApply
          quoteId={quoteId}
          quoteNumber={quote.quoteNumber}
          defaultMarginPct={detail.totals.marginPct == null ? null : detail.totals.marginPct * 100}
          onClose={() => setSlide(null)}
          onError={(m) => setError(m)}
          onApplied={async (notice) => { setSlide(null); await reload(); showToast(notice) }}
        />
      )}

      {slide?.kind === 'raisePo' && detail.supplierQuote && (
        <RaisePurchaseOrder
          supplierQuoteId={detail.supplierQuote.id}
          title={`${detail.supplierQuote.distributor} ${detail.supplierQuote.reference}`}
          onClose={() => setSlide(null)}
          onRaised={async () => { await reload() }}
        />
      )}

      {slide?.kind === 'adhocPo' && (
        <AdhocPurchaseOrder
          quoteId={quoteId}
          quoteNumber={quote.quoteNumber}
          onClose={() => setSlide(null)}
          onRaised={async () => { await reload() }}
        />
      )}

      {slide?.kind === 'amendAcceptance' && detail.acceptance && (
        <AmendAcceptanceSlideOver
          detail={detail}
          amending={amending}
          selections={selections}
          onClose={() => setSlide(null)}
          onStartAmend={() => {
            // Seed the live selection from the frozen record, unlock the preview.
            setSelections(selectionsFromAcceptance(detail.acceptance!.selections, items, groups.map((g) => ({
              id: g.id, kind: g.kind, parentGroupId: g.parentGroupId,
              optional: g.optional, preselected: g.preselected, choiceSet: g.choiceSet ?? null, sort: g.sort,
            }))))
            setAmending(true)
            setPreview(true)
            setEditing(true)   // decision 240: the preview lives in the overlay
            setSlide(null)
          }}
          onRecord={async (payload) => {
            const res = await amendAcceptanceOnBehalf(quoteId, { ...payload, selections, expectedVersion: detail.acceptance!.version })
            setAmending(false)
            setPreview(false)
            setEditing(false)
            await reload()
            showToast(`Acceptance amended — now version ${res.version}`)
            return res
          }}
        />
      )}

      {slide?.kind === 'acceptOnBehalf' && (
        <AcceptOnBehalfSlideOver
          detail={detail}
          selections={selections}
          onClose={() => setSlide(null)}
          onAccept={async (payload) => {
            const res = await acceptQuoteOnBehalf(quoteId, { ...payload, selections })
            await reload()
            return res
          }}
        />
      )}

      {/* Decision 171: the split screen — section→site mapping, shared bucket,
          real-number preview, explicit confirm; or the frozen record once split. */}
      {slide?.kind === 'splitDeals' && (
        <SplitDealsSlideOver
          detail={detail}
          portalId={hsOpts?.portalId ?? null}
          onClose={() => setSlide(null)}
          onDone={async () => { await reload() }}
        />
      )}

      {/* Decision 178: the provisioning pack — per-row order-new vs from-stock,
          the cost-log trail riding as notes; or the frozen record once packed. */}
      {slide?.kind === 'provisioningPack' && (
        <ProvisioningPackSlideOver
          detail={detail}
          onClose={() => setSlide(null)}
          onDone={async () => { await reload() }}
        />
      )}

      {slide?.kind === 'acceptance' && detail.acceptance && (
        <AcceptanceSlideOver detail={detail} onClose={() => setSlide(null)} />
      )}

      {slide?.kind === 'declineOnBehalf' && (
        <DeclineOnBehalfSlideOver
          quoteNumber={quote.quoteNumber}
          onClose={() => setSlide(null)}
          onDecline={async (payload) => {
            await declineQuoteOnBehalf(quoteId, payload)
            await reload()
            showToast('Decline recorded — the deal moves to closed-lost')
          }}
        />
      )}

      {slide?.kind === 'sites' && detail.customer && (
        <SitesSlideOver
          customer={detail.customer}
          sites={sites}
          disabled={ro || busy}
          onClose={() => setSlide(null)}
          onSave={(newSites) => run(async () => {
            await setQuoteCustomer(quoteId, {
              name: detail.customer!.name, urn: detail.customer!.urn,
              postcode: detail.customer!.postcode,
              contactName: detail.customer!.contactName, contactEmail: detail.customer!.contactEmail,
              sites: newSites,
            })
            const newTitle = applyTitleStandard(quote.title || 'New quote', newSites, detail.customer!.name)
            await patchQuote(quoteId, { title: newTitle })
            setSlide(null)
            await reload()
            showToast(`Sites updated — ${newSites.length} site${newSites.length === 1 ? '' : 's'} on the quote`)
          })}
        />
      )}

      {slide?.kind === 'links' && (
        <LinksSlideOver
          detail={detail} mode={mode} people={people}
          onPatch={(patch) => run(async () => { await patchQuote(quoteId, patch); await reload() })}
          onClose={() => setSlide(null)}
          onResend={async (recipientId) => {
            const res = await resendQuote(quoteId, recipientId)
            await reload()
            return res
          }}
        />
      )}

      {slide?.kind === 'activity' && (
        <ActivitySlideOver quoteId={quoteId} quoteNumber={quote.quoteNumber} onClose={() => setSlide(null)} />
      )}

      {startOpen && (
        <StartModal
          mode={mode}
          onClose={() => setStartOpen(false)}
          onPick={pickCustomer}
          onDeal={attachDeal}
        />
      )}
    </div>
  )
}

// ============================================================== picker

/** Additional sites (2026-10-01): a trust site DfE/GIAS doesn't list, from
 *  the Wave9 Hub's register. Its deal carries w9_site_ref, not a URN. */
const SITE_KIND_LABEL: Record<string, string> = {
  annex: 'Annex', send_unit: 'SEND unit', satellite: 'Satellite site', trust_hq: 'Trust HQ', other: 'Other site',
}
function RegisterTag({ s }: { s: { register?: boolean; siteKind?: string | null; hostName?: string | null } }) {
  if (!s.register) return null
  const kind = (s.siteKind && SITE_KIND_LABEL[s.siteKind]) || 'Additional site'
  return (
    <span className="pill pill--info tiny" style={{ marginLeft: 6 }}
      title={`Not listed with DfE — a site on the Wave9 Hub's register${s.hostName ? `, run by ${s.hostName}` : ''}. Its deal is tagged w9_site_ref.`}>
      {kind} · not DfE
    </span>
  )
}

function Picker({ products, mode, filter, onFilter, search, onSearch, onAdd, disabled }: {
  products: PricedProduct[]
  mode: Partner['mode']
  filter: string | null
  onFilter: (l: string | null) => void
  search: string
  onSearch: (s: string) => void
  onAdd: (p: PricedProduct) => void
  disabled: boolean
}) {
  const lines = useMemo(() => [...new Set(products.flatMap((p) => lineKeysFor(p)))].sort(), [products])
  // The contract term is baked into the PRODUCT (decision 44) — so a term
  // filter is how you find "the 3-year variant" without reading every SKU.
  const [termFilter, setTermFilter] = useState<number | 'one-off' | null>(null)
  const terms = useMemo(() => [...new Set(products.map((p) => p.termMonths).filter((t): t is number => t != null))].sort((a, b) => a - b), [products])
  const q = search.trim().toLowerCase()
  const visible = products
    .filter((p) => filter == null || lineKeysFor(p).includes(filter))
    .filter((p) => termFilter == null
      || (termFilter === 'one-off' ? p.termMonths == null : p.termMonths === termFilter))
    .filter((p) => !q || p.name.toLowerCase().includes(q) || (p.sku ?? '').toLowerCase().includes(q))

  return (
    <aside className="picker">
      <div className="picker__head">
        <input className="input" style={{ width: '100%' }} placeholder="Search catalogue — name or SKU…"
          value={search} onChange={(e) => onSearch(e.target.value)} />
        <div className="picker__filters">
          <button className={`fchip ${filter == null ? 'fchip--on' : ''}`} onClick={() => onFilter(null)}>All</button>
          {lines.map((l) => (
            <button key={l} className={`fchip ${filter === l ? 'fchip--on' : ''}`} onClick={() => onFilter(filter === l ? null : l)}>{l}</button>
          ))}
        </div>
        <div className="picker__filters" title="Term filter — the contract term is part of the product">
          <button className={`fchip ${termFilter == null ? 'fchip--on' : ''}`} onClick={() => setTermFilter(null)}>Any term</button>
          {terms.map((t) => (
            <button key={t} className={`fchip ${termFilter === t ? 'fchip--on' : ''}`} onClick={() => setTermFilter(termFilter === t ? null : t)}>{termLabel(t)}</button>
          ))}
          <button className={`fchip ${termFilter === 'one-off' ? 'fchip--on' : ''}`} onClick={() => setTermFilter(termFilter === 'one-off' ? null : 'one-off')}>One-off</button>
        </div>
      </div>
      <div className="picker__list">
        {visible.map((p) => {
          const price = mode === 'reseller' ? p.buyPrice : p.listPrice
          const label = mode === 'reseller' ? 'your buy' : 'list'
          const poa = p.listPrice === 0
          return (
            <button key={p.hsProductId} className="prow" disabled={disabled} onClick={() => onAdd(p)}
              title={p.description ?? 'No description yet — populate in HubSpot'}>
              <div className="prow__add">+</div>
              <div className="prow__body">
                <div className="prow__name">{p.name}</div>
                <div className="prow__meta">
                  <span className="muted" style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 }}>{p.sku ?? '—'}</span>
                  {p.termMonths
                    ? <span className="tag tag--term">{termLabel(p.termMonths)}</span>
                    : <span className="tag">one-off</span>}
                  {poa && <span className="tag tag--int">internal only</span>}
                </div>
              </div>
              <div className="prow__price num">
                {poa ? 'POA' : formatGBP(price ?? p.listPrice)}
                <div className="small muted">{p.recurring ? FREQUENCY_SUFFIX[p.billingFrequency ?? 'annually'] : ''} {poa ? '' : label}</div>
                {mode === 'internal' && p.costPrice != null && (
                  <div className="small muted num">cost {formatGBP(p.costPrice)}</div>
                )}
              </div>
            </button>
          )
        })}
        {visible.length === 0 && <p className="muted small" style={{ padding: 14 }}>Nothing matches.</p>}
      </div>
      <div className="picker__foot">
        {mode === 'internal'
          ? 'Full catalogue · picking a product picks its contract term'
          : 'Hidden products fail closed for this partner · term is part of the product'}
      </div>
    </aside>
  )
}

// ============================================================== line row

function LineRow({ item, group, mode, product, stale, flash, disabled, accepted, onOpen, onQty, onSell, onRemove, onMove }: {
  item: QuoteItemRow
  group: { kind: 'section' | 'choose_one' }
  /**
   * Decision 191: on an ACCEPTED quote, what the customer did with this line —
   * `{ qty }` = taken at that quantity, `null` = offered but not taken.
   * `undefined` = the quote is not accepted (no acceptance badges at all).
   */
  accepted?: { qty: number } | null
  mode: Partner['mode']
  product?: PricedProduct
  stale: boolean
  flash: boolean
  disabled: boolean
  onOpen: () => void
  onQty: (q: number) => void
  onSell: (s: number) => void
  onRemove: () => void
  /** Decision 87: reorder within the section. Absent = read-only / not movable that way. */
  onMove?: (dir: -1 | 1) => void
}) {
  const [floorFlash, setFloorFlash] = useState(false)
  const locked = mode === 'agent' && !item.isAdhoc
  const perYear = annualised(item.sellPrice, item)
  // Decision 191: on an accepted quote the row's figures are the ACCEPTED
  // ones — the accepted qty, or nothing at all for a line the customer left —
  // so the rows add up to the frozen bar above them.
  const untaken = accepted === null
  const shownQty = accepted ? accepted.qty : item.qty
  const annual = item.recurring && !untaken ? perYear * shownQty : 0
  const oneOff = item.recurring || untaken ? 0 : item.sellPrice * shownQty
  const years = termYears(item)

  const margin = (() => {
    if (mode === 'agent') return { label: 'list price (agent)', cls: 'muted' }
    const buy = item.buyPrice ?? item.listPrice
    if (mode === 'reseller') {
      const m = item.sellPrice - buy
      return { label: `buy ${formatGBP(buy)} · margin ${formatGBP(m * item.qty)}`, cls: m > 0 ? 'margin-ok' : 'margin-zero' }
    }
    // internal: buy IS the frozen cost basis (decision 53) — for ad-hoc lines
    // that's the cost typed in the line's slide-over (2026-08-01). An ABSENT
    // HubSpot cost is treated as £0 (amended 2026-08-05) — the margin shows,
    // badged as indicative rather than depressed to a false 0%.
    const noCost = item.isAdhoc ? false : (product ? product.costPrice == null : false)
    const m = item.sellPrice - buy
    const pct = item.sellPrice > 0 ? Math.round((m / item.sellPrice) * 100) : 0
    return {
      label: noCost ? `margin ${pct}% · no HubSpot cost (vs £0)` : `cost ${formatGBP(buy)} · margin ${pct}%`,
      cls: noCost ? 'margin-zero' : m > 0 ? 'margin-ok' : 'margin-zero',
    }
  })()

  const commitSell = (el: HTMLInputElement) => {
    let v = Number(el.value)
    if (!Number.isFinite(v)) { el.value = item.sellPrice.toFixed(2); return }
    if (!item.isAdhoc) {
      const check = checkSellPrice(mode, {
        listPrice: item.listPrice, buyPrice: item.buyPrice ?? item.listPrice, sellPrice: v,
      })
      if (!check.ok) {
        v = check.corrected
        setFloorFlash(true)
        setTimeout(() => setFloorFlash(false), 1200)
      }
    }
    v = Math.max(0, Math.round(v * 100) / 100)
    el.value = v.toFixed(2)
    if (v !== item.sellPrice) onSell(v)
  }

  const badges = [
    // Decision 191: the acceptance verdict leads on an accepted quote.
    accepted && <span key="acc" className="pill pill--ok" title="In the customer's frozen acceptance record at this quantity">
      ✓ accepted × {accepted.qty}{accepted.qty !== item.qty ? ` (offered ${item.qty})` : ''}</span>,
    untaken && <span key="unt" className="pill" title="Offered, but not in the customer's acceptance record">not taken</span>,
    group.kind !== 'choose_one' && item.optionality === 'optional' && <span key="opt" className="pill">{item.preselected ? 'optional' : 'optional · unticked by default'}</span>,
    group.kind === 'choose_one' && item.preselected && <span key="pre" className="pill pill--ok">pre-selected</span>,
    item.qtyEditable && <span key="qty" className="pill pill--info">qty {item.qtyMin}–{item.qtyMax} customer-editable</span>,
    item.subjectToSurvey && <span key="sur" className="pill pill--warn">subject to survey</span>,
    item.isAdhoc && <span key="adh" className="pill" style={{ background: '#efe6f7', color: '#5b2e91' }}>ad-hoc</span>,
    !item.isAdhoc && item.termMonths != null && <span key="term" className="pill">{termLabel(item.termMonths)} term{item.billingFrequency && item.billingFrequency !== 'annually' && item.billingFrequency !== 'one_time' ? ` · billed ${FREQUENCY_LABEL[item.billingFrequency].toLowerCase()}` : ''}</span>,
    stale && <span key="stale" className="pill pill--warn">catalogue moved</span>,
    // Decision 97: cost override state — unlogged nags until an entry covers it.
    item.costVariedAt && !item.costVariationId && <span key="cvu" className="pill pill--warn" title="The cost was changed but no variation entry covers it yet — log it">cost changed — unlogged</span>,
    item.costVariedAt && item.costVariationId && <span key="cvl" className="pill pill--info" title="Covered by a cost-variation entry — see the activity trail / Cost log">cost varied</span>,
  ].filter(Boolean)

  return (
    <div className={`gline ${flash ? 'gline--flash' : ''} ${stale ? 'gline--stale' : ''} ${untaken ? 'gline--untaken' : ''}`} data-line={item.id} onClick={onOpen}>
      <div>
        <div className="gline__name">{item.name}</div>
        <div className={`gline__desc ${item.description ? '' : 'gline__desc--missing'}`}>
          {item.description ?? 'No description yet — will read straight from HubSpot once populated'}
        </div>
        <div className="gline__badges">{badges}</div>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <span className="qty">
          <button disabled={disabled} onClick={() => onQty(Math.max(0.01, Math.round((item.qty - 1) * 100) / 100))}>−</button>
          <input key={item.qty} defaultValue={item.qty} disabled={disabled}
            title="Decimal quantities to 2dp are fine — e.g. 2.5 days"
            onBlur={(e) => { const n = Math.min(QTY_MAX, Math.max(0.01, Math.round((Number(e.target.value) || item.qty) * 100) / 100)); if (n !== item.qty) onQty(n) }}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />
          <button disabled={disabled} onClick={() => onQty(Math.min(QTY_MAX, Math.round((item.qty + 1) * 100) / 100))}>+</button>
        </span>
      </div>
      <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
        <input
          className={`sellin ${floorFlash ? 'sellin--floor' : ''}`}
          key={item.sellPrice}
          defaultValue={item.sellPrice.toFixed(2)}
          disabled={disabled || locked}
          title={locked ? 'Agent prices are locked to list' : undefined}
          onBlur={(e) => commitSell(e.target)}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
        <div className={`gline__margin ${margin.cls}`}>{margin.label}</div>
      </div>
      <div className="gline__total num">
        {untaken
          ? <><span className="muted">—</span><div className="small muted">not taken</div></>
          : <>
              {annual
                ? years < 1
                  ? <>{formatGBP(annual * years)}<span className="small muted"> over {termLabel(item.termMonths)}</span></>
                  : <>{formatGBP(annual)}<span className="small muted">/yr</span></>
                : formatGBP(oneOff)}
              <div className="small muted">{annual ? years < 1 ? `${formatGBP(item.sellPrice)}${FREQUENCY_SUFFIX[item.billingFrequency ?? 'annually']} each` : `${formatGBPCompact(annual * years)} over term` : 'one-off'}</div>
            </>}
      </div>
      <span className="gline__movers" onClick={(e) => e.stopPropagation()}>
        {onMove && (
          <>
            <button className="gline__x" disabled={disabled} title="Move up" onClick={() => onMove(-1)}>↑</button>
            <button className="gline__x" disabled={disabled} title="Move down" onClick={() => onMove(1)}>↓</button>
          </>
        )}
      </span>
      <button className="gline__x" disabled={disabled} onClick={(e) => { e.stopPropagation(); onRemove() }}>×</button>
    </div>
  )
}

// ============================================================== totals bar

function TotalsBar({ detail, mode, onEditFee }: { detail: QuoteDetail; mode: Partner['mode']; onEditFee: () => void }) {
  const t = detail.totals
  const fee = detail.quote.referralFeeOverride ?? detail.partner.referralFee ?? 0
  return (
    <div className="totals">
      <div className="tot"><span className="tiny">One-off</span><span className="num">{formatGBPCompact(t.oneOffSell)}</span></div>
      <div className="tot"><span className="tiny">Annual recurring</span><span className="num">{formatGBPCompact(t.annualSell)}<span className="small muted">/yr</span></span></div>
      <div className="tot"><span className="tiny">Contract value</span><span className="num">{formatGBPCompact(t.contractValueSell)}</span></div>
      {mode === 'reseller' && t.contractValueBuy != null && (
        <>
          <div className="tot tot--margin"><span className="tiny">Your buy (contract)</span><span className="num">{formatGBPCompact(t.contractValueBuy)}</span></div>
          <div className="tot tot--margin"><span className="tiny">Your margin</span><span className={`num ${((t.marginAmount ?? 0) > 0) ? 'margin-ok' : 'margin-zero'}`}>{formatGBPCompact(t.marginAmount ?? 0)}</span></div>
        </>
      )}
      {mode === 'agent' && (
        <div className="tot tot--margin"><span className="tiny">Referral fee on acceptance</span>
          <span className="num">{formatGBPCompact(fee)} <button className="btn btn--sm btn--ghost" title="Per-quote override" onClick={onEditFee}>edit</button></span>
        </div>
      )}
      {mode === 'internal' && t.contractValueBuy != null && (
        <>
          <div className="tot tot--margin"><span className="tiny">Wave9 cost (contract)</span><span className="num">{formatGBPCompact(t.contractValueBuy)}</span></div>
          <div className="tot tot--margin"><span className="tiny">Internal margin</span>
            <span className={`num ${((t.marginAmount ?? 0) > 0) ? 'margin-ok' : 'margin-zero'}`}>{Math.round((t.marginPct ?? 0) * 100)}%</span></div>
        </>
      )}
      <div className="totals__spacer" />
      {/* Decision 120: an accepted quote's bar shows the FROZEN accepted value
          (matching the quotes list and the HubSpot deal) — say so. */}
      <span className="tiny muted">
        {/* Decision 125: a template stores no prices, so "frozen at creation"
            would be a copy leak from the quote world (the #123 lesson). */}
        {detail.quote.templateWorkspaceFor
          ? 'ex VAT · today’s catalogue, for reference — a template stores no prices; every quote reprices on use'
          : detail.quote.status === 'accepted'
            ? 'ex VAT · the customer’s accepted selections — frozen at acceptance, matches the HubSpot deal'
            : 'ex VAT · at default selections · prices frozen at creation — drafts flag catalogue drift'}
      </span>
    </div>
  )
}

// ============================================================== line slide-over

/**
 * Decision 154: desired margin -> sell price, beside every cost entry in the
 * slide-over. Staff type the margin % they want on the line's cost basis and
 * apply the computed sell price (sell = cost ÷ (1 − margin) — the same gross-
 * margin definition every other margin figure in the portal uses). Reads the
 * item's CURRENT buyPrice: the cost input commits on blur, so tabbing across
 * to this field prices off the freshly-set cost.
 *
 * ⚠️ Module-level on purpose — a component defined inside the panel remounts
 * on every parent render and drops focus mid-type (the standing React footgun).
 */
function DesiredMarginRow({ cost, sell, disabled, onApply }: {
  cost: number
  sell: number
  disabled: boolean
  onApply: (sell: number) => void
}) {
  const [pct, setPct] = useState('')
  const n = Number(pct)
  const computed = pct.trim() === '' ? null : sellForMargin(cost, n / 100)
  const current = margin(sell, cost)
  if (cost <= 0) {
    return (
      <div className="hint" style={{ marginTop: 6 }}>
        Desired margin needs a cost above £0 — at £0 every sell price is 100% margin.
      </div>
    )
  }
  return (
    <div className="rowflex" style={{ marginTop: 8 }}>
      <label className="small muted">Desired margin %</label>
      <input className="input" style={{ width: 70, textAlign: 'right' }} inputMode="decimal"
        value={pct} disabled={disabled}
        placeholder={(current.marginPct * 100).toFixed(1)}
        onChange={(e) => setPct(e.target.value)} />
      {pct.trim() !== '' && computed == null && (
        <span className="small muted">0–99.9 only</span>
      )}
      {computed != null && (
        <span className="small num">→ sell {formatGBP(computed)}</span>
      )}
      <button className="btn btn--sm" disabled={disabled || computed == null || computed === sell}
        onClick={() => { if (computed != null) { onApply(computed); setPct('') } }}>
        Set sell price
      </button>
      <span className="tiny muted">now {formatGBP(sell)} · {(current.marginPct * 100).toFixed(1)}%</span>
    </div>
  )
}

function LineSlideOver({ item, mode, group, product, disabled, onClose, onPatch, onRemove }: {
  item: QuoteItemRow
  mode: Partner['mode']
  group?: { kind: 'section' | 'choose_one' }
  product?: PricedProduct
  disabled: boolean
  onClose: () => void
  onPatch: (patch: Record<string, unknown>) => void
  onRemove: () => void
}) {
  const chooseOne = group?.kind === 'choose_one'
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover">
        <div className="slideover__head">
          <div>
            <h2>{item.name}</h2>
            <div className="small muted" style={{ marginTop: 3 }}>
              {item.isAdhoc
                ? 'Ad-hoc line (internal only)'
                : <>
                    <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5 }}>{product?.sku ?? item.hsProductId}</span>
                    {product?.category ? ` · ${product.category}` : ''}
                    {item.termMonths ? ` · ${termLabel(item.termMonths)} term (baked into the product)` : ''}
                  </>}
            </div>
          </div>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          {item.isAdhoc && (
            <div className="field">
              <label>Line name</label>
              <input className="input" style={{ width: '100%' }} key={item.name} defaultValue={item.name} disabled={disabled}
                onBlur={(e) => e.target.value.trim() && e.target.value !== item.name && onPatch({ name: e.target.value.trim() })} />
            </div>
          )}
          <div className="field">
            <label>Line description — customer-facing quote copy</label>
            <textarea className="input" rows={3} key={item.description ?? ''} defaultValue={item.description ?? ''} disabled={disabled}
              onBlur={(e) => e.target.value !== (item.description ?? '') && onPatch({ description: e.target.value || null })} />
            <div className="hint">{item.isAdhoc
              ? 'Free text — this line exists only on this quote.'
              : 'Prefills from the HubSpot product description and froze into this quote at creation. Edits here affect this quote only.'}</div>
          </div>

          {/* Decision 107 (2026-08-08): sell price edits in the slide-over too —
              same rules as the canvas row (agent stays locked to list). Sits
              ABOVE the cost override, Mark's requested order. */}
          {mode !== 'agent' && (
            <div className="field">
              <label>Sell price override</label>
              <div className="rowflex">
                <label className="small muted">Sell £</label>
                <input className="input" style={{ width: 100, textAlign: 'right' }} key={`sp${item.sellPrice}`} defaultValue={item.sellPrice.toFixed(2)} disabled={disabled}
                  onBlur={(e) => {
                    const n = Number(e.target.value)
                    if (!Number.isFinite(n) || n < 0) return
                    const floor = mode === 'reseller' && !item.isAdhoc && item.buyPrice != null ? item.buyPrice : 0
                    const v = Math.max(floor, n)
                    if (v !== item.sellPrice) onPatch({ sellPrice: v })
                  }} />
                {mode === 'reseller' && !item.isAdhoc && item.buyPrice != null && (
                  <span className="small muted">floor {formatGBP(item.buyPrice)} — your buy price</span>
                )}
              </div>
              <div className="hint">
                {mode === 'internal'
                  ? 'The unit price the customer sees — the same edit as the price on the canvas row.'
                  : 'Your sell price for this line — it never goes below your buy price (the floor applies automatically).'}
              </div>
            </div>
          )}

          {mode === 'internal' && !item.isAdhoc && product?.listPrice !== 0 && (
            <div className="field">
              <label>Cost override (internal only)</label>
              <div className="rowflex">
                <label className="small muted">Cost £</label>
                <input className="input" style={{ width: 100, textAlign: 'right' }} key={`cv${item.buyPrice}`} defaultValue={(item.buyPrice ?? 0).toFixed(2)} disabled={disabled}
                  onBlur={(e) => { const n = Number(e.target.value); if (Number.isFinite(n) && n >= 0 && n !== item.buyPrice) onPatch({ costPrice: n }) }} />
                {item.costVariedAt && !item.costVariationId && <span className="pill pill--warn">unlogged — add a variation entry</span>}
                {item.costVariedAt && item.costVariationId && <span className="pill pill--info">covered by a variation entry</span>}
              </div>
              <DesiredMarginRow cost={item.buyPrice ?? 0} sell={item.sellPrice} disabled={disabled}
                onApply={(sell) => onPatch({ sellPrice: sell })} />
              <div className="hint">
                Overrides the HubSpot unit cost for THIS quote's margin basis.
                Every change needs a cost-variation entry as supporting evidence — the banner
                above the canvas collects it (supplier quote upload or a note).
                Desired margin prices the line FROM that cost: type the margin %
                you want and apply the computed sell price.
              </div>
            </div>
          )}

          {(item.isAdhoc || (mode === 'internal' && !item.isAdhoc && product?.listPrice === 0)) && (
            <div className="field">
              <label>{item.isAdhoc ? 'Ad-hoc pricing' : 'Price-on-application pricing (£0-list product)'}</label>
              <div className="rowflex">
                <label className="small muted">List £</label>
                <input className="input" style={{ width: 100, textAlign: 'right' }} key={`al${item.listPrice}`} defaultValue={item.listPrice.toFixed(2)} disabled={disabled}
                  onBlur={(e) => { const n = Number(e.target.value); if (Number.isFinite(n) && n >= 0 && n !== item.listPrice) onPatch({ listPrice: n }) }} />
                <label className="small muted">Cost £</label>
                <input className="input" style={{ width: 100, textAlign: 'right' }} key={`ac${item.buyPrice}`} defaultValue={(item.buyPrice ?? 0).toFixed(2)} disabled={disabled}
                  onBlur={(e) => { const n = Number(e.target.value); if (Number.isFinite(n) && n >= 0 && n !== item.buyPrice) onPatch({ costPrice: n }) }} />
              </div>
              <DesiredMarginRow cost={item.buyPrice ?? 0} sell={item.sellPrice} disabled={disabled}
                onApply={(sell) => onPatch({ sellPrice: sell })} />
              <div className="hint">
                {item.isAdhoc
                  ? 'Cost is the margin basis for this line — £0 is a real cost. Sell price is edited on the row, or from the cost via desired margin.'
                  : 'This product is priced per quote (£0 in the price book, internal-only). Set its real list and cost here; sell price is edited on the row, or from the cost via desired margin.'}
              </div>
            </div>
          )}

          {!chooseOne ? (
            <div className="field">
              <label>Optionality</label>
              <div className="choices">
                <label className={`choice ${item.optionality === 'required' ? 'choice--on' : ''}`}>
                  <input type="radio" checked={item.optionality === 'required'} disabled={disabled} onChange={() => onPatch({ optionality: 'required' })} />
                  <span><strong>Required</strong> <span className="muted small">always on the quote</span></span>
                </label>
                <label className={`choice ${item.optionality === 'optional' ? 'choice--on' : ''}`}>
                  <input type="radio" checked={item.optionality === 'optional'} disabled={disabled} onChange={() => onPatch({ optionality: 'optional' })} />
                  <span><strong>Optional</strong> <span className="muted small">customer ticks it on the quote page</span></span>
                </label>
              </div>
              {/* Decision 206 (Mark, 2026-09-09): an optional line used to open
                  TICKED every time; some options should be offered unticked
                  and left for the customer to add. `preselected` always
                  carried this — the customer page and the pricing model read
                  it — the builder just never exposed it for optional lines. */}
              {item.optionality === 'optional' && (
                <div className="choices" style={{ marginTop: 8 }} data-testid="optional-default">
                  <label className={`choice ${item.preselected ? 'choice--on' : ''}`}>
                    <input type="radio" checked={item.preselected} disabled={disabled} onChange={() => onPatch({ preselected: true })} />
                    <span><strong>Ticked by default</strong> <span className="muted small">on the quote — the customer can untick it</span></span>
                  </label>
                  <label className={`choice ${!item.preselected ? 'choice--on' : ''}`}>
                    <input type="radio" checked={!item.preselected} disabled={disabled} onChange={() => onPatch({ preselected: false })} />
                    <span><strong>Unticked by default</strong> <span className="muted small">offered — the customer adds it if they want it</span></span>
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div className="field">
              <label>Choose-one group</label>
              <label className={`choice ${item.preselected ? 'choice--on' : ''}`}>
                <input type="checkbox" checked={item.preselected} disabled={disabled} onChange={(e) => onPatch({ preselected: e.target.checked })} />
                <span><strong>Pre-selected</strong> <span className="muted small">the default pick when the customer opens the quote</span></span>
              </label>
              <div className="hint">
                Untick every option in the group and it becomes <strong>optional</strong> —
                the quote opens with none selected and the customer can accept without choosing one.
              </div>
            </div>
          )}

          <div className="field">
            <label>Customer-adjustable quantity</label>
            <label className={`choice ${item.qtyEditable ? 'choice--on' : ''}`}>
              <input type="checkbox" checked={item.qtyEditable} disabled={disabled} onChange={(e) => onPatch({ qtyEditable: e.target.checked })} />
              <span><strong>Customer can change quantity</strong> <span className="muted small">within bounds — unit price never changes</span></span>
            </label>
            {item.qtyEditable && (
              <div className="rowflex" style={{ marginTop: 8 }}>
                <label className="small muted">Min</label>
                <input className="input" style={{ width: 70 }} key={`min${item.qtyMin}`} defaultValue={item.qtyMin ?? 1} disabled={disabled}
                  onBlur={(e) => onPatch({ qtyMin: Math.max(0, Math.round((Number(e.target.value) || 1) * 100) / 100) })} />
                <label className="small muted">Max</label>
                <input className="input" style={{ width: 70 }} key={`max${item.qtyMax}`} defaultValue={item.qtyMax ?? 99} disabled={disabled}
                  onBlur={(e) => onPatch({ qtyMax: Math.min(QTY_MAX, Math.max(0.01, Math.round((Number(e.target.value) || 99) * 100) / 100)) })} />
              </div>
            )}
          </div>

          <div className="field">
            <label>Survey</label>
            <label className={`choice ${item.subjectToSurvey ? 'choice--on' : ''}`}>
              <input type="checkbox" checked={item.subjectToSurvey} disabled={disabled} onChange={(e) => onPatch({ subjectToSurvey: e.target.checked })} />
              <span><strong>Subject to survey</strong> <span className="muted small">customer page badges “price may vary following survey”</span></span>
            </label>
          </div>

          <div className="field">
            <label>Pricing ({mode})</label>
            <dl className="kv">
              <dt>List</dt><dd>{formatGBP(item.listPrice)}</dd>
              {mode === 'reseller' && item.buyPrice != null && (
                <><dt>Your buy</dt><dd>{formatGBP(item.buyPrice)} <span className="muted small">(floor for your sell price)</span></dd></>
              )}
              {mode === 'internal' && !item.isAdhoc && (
                <><dt>Wave9 cost</dt><dd>
                  {product?.costPrice != null
                    ? <>{formatGBP(product.costPrice)}{product.costPrice === 0 ? ' — real £0, 100% margin' : ''}</>
                    : 'absent in HubSpot — treated as £0, margin indicative'}
                </dd></>
              )}
              <dt>This quote</dt><dd>{formatGBP(item.sellPrice)} {mode === 'agent' ? '(locked to list)' : ''}</dd>
            </dl>
          </div>
        </div>
        <div className="slideover__foot">
          <button className="btn btn--danger" disabled={disabled} onClick={onRemove}>Remove line</button>
          <span style={{ flex: 1 }} />
          <button className="btn btn--primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </>
  )
}

// ============================================================== attachments

/**
 * Decision 180: the 0–99 priority box — commits on blur or Enter, resets to
 * the server value on reload (the key at the call site handles that).
 */
function PriorityInput({ value, disabled, onCommit }: { value: number; disabled: boolean; onCommit: (p: number) => void }) {
  const [draft, setDraft] = useState(String(value))
  const commit = () => {
    const n = Math.min(99, Math.max(0, Math.round(Number(draft))))
    if (!Number.isFinite(Number(draft)) || draft.trim() === '') { setDraft(String(value)); return }
    if (n !== value) onCommit(n)
    else setDraft(String(n))
  }
  return (
    <input className="input" type="number" min={0} max={99} value={draft} disabled={disabled}
      style={{ width: 58, textAlign: 'right' }} title="Priority 0–99 — lower shows first; ties keep attach order"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
    />
  )
}

function AttachmentsSlideOver({ assets, attachments, attached, mode, staffish, disabled, onClose, onToggle, onUpload, onReorder }: {
  assets: AssetRow[]
  /** The quote's attachment rows — one-offs (adhoc) appear here, never in `assets`. */
  attachments: QuoteAttachment[]
  attached: string[]
  mode: Partner['mode']
  /** True for a staff session — their library saves go to the Wave9 global library. */
  staffish: boolean
  disabled: boolean
  onClose: () => void
  onToggle: (assetId: string, on: boolean) => void
  /** Decision 157: upload a one-off (or save-to-library) attachment. */
  onUpload: (file: File, title: string, scope: 'quote' | 'library') => Promise<void>
  /** Decision 180: set a 0–99 priority or nudge a row up/down the display order. */
  onReorder: (body: { assetId: string; priority?: number; move?: 'up' | 'down' }) => void
}) {
  const sorted = [...assets].sort((a, b) => Number(b.suggested) - Number(a.suggested) || a.title.localeCompare(b.title))
  const [upFile, setUpFile] = useState<File | null>(null)
  const [upTitle, setUpTitle] = useState('')
  const [toLibrary, setToLibrary] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [upError, setUpError] = useState<string | null>(null)
  // Decision 157 ∩ decision 135: agents have no library of their own, so their
  // uploads are always quote-scoped; staff save to the Wave9 library, a
  // reseller session to theirs.
  const libraryOffered = staffish || mode === 'reseller'
  const doUpload = async () => {
    if (!upFile) return
    setUploading(true); setUpError(null)
    try {
      await onUpload(upFile, upTitle.trim() || upFile.name.replace(/\.pdf$/i, ''), toLibrary ? 'library' : 'quote')
      setUpFile(null); setUpTitle(''); setToLibrary(false)
    } catch (e) {
      setUpError((e as Error).message)
    } finally {
      setUploading(false)
    }
  }
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover">
        <div className="slideover__head">
          <div>
            <h2>Attachments</h2>
            <div className="small muted" style={{ marginTop: 3 }}>Wave9 asset library — auto-suggested from the products on this quote. Version-pinned at send.</div>
          </div>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          {attachments.length > 0 && (
            <div className="field" style={{ marginBottom: 14 }}>
              <label>Attached — in the order the customer sees them</label>
              <div className="small muted" style={{ marginBottom: 6 }}>
                Priority 0–99 (default 50) — lower shows first; the PDF pack follows the same order. Arrows swap a document with its neighbour.
              </div>
              {attachments.map((a, i) => (
                <div key={a.assetId} className="rowflex" style={{ gap: 8, alignItems: 'center', padding: '4px 0' }}>
                  <span className="muted small" style={{ width: 18, textAlign: 'right' }}>{i + 1}.</span>
                  <span style={{ display: 'inline-flex', gap: 2 }}>
                    <button className="btn btn--ghost btn--sm" title="Move up" disabled={disabled || i === 0}
                      onClick={() => onReorder({ assetId: a.assetId, move: 'up' })}>↑</button>
                    <button className="btn btn--ghost btn--sm" title="Move down" disabled={disabled || i === attachments.length - 1}
                      onClick={() => onReorder({ assetId: a.assetId, move: 'down' })}>↓</button>
                  </span>
                  <PriorityInput key={`${a.assetId}:${a.priority}`} value={a.priority} disabled={disabled}
                    onCommit={(p) => onReorder({ assetId: a.assetId, priority: p })} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong>{a.title}</strong>{' '}
                    {a.adhoc && <span className="pill pill--info tiny">one-off — not in the library</span>}
                  </span>
                  {a.adhoc && !disabled && (
                    <button className="btn btn--ghost btn--sm" title="Remove from this quote" onClick={() => onToggle(a.assetId, false)}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}
          {sorted.length === 0 && (
            <p className="muted small">The asset library is empty — Wave9 staff upload case studies and product sheets on the Asset library page.</p>
          )}
          <div className="choices">
            {sorted.map((a) => {
              const on = attached.includes(a.id)
              return (
                <label key={a.id} className={`choice ${on ? 'choice--on' : ''}`}>
                  <input type="checkbox" checked={on} disabled={disabled} onChange={(e) => onToggle(a.id, e.target.checked)} />
                  <span><strong>{a.title}</strong>{' '}
                    {a.category && <span className="pill tiny" style={pillStyleFor(a.category)}>{a.category}</span>}{' '}
                    <span className="muted small">{a.suggested ? 'suggested — matches this quote\u2019s product lines' : ''} v{a.version}</span>
                  </span>
                </label>
              )
            })}
          </div>
          {!disabled && (
            <div className="field" style={{ marginTop: 14 }}>
              <label>Upload a document — one-offs welcome</label>
              <div className="rowflex" style={{ flexWrap: 'wrap', gap: 8 }}>
                <input type="file" accept="application/pdf" key={upFile ? upFile.name : 'empty'}
                  onChange={(e) => { const f = e.target.files?.[0] ?? null; setUpFile(f); if (f && !upTitle) setUpTitle(f.name.replace(/\.pdf$/i, '')) }} />
                <input className="input" style={{ width: 220 }} placeholder="Title the customer sees"
                  value={upTitle} onChange={(e) => setUpTitle(e.target.value)} />
                <button className="btn btn--sm btn--primary" disabled={!upFile || uploading} onClick={doUpload}>
                  {uploading ? 'Uploading…' : 'Upload & attach'}
                </button>
              </div>
              {libraryOffered && (
                <label className={`choice ${toLibrary ? 'choice--on' : ''}`} style={{ marginTop: 8 }}>
                  <input type="checkbox" checked={toLibrary} onChange={(e) => setToLibrary(e.target.checked)} />
                  <span><strong>Also save to the {staffish ? 'Wave9 library' : 'your library'}</strong>{' '}
                    <span className="muted small">untick for a one-off — it lives on this quote only</span></span>
                </label>
              )}
              {upError && <div className="hint" style={{ color: 'var(--stop)' }}>{upError}</div>}
              <div className="hint">PDF only, up to ~4.5MB. A one-off never appears in the library or in templates; it follows a supersede or duplicate of this quote.</div>
            </div>
          )}

          <p className="hint" style={{ marginTop: 12 }}>Shown to the customer as a “Supporting documents” section. Version-pinned at send.</p>
        </div>
        <div className="slideover__foot"><button className="btn btn--primary" onClick={onClose}>Done</button></div>
      </div>
    </>
  )
}

// ============================================================== send

/**
 * Decision 90: a delegated (send-as-the-owner) failure is BLOCKED, never
 * silently substituted with noreply@ — so it must be impossible to miss here.
 * Self-serve causes tell the person what THEY can do; admin-dependent ones
 * say so plainly rather than sending them round a sign-out loop that won't help.
 */
function MailFailureBanner({ failures }: { failures: MailFailure[] }) {
  if (failures.length === 0) return null
  return (
    <>
      {failures.map((f, i) => (
        <div key={`${f.email}-${i}`} className="banner" style={{ marginBottom: 8, borderColor: 'var(--stop)', background: '#fdf1f0' }}>
          ⛔ <span>
            <strong>The email to {f.email} was NOT sent</strong> — and it was not substituted with the shared mailbox.
            {' '}{f.guidance}
            {' '}<span className="small muted">({f.selfServe ? 'you can fix this yourself' : 'needs your IT admin'} · {f.cause})</span>
            <br /><span className="small">The personal link below still works — you can copy it into your own email in the meantime.</span>
          </span>
        </div>
      ))}
    </>
  )
}

function SendSlideOver({ detail, partner, people, snippets, snippetTokens, termsSets, quoteCategories, disabled, onClose, onPatch, onAddRecipient, onRemoveRecipient, onSaveSnippet, onSend, onMarkSent, onPreviewLink }: {
  detail: QuoteDetail
  partner: Partner
  /** Decision 156: the Wave 9 roster for the internal BCC picker. */
  people: Array<{ id: string; name: string }>
  snippets: SnippetRow[]
  snippetTokens: SnippetTokenRow[]
  termsSets: TermsSetRow[]
  quoteCategories: Array<string | null>
  disabled: boolean
  onClose: () => void
  /** Returns the patch promise so Send can FLUSH unsaved edits before the email renders. */
  onPatch: (patch: Record<string, unknown>) => void | Promise<void>
  onAddRecipient: (r: { name: string; email: string; hsContactId?: string | null }) => void
  onRemoveRecipient: (id: string) => void
  onSaveSnippet: (title: string, body: string) => void
  onSend: () => Promise<SendResult>
  /** Decision 95: everything send does, minus the email. */
  onMarkSent: () => Promise<SendResult>
  onPreviewLink: () => Promise<string>
}) {
  const { quote, recipients } = detail
  const [note, setNote] = useState(quote.coverNote)
  const [footer, setFooter] = useState(quote.footerText)
  // Decision 107: the formatting toolbars operate on these textareas directly.
  const noteRef = useRef<HTMLTextAreaElement | null>(null)
  const footerRef = useRef<HTMLTextAreaElement | null>(null)

  // Decision 106: tokens expand the moment a snippet is inserted, from data
  // already on THIS quote — the result stays ordinary editable text, and a
  // token with no data stays visibly literal for a human to fill.
  const firstRecipient = recipients[0]?.name?.trim() ?? ''
  const tokenCtx: TokenContext = {
    recipient_first_name: firstRecipient.split(/\s+/)[0] || undefined,
    recipient_last_name: firstRecipient.split(/\s+/).slice(1).join(' ') || undefined,
    customer_name: detail.customer?.name ?? undefined,
    contract_end_date: detail.renewal?.contractEndDate ? formatUKDate(detail.renewal.contractEndDate) : undefined,
    contract_arr: detail.renewal?.hsArr != null ? formatGBP(detail.renewal.hsArr) : undefined,
    quote_number: quote.quoteNumber,
    valid_until: quote.validUntil ? formatUKDate(quote.validUntil) : undefined,
    owner_name: detail.owner?.name ?? undefined,
    // Decision 148: the issuer's name, same rule as the customer page. The
    // website/phone/email tokens resolve at SEND (server-side re-expansion,
    // #128) — the builder doesn't hold branding details or Organisation
    // settings, so they stay visibly literal here like the recipient tokens.
    partner_name: partner.mode === 'reseller' ? (partner.branding.footer ?? partner.name) : 'Wave 9 Managed Services',
    today_date: formatUKDate(new Date().toISOString()),
  }
  const insertSnippet = (body: string) => expandSnippetTokens(body, snippetTokens, tokenCtx)
  const [contactQ, setContactQ] = useState('')
  const [hits, setHits] = useState<ContactHit[]>([])
  const [contactsDown, setContactsDown] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [feeText, setFeeText] = useState(quote.referralFeeOverride?.toString() ?? '')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  // The send email renders server-side from the SAVED quote, so any cover-note/
  // footer text still only in this panel's state must land first. Belt-and-braces
  // over the persist-on-insert fix above: it also closes the blur-vs-click race
  // when Send is the very next click after typing.
  const flushEdits = async () => {
    if (note !== quote.coverNote) await onPatch({ coverNote: note })
    if (footer !== quote.footerText) await onPatch({ footerText: footer })
  }
  const [sendResult, setSendResult] = useState<SendResult | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()

  const usable = termsSets.filter((s) => s.currentVersion)
  const suggestion = suggestTermsSet(
    usable.map((s) => ({ id: s.id, name: s.name, suggestLines: s.suggestLines })),
    quoteCategories,
  )
  const selected = usable.find((s) => s.id === quote.termsSetId) ?? null
  const t = detail.totals

  useEffect(() => {
    if (contactQ.trim().length < 2) { setHits([]); return }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      searchContacts(contactQ.trim())
        .then((r) => { setHits(r.contacts); setContactsDown(null) })
        .catch((e: Error) => { setHits([]); setContactsDown(e.message) })
    }, 300)
  }, [contactQ])

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover">
        <div className="slideover__head">
          <div>
            <h2>Send quote</h2>
            <div className="small muted" style={{ marginTop: 3 }}>
              {quote.quoteNumber} · {detail.customer?.name ?? 'no recipient organisation'}
              {detail.sites.length > 1 ? ` · ${detail.sites.length} sites` : ''}
            </div>
          </div>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          {/* Decision 128: tokens this quote's data cannot fill would reach the
              customer literally ("Dear #FirstName"). Warn BEFORE the send, by
              name — send expands everything it can, so these are the genuine
              leftovers (usually a template-borne #FirstName with no recipient). */}
          {detail.unresolvedTokens.length > 0 && (
            <div className="banner banner--stop" style={{ marginBottom: 14 }}>
              ⚠ <span>
                <strong>{detail.unresolvedTokens.join(', ')} {detail.unresolvedTokens.length === 1 ? 'has' : 'have'} nothing to fill from.</strong>{' '}
                {detail.unresolvedTokens.some((t) => /name/i.test(t)) && recipients.length === 0
                  ? 'Add a recipient below and it will fill in on send. '
                  : 'It will be sent exactly as written. '}
                Edit the cover note or footer if that is not what you want.
              </span>
            </div>
          )}
          {!detail.customer && (
            <div className="banner" style={{ marginBottom: 14 }}>
              ⚠ <span><strong>No recipient organisation.</strong> Add one before sending — the deal and conflict checks need the site URN(s).</span>
            </div>
          )}

          <div className="field">
            <label>Terms &amp; conditions (selected per quote, version recorded at acceptance)</label>
            {usable.length === 0 ? (
              <p className="hint">No T&amp;C sets have a version uploaded yet — Wave9 admin manages the library on the Organisation page.
                {partner.mode === 'reseller' ? ' Your own terms (partner record) apply meanwhile.' : ''}</p>
            ) : (
              <select className="select" style={{ width: '100%' }} value={quote.termsSetId ?? ''} disabled={disabled}
                onChange={(e) => onPatch({ termsSetId: e.target.value || null })}>
                <option value="">{partner.mode === 'reseller' ? 'Your own terms of business' : '— choose a set —'}</option>
                {usable.map((s) => <option key={s.id} value={s.id}>{s.name} {s.currentVersion}</option>)}
              </select>
            )}
            {suggestion && quote.termsSetId !== suggestion.id && (
              <div className="hint">💡 Suggested: <b>{suggestion.name}</b> — this quote carries fibre/broadband lines.</div>
            )}
            {partner.mode === 'agent' && <div className="hint">Agent quotes always carry Wave9 terms.</div>}
          </div>

          <div className="field">
            <label>Cover note (top of the quote page and the send email)</label>
            <div className="rowflex" style={{ marginBottom: 6 }}>
              <MarkupToolbar target={() => noteRef.current} disabled={disabled}
                onApply={(t) => { setNote(t); if (t !== quote.coverNote) onPatch({ coverNote: t }) }} />
              {snippets.length > 0 && (
                <select className="select" style={{ flex: 1 }} value="" disabled={disabled}
                  onChange={(e) => {
                    const s = snippets.find((x) => x.id === e.target.value)
                    // ⚠️ Persist immediately (like the toolbar does): the select never
                    // focuses the textarea, so onBlur can't fire — an insert-then-Send
                    // used to email WITHOUT the note (the 0027/0028 bug, 2026-08-10).
                    if (s) { const t = insertSnippet(s.body); setNote(t); if (t !== quote.coverNote) onPatch({ coverNote: t }) }
                  }}>
                  <option value="">Insert from snippet library…</option>
                  {snippets.map((s) => <option key={s.id} value={s.id}>{s.partnerId ? s.title : `${s.title} (Wave9)`}</option>)}
                </select>
              )}
            </div>
            <textarea ref={noteRef} className="input" rows={3} value={note} disabled={disabled} onChange={(e) => setNote(e.target.value)}
              onBlur={() => note !== quote.coverNote && onPatch({ coverNote: note })} />
            <div className="hint">
              Snippets are a starting point — the note stays freely editable. Tokens like #FirstName or #EndDate fill in from this quote when inserted.
              Paragraphs and the toolbar formatting render on the quote page, the PDF and the send email.{' '}
              {note.trim() && (
                <button className="btn btn--sm btn--ghost" disabled={disabled} onClick={() => {
                  const title = window.prompt('Save this note to your snippet library as…', note.slice(0, 40))
                  if (title) onSaveSnippet(title.trim(), note)
                }}>Save as snippet</button>
              )}
            </div>
          </div>

          <div className="field">
            <label>Footer (shown after the contract value)</label>
            <div className="rowflex" style={{ marginBottom: 6 }}>
              <MarkupToolbar target={() => footerRef.current} disabled={disabled}
                onApply={(t) => { setFooter(t); if (t !== quote.footerText) onPatch({ footerText: t }) }} />
              {snippets.length > 0 && (
                <select className="select" style={{ flex: 1 }} value="" disabled={disabled}
                  onChange={(e) => {
                    const s = snippets.find((x) => x.id === e.target.value)
                    // Same persist-on-insert rule as the cover note above.
                    if (s) { const t = insertSnippet(s.body); setFooter(t); if (t !== quote.footerText) onPatch({ footerText: t }) }
                  }}>
                  <option value="">Insert from snippet library…</option>
                  {snippets.map((s) => <option key={s.id} value={s.id}>{s.partnerId ? s.title : `${s.title} (Wave9)`}</option>)}
                </select>
              )}
            </div>
            <textarea ref={footerRef} className="input" rows={3} value={footer} disabled={disabled} onChange={(e) => setFooter(e.target.value)}
              onBlur={() => footer !== quote.footerText && onPatch({ footerText: footer })} />
            <div className="hint">
              One shared snippet library with the cover note — the footer stays freely editable too.{' '}
              {footer.trim() && (
                <button className="btn btn--sm btn--ghost" disabled={disabled} onClick={() => {
                  const title = window.prompt('Save this footer to your snippet library as…', footer.slice(0, 40))
                  if (title) onSaveSnippet(title.trim(), footer)
                }}>Save as snippet</button>
              )}
            </div>
          </div>

          {partner.mode === 'agent' && (
            <div className="field">
              <label>Referral fee on acceptance (fixed £, per-quote override)</label>
              <div className="rowflex">
                <input className="input" style={{ width: 120 }} placeholder={`default ${formatGBPCompact(detail.partner.referralFee ?? 0)}`}
                  value={feeText} disabled={disabled} onChange={(e) => setFeeText(e.target.value)}
                  onBlur={() => {
                    const v = feeText.trim()
                    onPatch({ referralFeeOverride: v === '' ? null : Number(v) })
                  }} />
                <span className="small muted">blank = the partner default</span>
              </div>
            </div>
          )}

          <div className="field">
            <label>Recipients (up to {MAX_RECIPIENTS} — each gets their own tracked link)</label>
            {recipients.map((r) => (
              <span key={r.id} className="chip" style={{ margin: '0 6px 6px 0' }}>
                {r.name || r.email} <span className="muted tiny">{r.email}</span>
                {r.hsContactId
                  ? <span className="pill tiny">HubSpot</span>
                  : <span className="pill pill--info tiny">new — created in HubSpot on send</span>}
                {!disabled && <button onClick={() => onRemoveRecipient(r.id)}>×</button>}
              </span>
            ))}
            {recipients.length < MAX_RECIPIENTS && !disabled && (
              <>
                <input className="input" style={{ width: '100%', marginTop: 4 }}
                  placeholder="Search HubSpot contacts — name, email or domain…"
                  value={contactQ} onChange={(e) => setContactQ(e.target.value)} />
                {contactsDown && contactQ.trim().length >= 2 && (
                  <div className="hint">HubSpot search unavailable ({contactsDown}) — add the contact by hand below.</div>
                )}
                {hits.length > 0 && (
                  <div className="hits">
                    {hits.filter((h) => !recipients.some((r) => r.email === h.email)).map((h) => (
                      <div key={h.hsContactId} className="hits__row"
                        onClick={() => { onAddRecipient({ name: h.name, email: h.email!, hsContactId: h.hsContactId }); setContactQ('') }}>
                        <div style={{ flex: 1 }}>
                          <b>{h.name}</b> <span className="muted small">{h.email}</span>
                          <div className="tiny muted">{h.company ?? ''} {h.jobTitle ? `· ${h.jobTitle}` : ''}</div>
                        </div>
                        <span className="pill tiny">HubSpot</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="rowflex" style={{ marginTop: 8 }}>
                  <input className="input" style={{ flex: 1 }} placeholder="Name" value={newName} onChange={(e) => setNewName(e.target.value)} />
                  <input className="input" style={{ flex: 1.4 }} placeholder="email@school.sch.uk" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                  <button className="btn btn--sm" disabled={!newEmail.includes('@')}
                    onClick={() => { onAddRecipient({ name: newName.trim(), email: newEmail.trim() }); setNewName(''); setNewEmail('') }}>Add</button>
                </div>
                <div className="hint">A contact entered fresh is written back to HubSpot when the quote sends (phase 4 — needs <code>crm.objects.contacts.write</code> on the portal's private app).</div>
              </>
            )}
          </div>

          {/* Decision 156: the internal BCC copy — internal (Wave 9) quotes only.
              Persists on the quote, so resends copy the same person; the
              customer never sees a BCC. */}
          {partner.mode === 'internal' && people.length > 0 && (
            <div className="field">
              <label>BCC — internal copy (Wave 9 only)</label>
              <select className="input" style={{ maxWidth: 320 }} value={quote.bccUserId ?? ''} disabled={disabled}
                onChange={(e) => onPatch({ bccUserId: e.target.value || null })}>
                <option value="">None</option>
                {people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <div className="hint">
                Blind-copied on every quote email — this send and any resends.
                Invisible to the customer; each copy shows in the activity trail.
              </div>
            </div>
          )}

          <div className="field">
            <label>What happens on send</label>
            <dl className="kv">
              <dt>Email from</dt><dd>{detail.mailFrom}{' '}
                <span className="muted small">
                  {detail.owner && detail.mailFrom.toLowerCase() === detail.owner.email.toLowerCase()
                    ? '(sent as the quote owner — replies land with them)'
                    : '(no-reply — Reply-To = quote owner)'}
                </span></dd>
              <dt>HubSpot deal</dt><dd>{partner.mode === 'internal'
                ? (quote.hsDealLinked ? `updates deal ${quote.hsDealId}` : 'created in the direct pipeline')
                : 'created in “Partner Sales”'} <span className="muted small">(phase 4)</span></dd>
              <dt>Terms</dt><dd>{selected ? `${selected.name} ${selected.currentVersion}` : partner.mode === 'reseller' ? 'your own terms' : '— not chosen yet'}</dd>
              <dt>Totals</dt><dd>{formatGBPCompact(t.oneOffSell)} one-off · {formatGBPCompact(t.annualSell)}/yr · {formatGBPCompact(t.contractValueSell)} contract</dd>
              <dt>Prices</dt><dd>frozen at creation — never reprice after send</dd>
            </dl>
          </div>

          {!disabled && (
            <div className="field">
              <label>Preview link (watermarked, non-binding, no accept button)</label>
              <div className="rowflex">
                <button className="btn btn--sm" onClick={() => onPreviewLink().then(setPreviewUrl).catch(() => {})}>
                  {previewUrl ? 'Re-issue preview link' : 'Copy preview link'}
                </button>
                {previewUrl && <input className="input" readOnly style={{ flex: 1, fontSize: 11.5 }} value={previewUrl} onFocus={(e) => e.target.select()} />}
              </div>
              <div className="hint">Share for internal sign-off before sending — it's revoked automatically when the quote sends.</div>
            </div>
          )}

          {sendResult && (
            <div className="field">
              <label>Sent — per-recipient links (each is personal and tracked)</label>
              {sendResult.mail.skipped && (
                <div className="banner" style={{ marginBottom: 8 }}>
                  ✓ <span><strong>Marked as sent — no email went out</strong>. Copy each link below into the document you're sending instead. Terms froze and the HubSpot deal updated exactly as a normal send.</span>
                </div>
              )}
              {!sendResult.mail.skipped && !sendResult.mail.configured && (
                <div className="banner" style={{ marginBottom: 8 }}>
                  ⚠ <span><strong>Email is not configured yet</strong> (MS_* credentials pending) — copy each link into your own email. Nothing was lost: the messages are queued as events.</span>
                </div>
              )}
              {!sendResult.mail.skipped && sendResult.mail.configured && (
                <>
                  <MailFailureBanner failures={sendResult.mail.failures ?? []} />
                  <p className="hint">Emailed {sendResult.mail.sent} of {sendResult.links.length} link(s) — sent as the quote owner where their mailbox allows it, otherwise from the shared address (90).</p>
                </>
              )}
              {sendResult.links.map((l) => (
                <div key={l.recipientId} className="rowflex" style={{ marginBottom: 6 }}>
                  <span className="small" style={{ width: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name || l.email}</span>
                  <input className="input" readOnly style={{ flex: 1, fontSize: 11.5 }} value={l.url} onFocus={(e) => e.target.select()} />
                  <button className="btn btn--sm" onClick={() => navigator.clipboard.writeText(l.url).catch(() => {})}>Copy</button>
                </div>
              ))}
            </div>
          )}
          {sendError && <p className="pill pill--stop" style={{ whiteSpace: 'pre-wrap' }}>{sendError}</p>}
        </div>
        <div className="slideover__foot">
          {sendResult
            ? <>
                <span className="pill pill--ok">{sendResult.mail.skipped ? 'Marked as sent' : 'Quote sent'}</span>
                <span style={{ flex: 1 }} />
                <button className="btn btn--primary" onClick={onClose}>Done</button>
              </>
            : <>
                <span style={{ flex: 1 }} />
                <button className="btn" disabled={disabled || sending || recipients.length === 0}
                  title="freezes terms, mints the per-recipient links and updates the HubSpot deal — but sends NO email. For quotes whose link travels inside another document."
                  onClick={async () => {
                    setSending(true); setSendError(null)
                    try { await flushEdits(); setSendResult(await onMarkSent()) } catch (e) { setSendError((e as Error).message) } finally { setSending(false) }
                  }}>
                  {sending ? 'Working…' : 'Mark as sent'}
                </button>
                <button className="btn btn--primary" disabled={disabled || sending || recipients.length === 0}
                  onClick={async () => {
                    setSending(true); setSendError(null)
                    try { await flushEdits(); setSendResult(await onSend()) } catch (e) { setSendError((e as Error).message) } finally { setSending(false) }
                  }}>
                  {sending ? 'Sending…' : `Send to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`}
                </button>
              </>}
        </div>
      </div>
    </>
  )
}

// ================================================= cost variation (decision 97)

function CostVariationSlideOver({ detail, onClose, onLog }: {
  detail: QuoteDetail
  onClose: () => void
  onLog: (p: { description: string; items: Array<{ itemId: string; newCost: number }>; file?: File | null }) => Promise<unknown>
}) {
  const { items } = detail
  // Unlogged lines pre-tick; any other line can join the same entry.
  const [ticked, setTicked] = useState<Set<string>>(
    () => new Set(items.filter((i) => i.costVariedAt && !i.costVariationId).map((i) => i.id)),
  )
  const [costs, setCosts] = useState<Record<string, string>>({})
  const [description, setDescription] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggle = (id: string) => setTicked((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })

  const fileProblem = file && !/\.(pdf|png|jpe?g)$/i.test(file.name)
    ? 'PDF, PNG or JPG only.'
    : file && file.size > 4_500_000 ? `That file is ${(file.size / 1_000_000).toFixed(1)}MB — the cap is 4.5MB.` : null

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover" role="dialog" aria-label="Log cost variation">
        <div className="slideover__head">
          <div>
            <strong>Log a cost variation</strong>
            <div className="small muted" style={{ marginTop: 3 }}>
              Supporting evidence for cost overrides: one entry can cover several lines.
              Your name and the timestamp are recorded; the entry appears in the activity trail and the Cost log.
            </div>
          </div>
          <button className="btn btn--sm" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          <div className="field">
            <label>Lines this variation covers</label>
            {items.map((i) => {
              const on = ticked.has(i.id)
              const unlogged = Boolean(i.costVariedAt && !i.costVariationId)
              return (
                <div key={i.id} className="rowflex" style={{ padding: '3px 0', alignItems: 'center' }}>
                  <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
                    <input type="checkbox" checked={on} onChange={() => toggle(i.id)} />
                    {i.name}
                    {unlogged && <span className="pill pill--warn">unlogged change</span>}
                  </label>
                  {on && (
                    <>
                      <span className="tiny muted">cost £</span>
                      <input className="input" style={{ width: 90, textAlign: 'right' }}
                        value={costs[i.id] ?? (i.buyPrice ?? 0).toFixed(2)}
                        onChange={(e) => setCosts({ ...costs, [i.id]: e.target.value })} />
                    </>
                  )}
                </div>
              )
            })}
            <div className="hint">The cost shown applies when you log — change it here to adjust further lines in the same entry.</div>
          </div>

          <div className="field">
            <label>What changed, and why? <span className="muted">(required — this is the evidence record)</span></label>
            <textarea className="input" rows={3} style={{ width: '100%' }}
              placeholder="e.g. Supplier quote SQ-2231 from Gamma, 14% below book cost for this circuit — attached."
              value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="field">
            <label>Supplier quote / supporting document <span className="muted">(optional — PDF, PNG or JPG, up to 4.5MB)</span></label>
            <input className="input" type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            {fileProblem && <div className="pill pill--stop" style={{ marginTop: 6 }}>{fileProblem}</div>}
          </div>

          {error && <p className="pill pill--stop" style={{ whiteSpace: 'pre-wrap' }}>{error}</p>}

          {detail.costVariations.length > 0 && (
            <div className="field" style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 10 }}>
              <label>Previous entries on this quote</label>
              {detail.costVariations.map((v) => (
                <div key={v.id} className="small" style={{ padding: '4px 0' }}>
                  <strong>{new Date(v.createdAt).toLocaleString('en-GB')}</strong> · {v.createdBy}
                  {v.inherited && <> <span className="pill pill--info tiny" title="Logged on a quote this one supersedes — the varied costs carried forward.">logged on {v.loggedOnNumber}</span></>}
                  {v.evidenceFilename && <> · <a href={costEvidenceUrl(v.id)} target="_blank" rel="noreferrer">{v.evidenceFilename}</a></>}
                  <div className="muted">{v.description}</div>
                  <div className="tiny muted">{v.lines.map((l) => `${l.lineName}: £${l.oldCost.toFixed(2)} → £${l.newCost.toFixed(2)}`).join(' · ')}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="slideover__foot">
          <span className="tiny muted">Internal only — cost never reaches partners or the customer page.</span>
          <span style={{ flex: 1 }} />
          <button className="btn btn--primary" disabled={busy || ticked.size === 0 || !description.trim() || !!fileProblem}
            onClick={async () => {
              setBusy(true); setError(null)
              try {
                await onLog({
                  description: description.trim(),
                  items: [...ticked].map((id) => {
                    const line = items.find((i) => i.id === id)!
                    const typed = Number(costs[id])
                    return { itemId: id, newCost: Number.isFinite(typed) && costs[id] !== undefined ? typed : (line.buyPrice ?? 0) }
                  }),
                  file,
                })
                onClose()
              } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
            }}>
            {busy ? 'Logging…' : `Log variation (${ticked.size} line${ticked.size === 1 ? '' : 's'})`}
          </button>
        </div>
      </div>
    </>
  )
}

// ================================================= accept on behalf (decision 94)

/** Decision 121: staff record a decline given by phone/email — reason from the
 *  shared picklist, optional detail, SILENT (the customer already told us). */
function DeclineOnBehalfSlideOver({ quoteNumber, onClose, onDecline }: {
  quoteNumber: string
  onClose: () => void
  onDecline: (payload: { reasonKey: string; detail?: string }) => Promise<void>
}) {
  const [reasonKey, setReasonKey] = useState('')
  const [detailText, setDetailText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover" role="dialog" aria-label="Record decline">
        <div className="slideover__head">
          <div>
            <strong>Record a decline</strong>
            <div className="small muted" style={{ marginTop: 3 }}>{quoteNumber}</div>
          </div>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          <p className="small" style={{ background: '#f6fafc', border: '1px solid var(--line)', borderRadius: 8, padding: '9px 12px' }}>
            For a decision the customer has already given you by phone or email. The quote
            becomes <strong>declined</strong>, the reason lands in the activity trail and the
            HubSpot deal moves to closed-lost. <strong>Nothing is emailed to the customer.</strong>{' '}
            Superseding the quote later revives the conversation as a new version.
          </p>
          <div className="field" style={{ marginTop: 12 }}>
            <label>Reason <span className="pill pill--warn tiny">required</span></label>
            <select className="select" style={{ width: '100%' }} value={reasonKey} disabled={busy}
              onChange={(e) => setReasonKey(e.target.value)}>
              <option value="">— pick a reason —</option>
              {DECLINE_REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label>Detail <span className="muted">(optional — what they said)</span></label>
            <textarea className="input" rows={3} style={{ width: '100%' }} value={detailText}
              disabled={busy} onChange={(e) => setDetailText(e.target.value)} />
          </div>
          {error && <p className="pill pill--stop" style={{ marginTop: 10 }}>{error}</p>}
        </div>
        <div className="slideover__foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn--danger" disabled={busy || !reasonKey}
            onClick={() => {
              setBusy(true); setError(null)
              onDecline({ reasonKey, detail: detailText.trim() || undefined })
                .then(onClose)
                .catch((e: Error) => { setError(e.message); setBusy(false) })
            }}>Record decline</button>
        </div>
      </div>
    </>
  )
}

/**
 * Decision 218: amend the acceptance — two doors, one mechanism.
 *   1. The customer: a single-use link that reopens their accepted page with
 *      the frozen selection loaded; they change it and confirm again.
 *   2. Staff on the customer's instruction: amend mode unlocks the preview
 *      (seeded from the record), then this panel records the change with the
 *      contact, an evidence note and the on-behalf statement — #94's idiom.
 * Same quote, same number, same won deal: the record is versioned, the deal's
 * line items and amount rebuild, the stage does not move.
 */
function AmendAcceptanceSlideOver({ detail, amending, selections, onClose, onStartAmend, onRecord }: {
  detail: QuoteDetail
  amending: boolean
  selections: SelectionMap
  onClose: () => void
  onStartAmend: () => void
  onRecord: (p: {
    recipientId?: string; contactName?: string; contactEmail?: string
    poReference?: string; note?: string; confirmed: boolean
  }) => Promise<AmendAcceptanceResult>
}) {
  const { quote, recipients, items, groups } = detail
  const a = detail.acceptance!
  const [link, setLink] = useState<string | null>(null)
  const [linkBusy, setLinkBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [recipientId, setRecipientId] = useState<string | 'other'>(
    recipients.find((r) => r.email.toLowerCase() === (a.accepterEmail ?? '').toLowerCase())?.id ?? recipients[0]?.id ?? 'other',
  )
  const [otherName, setOtherName] = useState('')
  const [otherEmail, setOtherEmail] = useState('')
  const [poRef, setPoRef] = useState('')
  const [note, setNote] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AmendAcceptanceResult | null>(null)

  const asLines: QuoteLine[] = items.map((it) => ({
    id: it.id, groupId: it.groupId, hsProductId: it.hsProductId, name: it.name,
    description: it.description, qty: it.qty, qtyEditable: it.qtyEditable,
    qtyMin: it.qtyMin, qtyMax: it.qtyMax, subjectToSurvey: it.subjectToSurvey,
    isAdhoc: it.isAdhoc, listPrice: it.listPrice, buyPrice: it.buyPrice ?? it.listPrice,
    sellPrice: it.sellPrice, recurring: it.recurring, billingFrequency: it.billingFrequency,
    termMonths: it.termMonths, optionality: it.optionality, preselected: it.preselected, sort: it.sort,
  }))
  const selGroups = groups.map((g) => ({
    id: g.id, kind: g.kind, parentGroupId: g.parentGroupId,
    optional: g.optional, preselected: g.preselected, choiceSet: g.choiceSet ?? null, sort: g.sort,
  }))
  const proposed = amending ? applySelections(asLines, selections, selGroups) : []
  const t = amending ? computeTotals(asLines, selections, selGroups) : null
  const frozenById = new Map(a.selections.map((f) => [f.itemId, f]))
  const proposedById = new Map(proposed.map((l) => [l.id, l]))
  const added = proposed.filter((l) => !frozenById.has(l.id))
  const removed = a.selections.filter((f) => !proposedById.has(f.itemId))
  const qtyChanged = proposed.filter((l) => frozenById.has(l.id) && frozenById.get(l.id)!.qty !== l.qty)
    .map((l) => ({ name: l.name, from: frozenById.get(l.id)!.qty, to: l.qty }))
  const changed = added.length + removed.length + qtyChanged.length > 0
  const contactOk = recipientId !== 'other'
    || (otherName.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(otherEmail.trim()))

  const copyAmendLink = () => {
    setLinkBusy(true); setError(null)
    // #147: the clipboard write must start inside the gesture.
    const urlPromise = mintAmendLink(quote.id).then((r) => r.url)
    const wrote = copyTextFromPromise(urlPromise)
    urlPromise.then(async (url) => { setLink(url); setCopied(await wrote) })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLinkBusy(false))
  }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover" role="dialog" aria-label="Amend acceptance">
        <div className="slideover__head">
          <div>
            <strong>Amend the acceptance</strong>
            <div className="small muted" style={{ marginTop: 3 }}>
              {quote.quoteNumber} · accepted {formatUKDate(a.acceptedAt)}{a.accepterName ? ` by ${a.accepterName}` : ''} · version {a.version}.
              Same quote, same deal, no new version — for a forgotten option or a changed quantity. Prices never move; a new price or line is a Supersede.
            </div>
          </div>
          <button className="btn btn--sm" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          {result ? (
            <div className="field">
              <div className="banner">
                ✓ <span><strong>Acceptance amended — now version {result.version}</strong> on the instruction of {result.contactName}: {formatGBPCompact(result.contractValueSell)} contract value across {result.lines} line(s).
                {result.change.added.length > 0 && <> Added: {result.change.added.join(', ')}.</>}
                {result.change.removed.length > 0 && <> Removed: {result.change.removed.join(', ')}.</>}
                {result.change.qtyChanged.length > 0 && <> Quantities: {result.change.qtyChanged.join('; ')}.</>}
                {' '}The deal's line items and amount rebuild on the next sync; the stage does not move.</span>
              </div>
              {/* Decision 247: the pack records this amendment left out of step. */}
              {result.warnings.length > 0 && (
                <div className="bbanner" data-testid="amend-pack-after" style={{ marginTop: 8, borderRadius: 6, border: '1px solid #f0dcb4', alignItems: 'flex-start' }}>
                  ⚠ <span>
                    <strong>Provisioning pack to reconcile by hand in HubSpot.</strong> {result.warnings.map((w) => w.label).join('; ')}.
                    {result.affectedPackRecords.length > 0
                      ? <> Records whose line changed in this amendment: <ul style={{ margin: '4px 0 0 16px' }}>{result.affectedPackRecords.map((r) => <li key={r.hsObjectId}>{r.kind} {r.hsObjectId} — {r.name}{r.qty != null ? ` × ${r.qty}` : ''}</li>)}</ul></>
                      : <> None of the changed lines carried a pack record — check whether the added lines need assets of their own.</>}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Decision 247: a provisioning pack no longer blocks an amendment —
                  it warns, and names the records, because they are fixed by hand. */}
              {a.amendWarnings.length > 0 && (
                <div className="bbanner" data-testid="amend-pack-warning" style={{ marginBottom: 12, borderRadius: 6, border: '1px solid #f0dcb4', alignItems: 'flex-start' }}>
                  ⚠ <span>
                    <strong>Before you amend:</strong> {a.amendWarnings.map((w) => w.label).join('; ')}. The records:
                    <ul style={{ margin: '4px 0 0 16px' }}>
                      {a.amendWarnings.flatMap((w) => w.records).map((r) => <li key={r.hsObjectId}>{r.kind} {r.hsObjectId} — {r.name}{r.qty != null ? ` × ${r.qty}` : ''}</li>)}
                    </ul>
                    After the amendment lands, the ones whose line was removed or re-quantified are named here and in the owner email.
                  </span>
                </div>
              )}
              <h3 style={{ margin: '0 0 6px' }}>1 · Let the customer change it</h3>
              <p className="small muted" style={{ margin: '0 0 8px' }}>
                A single-use link that reopens their accepted page with the accepted selection loaded. They change options or quantities, confirm again, and you are notified. Nothing is emailed by the portal — paste the link into your own message.
              </p>
              <div className="rowflex" style={{ gap: 6 }}>
                <button className="btn btn--sm btn--primary" disabled={linkBusy} onClick={copyAmendLink} data-testid="copy-amend-link">
                  {linkBusy ? 'Minting…' : copied ? '✓ Amend link copied' : 'Copy amend link'}
                </button>
                <span className="tiny muted">for {recipients.find((r) => r.email.toLowerCase() === (a.accepterEmail ?? '').toLowerCase())?.name ?? recipients[0]?.name ?? recipients[0]?.email ?? 'the recipient'}</span>
              </div>
              {link && (
                <div className="rowflex" style={{ marginTop: 6, gap: 6 }}>
                  <input className="input" style={{ flex: 1, fontSize: 11.5 }} readOnly value={link} onFocus={(e) => e.target.select()} />
                  <button className="btn btn--sm" onClick={() => { void navigator.clipboard.writeText(link).then(() => setCopied(true)).catch(() => {}) }}>Copy</button>
                </div>
              )}

              <h3 style={{ margin: '18px 0 6px' }}>2 · Record it on the customer's instruction</h3>
              {!amending ? (
                <>
                  <p className="small muted" style={{ margin: '0 0 8px' }}>
                    The customer told you by phone or email. Unlock the customer preview, set the selection they now want, then come back here to record it.
                  </p>
                  <button className="btn btn--sm" onClick={onStartAmend} data-testid="start-amend">Set the amended selection in the preview</button>
                </>
              ) : (
                <>
                  <div className="banner" style={{ marginBottom: 10 }} data-testid="amend-diff">
                    {changed
                      ? <span>
                          <strong>Changes vs version {a.version}:</strong>
                          {added.length > 0 && <> added {added.map((l) => `${l.name} × ${l.qty}`).join(', ')}.</>}
                          {removed.length > 0 && <> removed {removed.map((f) => `${f.name} × ${f.qty}`).join(', ')}.</>}
                          {qtyChanged.length > 0 && <> quantity {qtyChanged.map((c) => `${c.name} ${c.from} → ${c.to}`).join('; ')}.</>}
                          {t && <> Now {formatGBPCompact(t.oneOffSell)} one-off · {formatGBPCompact(t.annualSell)}/yr · <strong>{formatGBPCompact(t.contractValueSell)} contract value</strong>.</>}
                        </span>
                      : <span>No change yet — adjust options or quantities in the preview behind this panel.</span>}
                  </div>

                  <div className="field">
                    <label>Who at the customer gave the instruction?</label>
                    {recipients.map((r) => (
                      <label key={r.id} className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0' }}>
                        <input type="radio" name="amend-contact" checked={recipientId === r.id} onChange={() => setRecipientId(r.id)} />
                        {r.name || r.email} <span className="tiny muted">· {r.email}</span>
                      </label>
                    ))}
                    <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0' }}>
                      <input type="radio" name="amend-contact" checked={recipientId === 'other'} onChange={() => setRecipientId('other')} />
                      Someone else…
                    </label>
                    {recipientId === 'other' && (
                      <div className="rowflex" style={{ marginTop: 6 }}>
                        <input className="input" style={{ width: 180 }} placeholder="Name" value={otherName} onChange={(e) => setOtherName(e.target.value)} />
                        <input className="input" style={{ flex: 1 }} placeholder="Email" value={otherEmail} onChange={(e) => setOtherEmail(e.target.value)} />
                      </div>
                    )}
                  </div>
                  <div className="field">
                    <label>New PO reference <span className="muted">(optional — blank keeps {a.poReference ? `PO ${a.poReference}` : a.poToFollow ? '"PO to follow"' : 'no PO'})</span></label>
                    <input className="input" style={{ width: '100%' }} placeholder="e.g. PO-4491-A" value={poRef} onChange={(e) => setPoRef(e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Evidence note <span className="muted">(optional — frozen into the amended record)</span></label>
                    <textarea className="input" rows={2} style={{ width: '100%' }}
                      placeholder="e.g. Bursar emailed 10/09 — they also want the UPS option"
                      value={note} onChange={(e) => setNote(e.target.value)} />
                  </div>
                  <div className="field" style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 10 }}>
                    <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
                      <span>I confirm the named contact instructed this amendment and confirmed they are authorised to amend the accepted quotation on behalf of {detail.customer?.name ?? 'the purchasing organisation'}. <span className="tiny muted">The exact statement — naming them and you — freezes into the amended record; the version it replaces is kept.</span></span>
                    </label>
                  </div>
                </>
              )}
              {error && <p className="pill pill--stop" style={{ whiteSpace: 'pre-wrap' }}>{error}</p>}
            </>
          )}
        </div>
        <div className="slideover__foot">
          {result
            ? <>
                <span className="pill pill--ok">Amended</span>
                <span style={{ flex: 1 }} />
                <button className="btn btn--primary" onClick={onClose}>Done</button>
              </>
            : <>
                <span className="tiny muted">The original acceptance date stands · the deal stage does not move</span>
                <span style={{ flex: 1 }} />
                {amending && (
                  <button className="btn btn--primary" disabled={busy || !confirmed || !contactOk || !changed} data-testid="record-amendment"
                    onClick={async () => {
                      setBusy(true); setError(null)
                      try {
                        setResult(await onRecord({
                          ...(recipientId === 'other'
                            ? { contactName: otherName.trim(), contactEmail: otherEmail.trim() }
                            : { recipientId }),
                          poReference: poRef.trim() || undefined,
                          note: note.trim() || undefined,
                          confirmed,
                        }))
                      } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
                    }}>
                    {busy ? 'Recording…' : 'Record amendment'}
                  </button>
                )}
              </>}
        </div>
      </div>
    </>
  )
}

function AcceptOnBehalfSlideOver({ detail, selections, onClose, onAccept }: {
  detail: QuoteDetail
  selections: SelectionMap
  onClose: () => void
  onAccept: (p: {
    recipientId?: string; contactName?: string; contactEmail?: string
    poReference?: string; poToFollow?: boolean; note?: string
    sendConfirmation?: boolean; confirmed: boolean
  }) => Promise<AcceptOnBehalfResult>
}) {
  const { quote, recipients, items, groups } = detail
  const [recipientId, setRecipientId] = useState<string | 'other'>(recipients[0]?.id ?? 'other')
  const [otherName, setOtherName] = useState('')
  const [otherEmail, setOtherEmail] = useState('')
  const [poRef, setPoRef] = useState('')
  const [poToFollow, setPoToFollow] = useState(false)
  const [note, setNote] = useState('')
  const [sendConfirmation, setSendConfirmation] = useState(true)   // decision 94: default ON
  const [confirmed, setConfirmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AcceptOnBehalfResult | null>(null)

  // The acceptance freezes the selections currently set in Preview — show the
  // value that will be recorded, computed exactly as the Preview does.
  const asLines: QuoteLine[] = items.map((it) => ({
    id: it.id, groupId: it.groupId, hsProductId: it.hsProductId, name: it.name,
    description: it.description, qty: it.qty, qtyEditable: it.qtyEditable,
    qtyMin: it.qtyMin, qtyMax: it.qtyMax, subjectToSurvey: it.subjectToSurvey,
    isAdhoc: it.isAdhoc, listPrice: it.listPrice, buyPrice: it.buyPrice ?? it.listPrice,
    sellPrice: it.sellPrice, recurring: it.recurring, billingFrequency: it.billingFrequency,
    termMonths: it.termMonths, optionality: it.optionality, preselected: it.preselected, sort: it.sort,
  }))
  // ⚠️ 117 sharp edge: SelectionGroup construction sites must carry choiceSet
  // + sort or set members silently resolve as plain sections (overcounting).
  const t = computeTotals(asLines, selections, groups.map((g) => ({
    id: g.id, kind: g.kind, parentGroupId: g.parentGroupId,
    optional: g.optional, preselected: g.preselected,
    choiceSet: g.choiceSet ?? null, sort: g.sort,
  })))

  const contactOk = recipientId !== 'other'
    || (otherName.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(otherEmail.trim()))

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover" role="dialog" aria-label="Accept on behalf">
        <div className="slideover__head">
          <div>
            <strong>Accept on behalf of the customer</strong>
            <div className="small muted" style={{ marginTop: 3 }}>
              Records an acceptance the customer gave by phone, email or PO.
              Identical record, ledger and HubSpot effect to the customer clicking accept.
            </div>
          </div>
          <button className="btn btn--sm" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          {!result && (
            <>
              <div className="banner" style={{ marginBottom: 10 }}>
                ℹ <span>Freezes the selections <strong>currently set in Preview</strong>: {formatGBPCompact(t.oneOffSell)} one-off · {formatGBPCompact(t.annualSell)}/yr · <strong>{formatGBPCompact(t.contractValueSell)} contract value</strong>. Adjust options/quantities in Preview first if the customer chose differently.</span>
              </div>

              <div className="field">
                <label>Who at the customer gave the instruction?</label>
                {recipients.map((r) => (
                  <label key={r.id} className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0' }}>
                    <input type="radio" name="aob-contact" checked={recipientId === r.id} onChange={() => setRecipientId(r.id)} />
                    {r.name || r.email} <span className="tiny muted">· {r.email}</span>
                  </label>
                ))}
                <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0' }}>
                  <input type="radio" name="aob-contact" checked={recipientId === 'other'} onChange={() => setRecipientId('other')} />
                  Someone else…
                </label>
                {recipientId === 'other' && (
                  <div className="rowflex" style={{ marginTop: 6 }}>
                    <input className="input" style={{ width: 180 }} placeholder="Name" value={otherName} onChange={(e) => setOtherName(e.target.value)} />
                    <input className="input" style={{ flex: 1 }} placeholder="Email" value={otherEmail} onChange={(e) => setOtherEmail(e.target.value)} />
                  </div>
                )}
              </div>

              <div className="rowflex" style={{ alignItems: 'flex-end' }}>
                <div className="field" style={{ flex: 1 }}>
                  <label>PO reference <span className="muted">(optional)</span></label>
                  <input className="input" style={{ width: '100%' }} placeholder="e.g. PO-4491" value={poRef}
                    onChange={(e) => { setPoRef(e.target.value); if (e.target.value.trim()) setPoToFollow(false) }} />
                </div>
                <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 10 }}>
                  <input type="checkbox" checked={poToFollow} disabled={poRef.trim() !== ''}
                    onChange={(e) => setPoToFollow(e.target.checked)} /> PO to follow
                </label>
              </div>

              <div className="field">
                <label>Evidence note <span className="muted">(optional — frozen into the acceptance record)</span></label>
                <textarea className="input" rows={2} style={{ width: '100%' }}
                  placeholder="e.g. Accepted by email from the bursar 06/08; PO attached to the deal"
                  value={note} onChange={(e) => setNote(e.target.value)} />
              </div>

              <div className="field">
                <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={sendConfirmation} onChange={(e) => setSendConfirmation(e.target.checked)} />
                  Send the acceptance confirmation email to the customer contact
                </label>
                <div className="hint">Their filed record, with a personal link to the accepted page. Untick if the paperwork already happened elsewhere.</div>
              </div>

              <div className="field" style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 10 }}>
                <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
                  <span>I confirm the named contact accepted this quotation and confirmed they are authorised to do so on behalf of {detail.customer?.name ?? 'the purchasing organisation'}. <span className="tiny muted">The exact statement — naming them and you — freezes into the acceptance record.</span></span>
                </label>
              </div>

              {error && <p className="pill pill--stop" style={{ whiteSpace: 'pre-wrap' }}>{error}</p>}
            </>
          )}

          {result && (
            <div className="field">
              <div className="banner" style={{ marginBottom: 8 }}>
                ✓ <span><strong>Accepted on behalf of {result.contactName}</strong> — {formatGBPCompact(result.contractValueSell)} contract value across {result.lines} line(s). {result.confirmation ? (result.confirmation.sent ? 'Confirmation emailed.' : 'Confirmation queued (mail not configured).') : 'No confirmation email sent.'}</span>
              </div>
              {result.link && (
                <>
                  <label>The contact's link to the accepted page (read-only, lives on)</label>
                  <div className="rowflex">
                    <input className="input" readOnly style={{ flex: 1, fontSize: 11.5 }} value={result.link} onFocus={(e) => e.target.select()} />
                    <button className="btn btn--sm" onClick={() => navigator.clipboard.writeText(result.link!).catch(() => {})}>Copy</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="slideover__foot">
          {result
            ? <>
                <span className="pill pill--ok">Accepted</span>
                <span style={{ flex: 1 }} />
                <button className="btn btn--primary" onClick={onClose}>Done</button>
              </>
            : <>
                <span className="tiny muted">Quote {quote.quoteNumber} · acceptance is subject to survey / technical due diligence</span>
                <span style={{ flex: 1 }} />
                <button className="btn btn--primary" disabled={busy || !confirmed || !contactOk}
                  onClick={async () => {
                    setBusy(true); setError(null)
                    try {
                      setResult(await onAccept({
                        ...(recipientId !== 'other' ? { recipientId } : { contactName: otherName.trim(), contactEmail: otherEmail.trim() }),
                        poReference: poRef.trim() || undefined,
                        poToFollow: poToFollow || undefined,
                        note: note.trim() || undefined,
                        sendConfirmation,
                        confirmed: true,
                      }))
                    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
                  }}>
                  {busy ? 'Recording…' : 'Record acceptance'}
                </button>
              </>}
        </div>
      </div>
    </>
  )
}

// ==================================================== split deals (decision 171)

/**
 * The split screen: every section with accepted lines gets a site dropdown
 * (pre-filled from the builder's own section→site tags), shared sections go
 * whole to one nominated deal, and the preview shows each deal's real lines
 * and value before an explicit confirm. Once split, the same panel shows the
 * frozen record read-only.
 */
function SplitDealsSlideOver({ detail, portalId, onClose, onDone }: {
  detail: QuoteDetail
  portalId: string | null
  onClose: () => void
  onDone: () => Promise<void>
}) {
  const { quote, splits } = detail
  const alreadySplit = quote.splitAt != null
  // Decision 195: the mode is chosen here, per split. Default = value (171).
  // Decision 216: a single-site project can only be split by PHASE — start there.
  const [splitMode, setSplitMode] = useState<SplitMode>(detail.sites.length > 1 ? 'value' : 'phases')
  const sitesOnly = splitMode === 'sites'
  const byPhase = splitMode === 'phases'
  const recordMode: SplitMode = quote.splitMode ?? 'value'
  const [phaseDates, setPhaseDates] = useState<Record<string, string | null>>({})
  const [plan, setPlan] = useState<SplitPlan | null>(null)
  const [mapping, setMapping] = useState<SplitMappingEntry[] | null>(null)
  const [sharedSiteId, setSharedSiteId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [result, setResult] = useState<SplitCommitResult | null>(null)

  const propose = useCallback(async (m: SplitMappingEntry[] | null, shared: string | null, mode: SplitMode, dates?: Record<string, string | null>) => {
    setBusy(true)
    setError(null)
    try {
      const res = await splitQuoteDeals(quote.id, {
        ...(m ? { mapping: m } : {}), ...(shared ? { sharedSiteId: shared } : {}), mode,
        ...(mode === 'phases' && dates ? { phaseDates: dates } : {}),
      }) as SplitPlan
      setPlan(res)
      setMapping(res.mapping)
      setSharedSiteId(res.sharedSiteId)
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }, [quote.id])

  useEffect(() => {
    if (!alreadySplit) void propose(null, null, detail.sites.length > 1 ? 'value' : 'phases')
  }, [alreadySplit, propose, detail.sites.length])

  const setMode = (mode: SplitMode) => {
    setSplitMode(mode)
    setConfirmed(false)
    // A site mapping is not a phase mapping (and vice versa) — the server re-seeds the default.
    setMapping(null)
    void propose(null, sharedSiteId, mode, phaseDates)
  }
  // Decision 216: the phase this section goes to; phase N+1 = "a new phase".
  const setPhase = (groupId: string, target: string) => {
    const next = (mapping ?? []).map((m) => (m.groupId === groupId ? { ...m, target } : m))
    if (!next.some((m) => m.groupId === groupId)) next.push({ groupId, target })
    setMapping(next)
    setConfirmed(false)
    void propose(next, null, 'phases', phaseDates)
  }
  const setPhaseDate = (key: string, value: string) => {
    const next = { ...phaseDates, [key]: value || null }
    setPhaseDates(next)
    setConfirmed(false)
    void propose(mapping, null, 'phases', next)
  }

  const setTarget = (groupId: string, target: string) => {
    const next = (mapping ?? []).map((m) => (m.groupId === groupId ? { ...m, target } : m))
    if (!next.some((m) => m.groupId === groupId)) next.push({ groupId, target })
    setMapping(next)
    setConfirmed(false)
    void propose(next, sharedSiteId, splitMode)
  }
  const phaseKeysInUse = [...new Set((mapping ?? []).map((m) => m.target).filter((t) => /^P\d+$/.test(t)))]
    .sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)))
  const phaseOptions = [...phaseKeysInUse, `P${phaseKeysInUse.length + 1}`]

  const hasShared = (mapping ?? []).some((m) => m.target === 'shared')
  const mappedSiteIds = [...new Set((mapping ?? []).filter((m) => m.target !== 'shared').map((m) => m.target))]

  const dealLink = (dealId: string) => hubspotDealUrl(portalId, dealId)

  // ---- the frozen record (already split) ----------------------------------
  // ⚠️ not while the result screen is up: the commit's own reload flips
  // `splitAt` mid-view, and the "Split complete" banner must survive it.
  if (alreadySplit && !result) {
    return (
      <>
        <div className="scrim" onClick={onClose} />
        <div className="slideover" role="dialog" aria-label="Split site deals">
          <div className="slideover__head">
            <div>
              <strong>{recordMode === 'sites' ? `${splits.length} site deal${splits.length === 1 ? '' : 's'} for assets` : recordMode === 'phases' ? `Split into ${splits.length} phase${splits.length === 1 ? '' : 's'}` : `Split into ${splits.length} site deal${splits.length === 1 ? '' : 's'}`}</strong>
              <div className="small muted" style={{ marginTop: 3 }}>
                {quote.splitAt ? `${recordMode === 'sites' ? 'Created' : 'Split'} ${formatUKDate(quote.splitAt)}. ` : ''}
                {recordMode === 'sites'
                  ? 'The master deal keeps the value, line items and renewal. These deals carry £0 — each exists so its site\'s assets sit on its own deal.'
                  : recordMode === 'phases'
                    ? 'The parent deal is a zeroed shell — each phase deal carries its lines and value, booking on its own expected date.'
                    : 'The parent deal is a zeroed shell — these deals carry the value to bill and renew, each on its own contract end date.'}
              </div>
            </div>
            <button className="btn btn--sm" onClick={onClose}>✕</button>
          </div>
          <div className="slideover__body">
            <table>
              <thead><tr><th>Ref</th><th>{recordMode === 'phases' ? 'Phase' : 'Site'}</th>{recordMode === 'phases' && <th>Expected booking</th>}<th style={{ textAlign: 'right' }}>Amount</th><th /></tr></thead>
              <tbody>
                {splits.map((s) => (
                  <tr key={s.suffix}>
                    <td className="tiny">{quote.quoteNumber}-{s.suffix}{s.carriesShared ? <span className="pill pill--info" style={{ marginLeft: 6 }}>{recordMode === 'phases' ? 'other lines' : '+ shared'}</span> : null}</td>
                    <td>{s.siteName}{recordMode !== 'phases' && s.companySource === 'trust' ? <span className="tiny muted"> · trust company</span> : null}</td>
                    {recordMode === 'phases' && <td className="small">{s.expectedBookingDate ? formatUKDate(s.expectedBookingDate) : <span className="muted">—</span>}</td>}
                    <td style={{ textAlign: 'right' }}>{formatGBPCompact(s.amount)}</td>
                    <td>{dealLink(s.hsDealId)
                      ? <a className="btn btn--sm" href={dealLink(s.hsDealId)!} target="_blank" rel="noreferrer">Open deal →</a>
                      : <span className="tiny muted">deal {s.hsDealId}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {quote.hsDealId && dealLink(quote.hsDealId) && (
              <p className="small muted" style={{ marginTop: 10 }}>
                {recordMode === 'sites' ? 'Master deal' : 'Parent shell'}: <a href={dealLink(quote.hsDealId)!} target="_blank" rel="noreferrer">open in HubSpot →</a>
                {recordMode === 'sites' ? ' (the whole order — value, line items, renewal)' : ' (amount £0 — history and the acceptance record live there)'}
              </p>
            )}
          </div>
          <div className="slideover__foot">
            <span className="tiny muted">A split is not reversible from the portal.</span>
            <span style={{ flex: 1 }} />
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </>
    )
  }

  // ---- the planner ---------------------------------------------------------
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover" role="dialog" aria-label="Split deal">
        <div className="slideover__head">
          <div>
            <strong>{byPhase ? 'Split into phases' : 'Split into one deal per site'}</strong>
            <div className="small muted" style={{ marginTop: 3 }}>
              {sitesOnly
                ? 'The master deal keeps everything — value, line items, stage, renewal. Each site gets a £0 deal at the same stage so its assets sit on its own record.'
                : byPhase
                  ? 'One deal per phase — each carries its sections\' accepted lines at frozen prices, at the parent deal\'s current stage, with its own expected booking date. The parent becomes a zeroed shell.'
                  : 'Each site deal carries its accepted lines at frozen prices, at the parent deal\'s current stage. The parent becomes a zeroed shell — the site deals then bill and renew independently.'}
            </div>
          </div>
          <button className="btn btn--sm" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          {!plan && !error && <p className="muted">Working out the plan…</p>}
          {error && <p className="pill pill--stop" style={{ whiteSpace: 'pre-wrap' }}>{error}</p>}

          {plan && !result && (
            <>
              <div className="field">
                <label>How is it split?</label>
                <div className="toggle" role="radiogroup" aria-label="Split mode">
                  {detail.sites.length > 1 && <button aria-pressed={splitMode === 'value'} disabled={busy} onClick={() => setMode('value')}>By site — values move</button>}
                  {detail.sites.length > 1 && <button aria-pressed={sitesOnly} disabled={busy} onClick={() => setMode('sites')}>By site — values stay</button>}
                  <button aria-pressed={byPhase} disabled={busy} onClick={() => setMode('phases')} data-testid="split-mode-phases">By phase</button>
                </div>
                <div className="hint">
                  {sitesOnly
                    ? 'Site deals for assets only: £0, no line items, at the master\'s stage. The master keeps billing and renewing as one. Use this when the order is one contract but the kit belongs to each site.'
                    : byPhase
                      ? 'One deal per phase, each with its own expected booking date; the parent becomes a £0 shell. Use this for a large project delivered in stages — each section is a phase by default; give two sections the same phase to merge them.'
                      : 'Each site deal carries its lines and value; the parent becomes a £0 shell. Use this when the sites bill and renew on their own dates.'}
                </div>
              </div>

              {byPhase && (
                <div className="field">
                  <label>Sections → phases</label>
                  {plan.sections.map((sec) => (
                    <div key={sec.id} className="rowflex" style={{ alignItems: 'center', padding: '3px 0' }}>
                      <span className="small" style={{ flex: 1 }}>
                        {sec.name} <span className="tiny muted">· {sec.lineCount} line{sec.lineCount === 1 ? '' : 's'} · {formatGBPCompact(sec.amount)}</span>
                      </span>
                      <select className="input" style={{ width: 160 }} data-testid={`phase-for-${sec.id}`}
                        value={(mapping ?? []).find((m) => m.groupId === sec.id)?.target ?? 'P1'}
                        onChange={(e) => setPhase(sec.id, e.target.value)}>
                        {phaseOptions.map((k, i) => <option key={k} value={k}>{i === phaseOptions.length - 1 ? `Phase ${k.slice(1)} (new)` : `Phase ${k.slice(1)}`}</option>)}
                      </select>
                    </div>
                  ))}
                  <div className="hint">One phase per section to start with, in section order. Pick the same phase for two sections to merge them; the deals are renumbered to stay contiguous.</div>
                </div>
              )}

              {byPhase && plan.valid && (plan.phases ?? []).length > 0 && (
                <div className="field">
                  <label>Expected booking date per phase</label>
                  {(plan.phases ?? []).map((ph) => (
                    <div key={ph.key} className="rowflex" style={{ alignItems: 'center', padding: '3px 0' }}>
                      <span className="small" style={{ flex: 1 }}>
                        <strong>{ph.name}</strong>{ph.isRemainder && <span className="tiny muted"> · lines outside any section</span>}
                      </span>
                      <input type="date" className="input" style={{ width: 170 }} data-testid={`phase-date-${ph.key}`}
                        value={ph.expectedBookingDate ?? ''} onChange={(e) => setPhaseDate(ph.key, e.target.value)} />
                    </div>
                  ))}
                  <div className="hint">Defaults to the master's expected booking date{plan.masterBookingDate ? ` (${formatUKDate(plan.masterBookingDate)})` : ''} — a later phase usually books later. Written to each phase deal's Expected Booking Date.</div>
                </div>
              )}

              {!byPhase && <div className="field">
                <label>Sections → sites</label>
                {plan.sections.map((sec) => (
                  <div key={sec.id} className="rowflex" style={{ alignItems: 'center', padding: '3px 0' }}>
                    <span className="small" style={{ flex: 1 }}>
                      {sec.name} <span className="tiny muted">· {sec.lineCount} line{sec.lineCount === 1 ? '' : 's'} · {formatGBPCompact(sec.amount)}</span>
                    </span>
                    <select className="input" style={{ width: 220 }}
                      value={(mapping ?? []).find((m) => m.groupId === sec.id)?.target ?? 'shared'}
                      onChange={(e) => setTarget(sec.id, e.target.value)}>
                      {plan.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      <option value="shared">Shared (whole quote)</option>
                    </select>
                  </div>
                ))}
                <div className="hint">Pre-filled from the builder's own section→site tags — check every row; nothing is inferred silently.</div>
              </div>}

              {!byPhase && hasShared && mappedSiteIds.length > 0 && (
                <div className="field">
                  <label>Shared sections go whole to</label>
                  <select className="input" style={{ width: 260 }}
                    value={sharedSiteId ?? mappedSiteIds[0]}
                    onChange={(e) => { setSharedSiteId(e.target.value); setConfirmed(false); void propose(mapping, e.target.value, splitMode) }}>
                    {mappedSiteIds.map((id) => (
                      <option key={id} value={id}>{plan.sites.find((s) => s.id === id)?.name ?? id}</option>
                    ))}
                  </select>
                  <div className="hint">Setup charges and trust-wide lines are never pro-rated — they ride one deal whole (the frozen-value rule).</div>
                </div>
              )}

              {!plan.valid && plan.error && (
                <p className="pill pill--warn" style={{ whiteSpace: 'pre-wrap' }}>{plan.error}</p>
              )}

              {plan.valid && (
                <div className="field">
                  <label>The deals this creates</label>
                  {plan.deals.map((d) => (
                    <div key={d.suffix} className="card" style={{ padding: 10, marginBottom: 8 }}>
                      <div className="rowflex" style={{ alignItems: 'baseline' }}>
                        <strong className="small">{d.quoteRef}</strong>
                        <span className="small" style={{ flex: 1 }}>{d.dealName}</span>
                        <strong>{formatGBPCompact(d.amount)}</strong>
                        {sitesOnly && <span className="tiny muted" style={{ marginLeft: 6 }}>({formatGBPCompact(d.sliceAmount)} stays on the master)</span>}
                      </div>
                      <div className="tiny muted" style={{ margin: '2px 0 6px' }}>
                        {byPhase
                          ? <>{d.expectedBookingDate ? `expected booking ${formatUKDate(d.expectedBookingDate)}` : 'no expected booking date'} · the master's company{d.carriesShared ? ' · lines outside any section' : ''}</>
                          : <>{d.company.source === 'site' ? '✓ own HubSpot company (by URN)' : '⚠ associates to the trust’s company'}{d.carriesShared ? ' · carries the shared sections' : ''}</>}
                      </div>
                      <table>
                        <tbody>
                          {d.lines.map((l, i) => (
                            <tr key={i}>
                              <td className="tiny">{l.name}</td>
                              <td className="tiny" style={{ textAlign: 'right', width: 40 }}>×{l.qty}</td>
                              <td className="tiny" style={{ textAlign: 'right', width: 90 }}>{sitesOnly ? <span className="muted" title="stays on the master deal">—</span> : formatGBPCompact(l.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                  <div className="small" style={{ marginTop: 4 }}>
                    {sitesOnly
                      ? <>Site deals: <strong>£0 each</strong>{plan.parent.amount != null && ` · master deal stays at ${formatGBPCompact(plan.parent.amount)}`}</>
                      : <>Total across {byPhase ? 'phase' : 'site'} deals: <strong>{formatGBPCompact(plan.totalAmount)}</strong>{plan.parent.amount != null && ` · parent deal today: ${formatGBPCompact(plan.parent.amount)} → £0 shell`}</>}
                  </div>
                </div>
              )}

              {plan.warnings.length > 0 && (
                <div className="field">
                  {plan.warnings.map((w, i) => <p key={i} className="pill pill--warn" style={{ display: 'block', whiteSpace: 'pre-wrap', marginBottom: 4 }}>{w}</p>)}
                </div>
              )}

              {plan.valid && (
                <div className="field" style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 10 }}>
                  <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
                    <span>{sitesOnly
                      ? <>Create these {plan.deals.length} £0 site deals exactly as previewed and leave the master deal as it is. </>
                      : <>Create these {plan.deals.length} {byPhase ? 'phase ' : ''}deals exactly as previewed, zero the parent deal and move it to the split shell stage. </>}
                      <span className="tiny muted">Not reversible from the portal — undoing a split is a HubSpot job by hand{sitesOnly ? ', and the mode cannot be changed afterwards' : ''}.</span></span>
                  </label>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="field">
              <div className="banner" style={{ marginBottom: 8 }}>
                ✓ <span><strong>{result.mode === 'sites' ? 'Site deals created' : 'Split complete'}</strong> — {result.deals.length} {result.mode === 'phases' ? 'phase' : 'site'} deal{result.deals.length === 1 ? '' : 's'} created; {result.mode === 'sites' ? 'the master deal is unchanged and keeps the value.' : 'the parent deal is now a zeroed shell.'}</span>
              </div>
              <table>
                <tbody>
                  {result.deals.map((d) => (
                    <tr key={d.suffix}>
                      <td className="tiny">{quote.quoteNumber}-{d.suffix}</td>
                      <td className="small">{d.siteName}</td>
                      <td style={{ textAlign: 'right' }}>{formatGBPCompact(d.amount)}</td>
                      <td>{dealLink(d.dealId)
                        ? <a className="btn btn--sm" href={dealLink(d.dealId)!} target="_blank" rel="noreferrer">Open deal →</a>
                        : <span className="tiny muted">deal {d.dealId}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.warnings.length > 0 && result.warnings.map((w, i) => (
                <p key={i} className="pill pill--warn" style={{ display: 'block', marginTop: 6 }}>{w}</p>
              ))}
            </div>
          )}
        </div>
        <div className="slideover__foot">
          {result
            ? <>
                <span className="pill pill--ok">{result.mode === 'sites' ? 'accepted · site deals' : result.mode === 'phases' ? 'accepted · phases' : 'accepted & split'}</span>
                <span style={{ flex: 1 }} />
                <button className="btn btn--primary" onClick={onClose}>Done</button>
              </>
            : <>
                <span className="tiny muted">Quote {quote.quoteNumber}{plan && !plan.splitStageSet && !sitesOnly ? ' · set "Split: shell deal stage id" on Admin → Organisation first' : ''}</span>
                <span style={{ flex: 1 }} />
                <button className="btn btn--primary"
                  disabled={busy || !plan?.valid || !confirmed || (!plan?.splitStageSet && !sitesOnly)}
                  onClick={async () => {
                    setBusy(true); setError(null)
                    try {
                      const res = await splitQuoteDeals(quote.id, {
                        mapping: mapping ?? undefined, sharedSiteId, confirmed: true, mode: splitMode,
                        ...(byPhase ? { phaseDates } : {}),
                      }) as SplitCommitResult
                      setResult(res)
                      await onDone()
                    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
                  }}>
                  {busy ? 'Working…' : `Create ${plan?.deals.length ?? 0} ${sitesOnly ? '£0 ' : ''}${byPhase ? 'phase' : 'site'} deals`}
                </button>
              </>}
        </div>
      </div>
    </>
  )
}

// ======================================================== acceptance panel

/**
 * Decision 191: the frozen acceptance record, read-only. Who accepted, when,
 * on what paper, and — the reason it exists — every offered line marked taken
 * (at the accepted quantity and customer price) or left. The total bar and
 * the HubSpot deal already agreed; this is the line-level answer to "what
 * did they actually say yes to". Sell figures only, like the record itself.
 */
function AcceptanceSlideOver({ detail, onClose }: { detail: QuoteDetail; onClose: () => void }) {
  const { quote, groups, items, sites } = detail
  const a = detail.acceptance!
  const byItem = new Map(a.selections.map((f) => [f.itemId, f]))
  const siteById = new Map(sites.map((x) => [x.id, x]))
  const multi = sites.length > 1
  const topLevel = groups.filter((g) => !g.parentGroupId)
  const sectionOf = (g: typeof groups[number]) => (g.kind === 'choose_one' && g.parentGroupId) ? g.parentGroupId : g.id
  const linesIn = (sectionId: string) => items.filter((it) => {
    if (!it.groupId) return false
    const g = groups.find((x) => x.id === it.groupId)
    return g ? sectionOf(g) === sectionId : false
  })
  const taken = items.filter((it) => byItem.has(it.id))
  const left = items.filter((it) => !byItem.has(it.id))
  const perYear = (f: { unitPrice: number; billingFrequency: BillingFrequency }) => annualised(f.unitPrice, f)
  const t = detail.totals

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover" role="dialog" aria-label="What was accepted">
        <div className="slideover__head">
          <div>
            <h2>What the customer accepted</h2>
            <p className="tiny muted" style={{ margin: '2px 0 0' }}>
              Quote {quote.quoteNumber} · the frozen acceptance record — the HubSpot deal and the total bar are built from this.
            </p>
          </div>
          <button className="btn btn--sm btn--ghost" onClick={onClose}>Close</button>
        </div>
        <div className="slideover__body">
          <div className="acc__facts" data-testid="acceptance-facts">
            <div><span className="tiny muted">Accepted</span><div>{formatUKDate(a.acceptedAt)}</div></div>
            <div><span className="tiny muted">By</span><div>{a.accepterName ?? '—'}{a.accepterEmail ? <div className="tiny muted">{a.accepterEmail}</div> : null}</div></div>
            <div><span className="tiny muted">Purchase order</span><div>{a.poReference ?? (a.poToFollow ? 'to follow' : '—')}</div></div>
            <div><span className="tiny muted">Terms</span><div>{a.termsVersion ?? '—'}{a.caveatVersion ? <div className="tiny muted">caveat {a.caveatVersion}</div> : null}</div></div>
            <div><span className="tiny muted">Contract value</span><div className="num">{formatGBPCompact(t.contractValueSell)} <span className="tiny muted">ex VAT</span></div></div>
            <div><span className="tiny muted">One-off · annual</span><div className="num">{formatGBPCompact(t.oneOffSell)} · {formatGBPCompact(t.annualSell)}/yr</div></div>
          </div>
          {/* The authority statement is a paragraph, not a pill — .pill is
              nowrap, and the statement names people, the school and (on
              imports) the Quotient timestamp, so it must wrap. */}
          {a.onBehalf && (
            <div style={{ marginTop: 8, background: '#e4f2f9', border: '1px solid #bfe0ef', borderRadius: 8, padding: '8px 12px' }} data-testid="acceptance-on-behalf">
              <span className="pill pill--info">Recorded by Wave9 staff on the customer's instruction</span>
              {a.authorityNote && (
                <p className="small" style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.45, color: '#0a5b80' }}>{a.authorityNote}</p>
              )}
            </div>
          )}
          {/* Decision 218: an amended record says so, and what it replaced. */}
          {a.version > 1 && (
            <p className="pill pill--info" style={{ display: 'block', marginTop: 8, whiteSpace: 'normal', lineHeight: 1.4 }} data-testid="acceptance-amended">
              Version {a.version} — amended {a.amendedAt ? formatUKDate(a.amendedAt) : ''}{a.amendedByName ? ` by ${a.amendedByName}` : ''}
              {a.amendAuthorityNote ? <> — <span className="tiny">{a.amendAuthorityNote}</span></> : null}
            </p>
          )}

          <h3 style={{ margin: '16px 0 6px' }}>Taken <span className="pill pill--ok">{taken.length} line{taken.length === 1 ? '' : 's'}</span></h3>
          {topLevel.map((g) => {
            const rows = linesIn(g.id).filter((it) => byItem.has(it.id))
            if (rows.length === 0) return null
            const site = g.siteId ? siteById.get(g.siteId) : null
            return (
              <div key={g.id} style={{ marginBottom: 10 }}>
                <div className="small" style={{ fontWeight: 600, margin: '6px 0 2px' }}>
                  {g.name}{multi && site ? <span className="muted" style={{ fontWeight: 400 }}> — {site.name}</span> : null}
                </div>
                <table data-testid="accepted-lines">
                  <tbody>
                    {rows.map((it) => {
                      const f = byItem.get(it.id)!
                      const unit = f.recurring ? perYear(f) : f.unitPrice
                      return (
                        <tr key={it.id}>
                          <td>
                            {f.name}
                            {f.qty !== it.qty && <span className="pill pill--info" style={{ marginLeft: 6 }} title="The customer changed the quantity">offered {it.qty}</span>}
                            {f.subjectToSurvey && <span className="pill pill--warn" style={{ marginLeft: 6 }}>subject to survey</span>}
                          </td>
                          <td className="small" style={{ whiteSpace: 'nowrap' }}>× {f.qty}</td>
                          <td className="num small" style={{ whiteSpace: 'nowrap' }}>{formatGBP(unit)} <span className="tiny muted">{f.recurring ? 'each /yr' : 'each'}</span></td>
                          <td className="num" style={{ whiteSpace: 'nowrap' }}>{formatGBP(unit * f.qty)} <span className="tiny muted">{f.recurring ? `/yr · ${f.termMonths ?? 12} mo` : 'one-off'}</span></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )
          })}

          <h3 style={{ margin: '16px 0 6px' }}>Not taken <span className="pill">{left.length} line{left.length === 1 ? '' : 's'}</span></h3>
          {left.length === 0
            ? <p className="small muted">Every offered line was accepted.</p>
            : <table data-testid="untaken-lines" style={{ opacity: 0.75 }}>
                <tbody>
                  {left.map((it) => {
                    const g = groups.find((x) => x.id === it.groupId)
                    const section = g ? groups.find((x) => x.id === sectionOf(g)) : null
                    return (
                      <tr key={it.id}>
                        <td>{it.name}<span className="tiny muted"> — {section?.name ?? ''}</span></td>
                        <td className="small" style={{ whiteSpace: 'nowrap' }}>
                          {g?.kind === 'choose_one' ? 'alternative not chosen' : it.optionality === 'optional' ? 'optional, left out' : 'section not included'}
                        </td>
                        <td className="num small muted" style={{ whiteSpace: 'nowrap' }}>{formatGBP(it.recurring ? annualised(it.sellPrice, it) : it.sellPrice)} <span className="tiny">{it.recurring ? 'each /yr' : 'each'}</span></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>}
          {a.history.length > 0 && (
            <details style={{ marginTop: 16 }} data-testid="acceptance-history">
              <summary className="small" style={{ cursor: 'pointer', fontWeight: 600 }}>Previously accepted — {a.history.length} earlier version{a.history.length === 1 ? '' : 's'}</summary>
              {a.history.map((h) => (
                <div key={h.version} style={{ margin: '8px 0 4px', paddingLeft: 8, borderLeft: '2px solid var(--line-soft)' }}>
                  <div className="small">
                    <strong>Version {h.version}</strong> · {formatGBPCompact(h.contractValueSell)} contract value · {h.selections.length} line{h.selections.length === 1 ? '' : 's'} · {h.poReference ? `PO ${h.poReference}` : 'no PO'}
                    <span className="muted"> — replaced {formatUKDate(h.supersededAt)}{h.supersededByName ? ` by ${h.supersededByName}` : ''}{h.supersededOnBehalf ? ' (on the customer\'s instruction)' : ''}</span>
                  </div>
                  <div className="tiny muted">{h.selections.map((f) => `${f.name} × ${f.qty}`).join(' · ')}</div>
                </div>
              ))}
            </details>
          )}
        </div>
        <div className="slideover__foot">
          <span className="tiny muted">Frozen at acceptance · prices are the customer's (sell) prices · nothing here can be edited</span>
          <span style={{ flex: 1 }} />
          <button className="btn btn--primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </>
  )
}

// =================================================== provisioning pack panel

/**
 * Decision 178: the provisioning pack. Propose-first over the frozen accepted
 * lines: each asset-typed row defaults to ORDER NEW (a real pre-filled asset
 * record at To Order); switched to FROM STOCK it creates a deal task instead
 * (stock cannot be allocated at pack time). Cost-varied lines show the trail
 * that will ride as a note on the asset. Projects & checklists are the
 * HubSpot workflows' job — deliberately absent here.
 */
function ProvisioningPackSlideOver({ detail, onClose, onDone }: {
  detail: QuoteDetail
  onClose: () => void
  onDone: () => Promise<void>
}) {
  const { quote, provisioningPack: record } = detail
  const alreadyPacked = quote.provisioningPackAt != null
  const [plan, setPlan] = useState<PackPlan | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [result, setResult] = useState<PackCommitResult | null>(null)
  // Per-row choices, keyed dealId|itemId — seeded ON when the plan lands.
  const [choices, setChoices] = useState<Record<string, { include: boolean; source: 'new' | 'stock'; supplier: string | null }>>({})

  useEffect(() => {
    if (alreadyPacked) return
    setBusy(true)
    provisioningPack(quote.id, {})
      .then((res) => {
        const p = res as PackPlan
        setPlan(p)
        setChoices(Object.fromEntries(p.rows.map((r) => [
          `${r.dealId}|${r.itemId}`,
          { include: !r.unrouted, source: 'new' as const, supplier: r.supplier },
        ])))
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false))
  }, [alreadyPacked, quote.id])

  const keyOf = (r: PackRow) => `${r.dealId}|${r.itemId}`
  const setChoice = (r: PackRow, patch: Partial<{ include: boolean; source: 'new' | 'stock'; supplier: string | null }>) => {
    setChoices((c) => ({ ...c, [keyOf(r)]: { ...(c[keyOf(r)] ?? { include: true, source: 'new', supplier: r.supplier }), ...patch } }))
    setConfirmed(false)
  }
  const counts = (() => {
    let assets = 0, stock = 0
    for (const r of plan?.rows ?? []) {
      const c = choices[keyOf(r)]
      if (!c?.include) continue
      if (c.source === 'stock') stock++
      else if (!r.unrouted) assets++
    }
    return { assets, stock }
  })()

  // ---- the frozen record (already packed) ---------------------------------
  if (alreadyPacked && !result) {
    const assets = record.filter((r) => r.kind === 'asset')
    const tasks = record.filter((r) => r.kind === 'task')
    return (
      <>
        <div className="scrim" onClick={onClose} />
        <div className="slideover" role="dialog" aria-label="Provisioning pack">
          <div className="slideover__head">
            <div>
              <strong>Provisioning pack</strong>
              <div className="small muted" style={{ marginTop: 3 }}>
                {quote.provisioningPackAt ? `Created ${formatUKDate(quote.provisioningPackAt)}. ` : ''}
                The frozen record of what the pack created — the asset records live in HubSpot.
              </div>
            </div>
            <button className="btn btn--sm" onClick={onClose}>✕</button>
          </div>
          <div className="slideover__body">
            {assets.length > 0 && (
              <div className="field">
                <label>Assets at To Order</label>
                <table>
                  <tbody>
                    {assets.map((r) => (
                      <tr key={r.hsObjectId}>
                        <td className="tiny">{String((r.detail as Record<string, unknown>)?.uniqueRef ?? r.hsObjectId)}</td>
                        <td className="small">{String((r.detail as Record<string, unknown>)?.siteName ?? '')}</td>
                        <td className="tiny" style={{ textAlign: 'right' }}>×{String((r.detail as Record<string, unknown>)?.qty ?? '')}</td>
                        <td className="tiny muted">{(r.detail as Record<string, unknown>)?.costNote ? '± cost note' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {tasks.length > 0 && (
              <div className="field">
                <label>Stock-allocation tasks on the deal</label>
                {tasks.map((r) => (
                  <p key={r.hsObjectId} className="small" style={{ margin: '2px 0' }}>
                    ☑ {String((r.detail as Record<string, unknown>)?.subject ?? r.hsObjectId)}
                  </p>
                ))}
              </div>
            )}
          </div>
          <div className="slideover__foot">
            <span className="tiny muted">Projects &amp; checklists come from the HubSpot workflows, not the pack.</span>
            <span style={{ flex: 1 }} />
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </>
    )
  }

  // ---- the planner ---------------------------------------------------------
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover" role="dialog" aria-label="Create the provisioning pack">
        <div className="slideover__head">
          <div>
            <strong>Create the provisioning pack</strong>
            <div className="small muted" style={{ marginTop: 3 }}>
              Pre-filled asset records — and an allocation task for anything coming from stock — derived
              from the frozen accepted lines. Nothing is created until you confirm.
            </div>
          </div>
          <button className="btn btn--sm" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          {!plan && !error && <p className="muted">Deriving the pack…</p>}
          {error && <p className="pill pill--stop" style={{ whiteSpace: 'pre-wrap' }}>{error}</p>}

          {plan && !result && (
            <>
              <p className="small muted" style={{ margin: 0 }}>
                Project &amp; task checklists are <b>not created here</b> — the HubSpot workflows build them
                from the deal's W9 Product Lines. This pack creates the <b>asset records</b>.
              </p>
              {plan.rows.length === 0 && (
                <p className="pill pill--warn" style={{ display: 'block' }}>
                  No accepted line carries an asset-typed product — nothing to create.
                </p>
              )}
              {plan.targets.map((tgt) => {
                const rows = plan.rows.filter((r) => r.dealId === tgt.dealId)
                if (rows.length === 0) return null
                return (
                  <div className="field" key={tgt.dealId}>
                    <label>{plan.split ? `${tgt.dealRef} — ${tgt.siteName}` : 'Assets — one per accepted asset-typed line'}</label>
                    {rows.map((r) => {
                      const c = choices[keyOf(r)] ?? { include: !r.unrouted, source: 'new' as const, supplier: r.supplier }
                      return (
                        <div className="card" key={keyOf(r)} style={{ padding: 10, marginBottom: 8, opacity: c.include ? 1 : 0.5 }}>
                          <div className="rowflex" style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <input type="checkbox" checked={c.include} disabled={r.unrouted && c.source === 'new'}
                              onChange={(e) => setChoice(r, { include: e.target.checked })} />
                            <strong className="small" style={{ flex: 1 }}>{r.productName}</strong>
                            <span className="toggle">
                              <button aria-pressed={c.source === 'new'} onClick={() => setChoice(r, { source: 'new', include: !r.unrouted })}>Order new</button>
                              <button aria-pressed={c.source === 'stock'} onClick={() => setChoice(r, { source: 'stock', include: true })}>From stock</button>
                            </span>
                            {r.stockMatches != null && r.stockMatches > 0 && (
                              <span className="pill pill--info" title="Assets with this SKU in a stock state — information only">{r.stockMatches} in stock</span>
                            )}
                            {r.pipelineLabel && <span className="pill">{r.pipelineLabel} · To Order</span>}
                          </div>
                          {c.source === 'new' && !r.unrouted && (
                            <div className="tiny muted" style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '2px 14px' }}>
                              <span>Ref <b className="mono">{r.uniqueRef}</b></span>
                              <span>SKU <b className="mono">{r.sku ?? '—'}</b></span>
                              <span>Qty <b>{r.qty}</b></span>
                              <span>Service Type <b>{r.serviceType}</b></span>
                              <span>Site <b>{r.sitePostcode ?? '—'}{r.siteUrn ? ` · ${r.siteUrn}` : ''}</b></span>
                              <span>Unit Annual Cost <b>{formatGBPCompact(r.unitAnnualCost)}</b></span>
                              <span>Quote ref <b className="mono">{r.dealRef}</b></span>
                              <span>
                                Supplier{' '}
                                <select className="input" style={{ padding: '1px 4px', fontSize: 12 }}
                                  value={c.supplier ?? ''}
                                  onChange={(e) => setChoice(r, { supplier: e.target.value || null })}>
                                  <option value="">—</option>
                                  {r.supplierOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </span>
                            </div>
                          )}
                          {c.source === 'new' && r.unrouted && (
                            <p className="pill pill--warn" style={{ display: 'block', marginTop: 6 }}>
                              Product Line "{r.line ?? '(uncategorised)'}" has no Service Type / asset pipeline yet
                              (Admin → Product Lines) — this row is skipped until routed, or switch it to From stock.
                            </p>
                          )}
                          {c.source === 'stock' && (
                            <p className="small" style={{ marginTop: 6, borderLeft: '3px solid var(--w9-blue-light)', paddingLeft: 8 }}>
                              ☑ <b>Task on the deal instead:</b> Allocate from stock — {r.qty} × {r.sku ?? r.productName} for {r.siteName} ({r.dealRef}).
                              No asset record is created; the allocation moves the existing stock asset(s) when it happens.
                            </p>
                          )}
                          {c.source === 'new' && r.costTrail && (
                            <p className="small" style={{ marginTop: 6, borderLeft: '3px solid var(--ok, #2e7d4f)', paddingLeft: 8 }}>
                              ✓ <b>Cost log rides along:</b> {r.costTrail.oldCost != null ? `£${r.costTrail.oldCost.toFixed(2)} → ` : ''}{r.costTrail.newCost != null ? `£${r.costTrail.newCost.toFixed(2)}` : ''} — "{r.costTrail.description}" ({r.costTrail.loggedByName}
                              {r.costTrail.evidenceFilename ? `, evidence ${r.costTrail.evidenceFilename}` : ''}) — lands as a note on the asset.
                            </p>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })}

              {plan.warnings.length > 0 && (
                <div className="field">
                  {plan.warnings.map((w, i) => <p key={i} className="pill pill--warn" style={{ display: 'block', whiteSpace: 'pre-wrap', marginBottom: 4 }}>{w}</p>)}
                </div>
              )}

              {(counts.assets > 0 || counts.stock > 0) && (
                <div className="field" style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 10 }}>
                  <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
                    <span>Create exactly what's previewed above. <span className="tiny muted">Runs once per deal — a re-run resumes rather than duplicates. Split site deals each get their own pack (split first, then pack).</span></span>
                  </label>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="field">
              <div className="banner" style={{ marginBottom: 8 }}>
                ✓ <span><strong>Provisioning pack created</strong> — {result.assets.length} asset{result.assets.length === 1 ? '' : 's'} at To Order
                {result.stockTasks.length > 0 ? `, ${result.stockTasks.length} stock-allocation task${result.stockTasks.length === 1 ? '' : 's'} on the deal` : ''}
                {result.costNotes > 0 ? `, ${result.costNotes} cost-log note${result.costNotes === 1 ? '' : 's'}` : ''}.</span>
              </div>
              {result.warnings.map((w, i) => (
                <p key={i} className="pill pill--warn" style={{ display: 'block', marginTop: 6 }}>{w}</p>
              ))}
              <p className="small muted">The ticket flow was not touched — this deal is on the pack pilot. Everything above is on the quote's activity trail.</p>
            </div>
          )}
        </div>
        <div className="slideover__foot">
          {result
            ? <>
                <span className="pill pill--ok">pack created</span>
                <span style={{ flex: 1 }} />
                <button className="btn btn--primary" onClick={onClose}>Done</button>
              </>
            : <>
                <span className="tiny muted mono">{quote.quoteNumber}</span>
                <span style={{ flex: 1 }} />
                <button className="btn btn--primary"
                  disabled={busy || !plan || !confirmed || (counts.assets === 0 && counts.stock === 0)}
                  onClick={async () => {
                    if (!plan) return
                    setBusy(true); setError(null)
                    try {
                      const decisions: PackDecisionEntry[] = plan.rows.map((r) => {
                        const c = choices[keyOf(r)] ?? { include: !r.unrouted, source: 'new' as const, supplier: r.supplier }
                        return { itemId: r.itemId, dealId: r.dealId, include: c.include, source: c.source, supplier: c.supplier }
                      })
                      const res = await provisioningPack(quote.id, { decisions, confirmed: true }) as PackCommitResult
                      setResult(res)
                      await onDone()
                    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
                  }}>
                  {busy ? 'Working…' : `Create ${[counts.assets > 0 ? `${counts.assets} asset${counts.assets === 1 ? '' : 's'}` : null, counts.stock > 0 ? `${counts.stock} stock task${counts.stock === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ') || 'pack'}`}
                </button>
              </>}
        </div>
      </div>
    </>
  )
}

// ============================================================== start modal

function StartModal({ mode, onClose, onPick, onDeal }: {
  mode: Partner['mode']
  onClose: () => void
  onPick: (payload: {
    name: string; urn?: string | null; postcode?: string | null; address?: unknown; contactName?: string | null
    sites: Array<{ name: string; urn?: string | null; postcode?: string | null; address?: unknown; contact?: string | null; numberOnRoll?: number | null }>
  }) => void
  onDeal: (deal: DealHit | null) => void
}) {
  const tabs = mode === 'internal'
    ? ['GIAS school search', 'Enter manually', 'Attach to a deal']
    : ['GIAS school search', 'Enter manually']
  const [tab, setTab] = useState(0)
  const [q, setQ] = useState('')
  const [schools, setSchools] = useState<SchoolHit[]>([])
  const [giasDown, setGiasDown] = useState<string | null>(null)
  const [checked, setChecked] = useState<Set<number>>(new Set())
  const [trustName, setTrustName] = useState('')
  // "Live Wave9 customer" toggle — the server only offers it to STAFF sessions
  // (channel-conflict information, spec §6.3: partners never see it).
  const [canFilterCustomers, setCanFilterCustomers] = useState(false)
  const [customersOnly, setCustomersOnly] = useState(false)
  // Decision 62 — the "trust — pick sites" flow: expanding a trust hit fetches
  // its roster from the Hub so EVERY member school is offered, not just the
  // ones whose names happened to match the search.
  const [trustOpen, setTrustOpen] = useState<string | null>(null)
  const [roster, setRoster] = useState<SchoolHit[]>([])
  const [rosterState, setRosterState] = useState<'idle' | 'loading' | 'error'>('idle')
  const [rosterChecked, setRosterChecked] = useState<Set<string>>(new Set())

  const openTrust = (uid: string) => {
    if (trustOpen === uid) { setTrustOpen(null); return }
    setTrustOpen(uid)
    setRoster([])
    setRosterChecked(new Set())
    setRosterState('loading')
    fetchTrustRoster(uid)
      .then((r) => { setRoster(r.schools); setRosterState('idle') })
      .catch(() => setRosterState('error'))
  }
  const [manualName, setManualName] = useState('')
  const [manualPostcode, setManualPostcode] = useState('')
  const [manualContact, setManualContact] = useState('')
  const [dealQ, setDealQ] = useState('')
  const [deals, setDeals] = useState<DealHit[]>([])
  const [dealsDown, setDealsDown] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (q.trim().length < 2) { setSchools([]); return }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      searchSchools(q.trim(), customersOnly)
        .then((r) => {
          setSchools(r.schools)
          setCanFilterCustomers(r.canFilterCustomers === true)
          setGiasDown(null)
        })
        .catch((e: Error) => { setSchools([]); setGiasDown(e.message) })
    }, 300)
  }, [q, customersOnly])

  useEffect(() => {
    if (tab !== 2 || dealQ.trim().length < 2) { setDeals([]); return }
    const t = setTimeout(() => {
      searchDeals(dealQ.trim())
        .then((r) => { setDeals(r.deals); setDealsDown(null) })
        .catch((e: Error) => { setDeals([]); setDealsDown(e.message) })
    }, 300)
    return () => clearTimeout(t)
  }, [dealQ, tab])

  const toSite = (s: SchoolHit) => ({
    name: s.name, urn: s.urn, postcode: s.postcode, address: s.address, contact: null, numberOnRoll: s.numberOnRoll ?? null,
  })

  const pickSingle = (s: SchoolHit) => onPick({
    name: s.name, urn: s.urn, postcode: s.postcode, address: s.address, sites: [toSite(s)],
  })

  const pickTicked = () => {
    const chosen = [...checked].map((i) => schools[i]).filter(Boolean)
    if (chosen.length === 0) return
    onPick({
      name: trustName.trim() || chosen[0].trustName || `${chosen[0].name} (trust)`,
      urn: null,
      sites: chosen.map(toSite),
    })
  }

  const pickTrustRoster = (trust: SchoolHit) => {
    const chosen = roster.filter((s) => s.urn && rosterChecked.has(s.urn))
    if (chosen.length === 0) return
    onPick({ name: trust.name, urn: null, sites: chosen.map(toSite) })
  }

  const commonTrust = [...new Set(schools.filter((_, i) => checked.has(i)).map((s) => s.trustName).filter(Boolean))]

  return (
    <div className="modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal__card">
        <div className="modal__head">
          <h1>Who is this quote for?</h1>
          <div className="tabs">
            {tabs.map((t, i) => <button key={t} aria-pressed={i === tab} onClick={() => setTab(i)}>{t}</button>)}
          </div>
        </div>
        <div className="modal__body">
          {tab === 0 && (
            <>
              <div className="rowflex">
                <input className="input" style={{ flex: 1 }} autoFocus
                  placeholder="School or trust name, URN or postcode…" value={q} onChange={(e) => setQ(e.target.value)} />
                {canFilterCustomers && (
                  <label className={`chip chip--action ${customersOnly ? 'chip--on' : ''}`} title="Live Wave9 customers only — a live telecom asset in the Hub, or a HubSpot company at lifecycle customer / with won revenue. Wave9 staff only.">
                    <input type="checkbox" checked={customersOnly} onChange={(e) => setCustomersOnly(e.target.checked)} />
                    Wave9 customers only
                  </label>
                )}
              </div>
              {giasDown && q.trim().length >= 2 && (
                <div className="banner" style={{ margin: '10px 0' }}>
                  <span><strong>GIAS search is unavailable</strong> — {giasDown} Use “Enter manually” meanwhile (flagged as an analytics gap: conflict alerts depend on URN).</span>
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                {schools.map((s, i) => s.kind === 'trust' ? (
                  <div key={`t-${s.trustUid}-${i}`} className="school" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <div className="rowflex" style={{ cursor: 'pointer', justifyContent: 'space-between' }} onClick={() => s.trustUid && openTrust(s.trustUid)}>
                      <div>
                        <strong>{s.name}</strong>{s.customer === true && <span className="pill pill--ok" style={{ marginLeft: 8 }}>Wave9 customer</span>}
                        <div className="small muted">
                          Multi-academy trust{s.openSchools != null ? ` · ${s.openSchools} open school${s.openSchools === 1 ? '' : 's'}` : ''}
                        </div>
                      </div>
                      <span className="pill pill--info">trust — pick sites</span>
                    </div>
                    {trustOpen === s.trustUid && (
                      <div style={{ marginTop: 10, borderTop: '1px solid var(--line-soft)', paddingTop: 10 }}>
                        {rosterState === 'loading' && <p className="muted small">Loading the trust's schools…</p>}
                        {rosterState === 'error' && <p className="pill pill--stop">Could not load the roster — pick the schools from search instead.</p>}
                        {roster.map((m) => (
                          <label key={m.urn ?? m.name} className="rowflex small" style={{ padding: '3px 0', cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!m.urn && rosterChecked.has(m.urn)} onChange={(e) => {
                              if (!m.urn) return
                              const next = new Set(rosterChecked)
                              if (e.target.checked) next.add(m.urn); else next.delete(m.urn)
                              setRosterChecked(next)
                            }} />
                            {m.name} <span className="muted tiny">({m.postcode ?? '—'}{m.urn ? `, ${m.register ? 'site ref' : 'URN'} ${m.urn}` : ''})</span>
                            <RegisterTag s={m} />
                            {m.customer === true && <span className="pill pill--ok tiny">Wave9 customer</span>}
                          </label>
                        ))}
                        {rosterState === 'idle' && roster.length > 0 && (
                          <div className="rowflex" style={{ marginTop: 8 }}>
                            <button className="btn btn--sm btn--primary" disabled={rosterChecked.size === 0} onClick={() => pickTrustRoster(s)}>
                              Prepare quote for this trust{rosterChecked.size > 0 ? ` — ${rosterChecked.size} site${rosterChecked.size === 1 ? '' : 's'}` : ''}
                            </button>
                            <button className="btn btn--sm btn--ghost" onClick={() => {
                              setRosterChecked(rosterChecked.size === roster.length
                                ? new Set()
                                : new Set(roster.map((m) => m.urn).filter((u): u is string => !!u)))
                            }}>{rosterChecked.size === roster.length ? 'Clear all' : 'Select all'}</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div key={`${s.urn}-${i}`} className="school">
                    <label className="rowflex" style={{ flex: 1, cursor: 'pointer' }} title="Tick more than one to quote a trust across sites">
                      <input type="checkbox" checked={checked.has(i)} onChange={(e) => {
                        const next = new Set(checked)
                        if (e.target.checked) next.add(i); else next.delete(i)
                        setChecked(next)
                      }} />
                      <div>
                        <strong>{s.name}</strong>{s.customer === true && <span className="pill pill--ok" style={{ marginLeft: 8 }}>Wave9 customer</span>}
                        <RegisterTag s={s} />
                        <div className="small muted">
                          {s.urn ? `${s.register ? 'Site ref' : 'URN'} ${s.urn} · ` : ''}{s.postcode ?? ''}{s.la ? ` · ${s.la}` : ''}
                          {s.trustName ? ` · ${s.trustName}` : ''}
                        </div>
                      </div>
                    </label>
                    <button className="btn btn--sm" onClick={() => pickSingle(s)}>Select</button>
                  </div>
                ))}
                {q.trim().length >= 2 && !giasDown && schools.length === 0 && <p className="muted small">No matches in GIAS.</p>}
              </div>
              {checked.size > 0 && (
                <div className="card" style={{ padding: 12, marginTop: 10 }}>
                  <div className="small" style={{ marginBottom: 8 }}>
                    <strong>{checked.size} site{checked.size === 1 ? '' : 's'} ticked</strong> — a quote is <b>prepared for</b> a recipient and covers one or more <b>sites</b>.
                  </div>
                  <div className="rowflex">
                    <input className="input" style={{ flex: 1 }} placeholder="Recipient organisation (e.g. the trust)"
                      value={trustName || (commonTrust.length === 1 ? commonTrust[0]! : '')}
                      onChange={(e) => setTrustName(e.target.value)} />
                    <button className="btn btn--sm btn--primary" onClick={pickTicked}>Prepare quote for these sites</button>
                  </div>
                </div>
              )}
              <p className="hint" style={{ marginTop: 10 }}>
                GIAS/Edubase lookup via the Wave9 Hub — URN prefills the address and powers channel-conflict checks.
                Contains GIAS data © Crown copyright, Open Government Licence v3.0.
              </p>
            </>
          )}
          {tab === 1 && (
            <>
              <div className="field"><label>Organisation name</label>
                <input className="input" style={{ width: '100%' }} placeholder="e.g. Stafford Grammar School Trust" value={manualName} onChange={(e) => setManualName(e.target.value)} /></div>
              <div className="rowflex">
                <div className="field" style={{ flex: 1 }}><label>Postcode</label>
                  <input className="input" placeholder="ST18…" value={manualPostcode} onChange={(e) => setManualPostcode(e.target.value)} /></div>
                <div className="field" style={{ flex: 2 }}><label>Contact email</label>
                  <input className="input" style={{ width: '100%' }} placeholder="finance@…" value={manualContact} onChange={(e) => setManualContact(e.target.value)} /></div>
              </div>
              <p className="hint">Manual entry skips URN capture — flagged as an analytics gap (conflict alerts depend on URN).</p>
              <button className="btn btn--primary" disabled={!manualName.trim()} onClick={() => onPick({
                name: manualName.trim(), urn: null, postcode: manualPostcode.trim() || null,
                contactName: null,
                sites: [{ name: manualName.trim(), urn: null, postcode: manualPostcode.trim() || null }],
              })}>Use this customer</button>
            </>
          )}
          {tab === 2 && (
            <>
              <input className="input" style={{ width: '100%' }} placeholder="Search the direct pipeline…" value={dealQ} onChange={(e) => setDealQ(e.target.value)} />
              {dealsDown && <div className="banner" style={{ margin: '10px 0' }}><span>{dealsDown}</span></div>}
              <div style={{ marginTop: 10 }}>
                {deals.map((d) => (
                  <div key={d.hsDealId} className="school school--pick" onClick={() => onDeal(d)}>
                    <div><strong>{d.name}</strong><div className="small muted">{d.stage ?? '—'}{d.amount != null ? ` · ${formatGBPCompact(d.amount)}` : ''}</div></div>
                    <span className="pill pill--info">attach</span>
                  </div>
                ))}
              </div>
              <p className="hint">Internal quotes attach to an existing direct-pipeline deal — update, never duplicate. If none exists the portal creates one on send.</p>
              <button className="btn btn--sm btn--ghost" onClick={() => onDeal(null)}>Clear the linked deal</button>
            </>
          )}
        </div>
        <div className="slideover__foot">
          <button className="btn btn--ghost" onClick={onClose}>Skip for now — add before send</button>
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

// ============================================================== customer preview

function Preview({ detail, partner, settings, productById, termsSets, selections: liveSelections, onSelections: onLiveSelections, amendMode = false }: {
  detail: QuoteDetail
  partner: Partner
  settings: PortalSettings | null
  productById: Map<string, PricedProduct>
  termsSets: TermsSetRow[]
  selections: SelectionMap
  onSelections: (s: SelectionMap) => void
  /** Decision 218: amend mode — the accepted record is the STARTING point and the switches move. */
  amendMode?: boolean
}) {
  const { quote, groups, items, sites } = detail
  // Decision 191: on an ACCEPTED quote the preview is the frozen record, not
  // the offer at its defaults — the switches show what the customer chose and
  // cannot be moved. (Before, this tab quietly showed the preselected offer,
  // which could differ from what was accepted in every optional line.)
  // Decision 218: in amend mode the live selection state takes over (seeded
  // from the record by the Builder when amend mode starts).
  const frozen = quote.status === 'accepted' && detail.acceptance && !amendMode ? detail.acceptance : null
  const selections: SelectionMap = frozen
    ? selectionsFromAcceptance(frozen.selections, items, groups.map((g) => ({
        id: g.id, kind: g.kind, parentGroupId: g.parentGroupId,
        optional: g.optional, preselected: g.preselected, choiceSet: g.choiceSet ?? null, sort: g.sort,
      })))
    : liveSelections
  const onSelections = frozen ? () => {} : onLiveSelections
  const locked = frozen != null
  const multi = sites.length > 1
  const branding = (partner.branding ?? {}) as Record<string, string | undefined>
  const isReseller = partner.mode === 'reseller'
  const brand = isReseller ? (branding.primary ?? '#006699') : '#006699'
  const s = settings?.settings ?? {}

  const issuer = isReseller
    ? {
        name: branding.footer ?? partner.name,
        trading: branding.trading_address ?? '(trading address — partner branding)',
        web: branding.website ?? '', email: branding.reply_to ?? '', phone: branding.phone ?? '',
        legal: branding.legal_name ?? partner.name,
        companyNo: branding.company_no ?? '(company no — partner branding)',
        regOffice: branding.registered_office ?? '(registered office — partner branding)',
        vat: branding.vat_number ?? '(VAT no — partner branding)',
      }
    : {
        name: 'Wave 9 Managed Services',
        trading: s.trading_address ?? '', web: s.website ?? '', email: s.email ?? '',
        phone: s.phone || '(phone — Admin → Organisation)',
        legal: s.legal_name ?? '', companyNo: s.company_no ?? '', regOffice: s.registered_office ?? '',
        vat: s.vat_number || '(VAT — Admin → Organisation)',
      }

  // Public totals, live against the preview's selections — sell side only.
  const asLines: QuoteLine[] = items.map((it) => ({
    id: it.id, groupId: it.groupId, hsProductId: it.hsProductId, name: it.name,
    description: it.description, qty: it.qty, qtyEditable: it.qtyEditable,
    qtyMin: it.qtyMin, qtyMax: it.qtyMax, subjectToSurvey: it.subjectToSurvey,
    isAdhoc: it.isAdhoc, listPrice: it.listPrice, buyPrice: it.buyPrice ?? it.listPrice,
    sellPrice: it.sellPrice, recurring: it.recurring, billingFrequency: it.billingFrequency,
    termMonths: it.termMonths, optionality: it.optionality, preselected: it.preselected, sort: it.sort,
  }))
  // ⚠️ 117 sharp edge: SelectionGroup construction sites must carry choiceSet
  // + sort or set members silently resolve as plain sections (overcounting).
  const previewGroups = groups.map((g) => ({
    id: g.id, kind: g.kind, parentGroupId: g.parentGroupId,
    optional: g.optional, preselected: g.preselected,
    choiceSet: g.choiceSet ?? null, sort: g.sort,
  }))
  const t = computeTotals(asLines, selections, previewGroups)
  // Decision 119: live per-section subtotals for subtotal-priced sections.
  const subTotals = sectionSubtotals(asLines, selections, previewGroups)

  const selectedTerms = termsSets.find((x) => x.id === quote.termsSetId)
  const surveyableCats = items.map((it) => (it.hsProductId ? productById.get(it.hsProductId)?.category ?? null : null))
  const showEcc = quote.eccNotice && hasSurveyableLines(surveyableCats)

  const setSel = (lineId: string, patch: { selected?: boolean; qty?: number }) => {
    onSelections({ ...selections, [lineId]: { ...selections[lineId], ...patch } })
  }
  // Decision 117: the shared answer to "which sections count".
  const includedIds = includedSectionIds(
    groups.map((g) => ({
      id: g.id, kind: g.kind, parentGroupId: g.parentGroupId,
      optional: g.optional, preselected: g.preselected,
      choiceSet: g.choiceSet ?? null, sort: g.sort,
    })),
    selections,
  )
  const isPicked = (line: QuoteItemRow, groupLines: QuoteItemRow[]) => {
    if (line.optionality === 'required') return true
    if (line.optionality === 'optional') return selections[line.id]?.selected ?? line.preselected
    // Decision 107: no preselected member = optional group — none is valid.
    const pickedId = groupLines.find((l) => selections[l.id]?.selected)?.id
      ?? groupLines.find((l) => l.preselected)?.id
    return pickedId === line.id
  }

  const siteById = new Map(sites.map((x) => [x.id, x]))

  return (
    <div className="preview">
      <div className="pdoc" style={{ ['--brand' as never]: brand }}>
        <div className="pdoc__brand">
          {/* Issuer column: logo on top, name + trading address UNDER it (Mark, 2026-08-01). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start', maxWidth: 320 }}>
            {isReseller && branding.logo_url
              ? <img src={branding.logo_url} alt={partner.name} style={{ height: 44, borderRadius: 9 }} />
              : isReseller
                ? <div className="pdoc__logo">{(issuer.name || 'R')[0]}</div>
                : <img src="/Wave-9-Logo-Large-Transparent-Background-1000px.png" alt="Wave9"
                    style={{ height: 44, background: '#fff', borderRadius: 9, padding: '4px 8px' }} />}
            <div>
              <strong style={{ fontSize: 15 }}>{issuer.name}</strong>
              <div className="small muted" style={{ marginTop: 3, lineHeight: 1.5 }}>
                {issuer.trading}<br />
                {issuer.web}<br />
                {issuer.phone}
                {detail.owner && (
                  <><br /><span style={{ color: 'var(--ink)' }}>
                    Prepared by {detail.owner.name ?? detail.owner.email}
                    {detail.owner.name ? <span className="muted"> · {detail.owner.email}</span> : null}
                  </span></>
                )}
              </div>
              {isReseller && <div className="tiny muted" style={{ marginTop: 3 }}>issuer identity &amp; logo from partner branding tokens</div>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: brand, letterSpacing: '0.04em' }}>QUOTATION</div>
            <div className="small muted" style={{ margin: '3px 0 8px' }}>
              {quote.quoteNumber}{quote.validUntil ? ` · valid until ${formatUKDate(quote.validUntil)}` : ''}
            </div>
            <div style={{ background: '#f6fafc', border: '1px solid var(--line)', borderRadius: 8, padding: '9px 13px', textAlign: 'left', fontSize: 12.5, lineHeight: 1.5, minWidth: 250 }}>
              <div className="tiny muted" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Prepared for</div>
              <strong>{detail.customer?.name ?? '—'}</strong>
              {/* Attn: the quote's recipient contact(s) — name and email (decision 65). */}
              {detail.recipients.length > 0
                ? <div className="muted">Attn: {detail.recipients[0].name || detail.recipients[0].email} · {detail.recipients[0].email}</div>
                : !multi && sites[0]?.contact
                  ? <div className="muted">Attn: {sites[0].contact}</div>
                  : null}
              {!multi && sites[0] && (
                <div className="muted">
                  {sites[0].postcode ?? ''}{sites[0].urn ? ` · URN ${sites[0].urn}` : ''}
                </div>
              )}
              {multi && (
                <div className="muted" style={{ marginTop: 3 }}>
                  Sites covered:<br />
                  {sites.map((x) => <span key={x.id}>• {x.name} ({x.postcode ?? '—'}{x.urn ? `, URN ${x.urn}` : ''})<br /></span>)}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="pdoc__body">
          {/* The quote title sits just under the brand bar (Mark, 2026-08-01). */}
          {quote.title && <div className="ptitle">{quote.title}</div>}
          {quote.coverNote && <div className="covernote"><QuoteMarkup text={quote.coverNote} /></div>}
          {(() => {
          const topLevel = groups.filter((g) => !g.parentGroupId)
          const pickSetMember = (g: typeof topLevel[number], currentlyOn: boolean) => {
            const members = topLevel.filter((x) => x.choiceSet === g.choiceSet)
            const noDefault = members.every((m) => !m.preselected)
            const next = { ...selections }
            for (const m of members) {
              next[`s:${m.id}`] = { ...next[`s:${m.id}`], selected: currentlyOn && noDefault ? false : m.id === g.id }
            }
            onSelections(next)
          }
          const renderSection = (g: typeof topLevel[number], optionIndex?: number) => {
            const own = items.filter((it) => it.groupId === g.id)
            const blocks = groups
              .filter((c) => c.parentGroupId === g.id)
              .map((c) => ({ block: c, lines: items.filter((it) => it.groupId === c.id) }))
              .filter((b) => b.lines.length > 0)
            if (own.length === 0 && blocks.length === 0) return null
            const site = g.siteId ? siteById.get(g.siteId) : null
            // Decision 87 + 117: optional sections and choice-set members both
            // resolve through the shared includedSectionIds.
            const sectionOn = (g.optional || g.choiceSet) ? includedIds.has(g.id) : true
            // Decision 119: subtotal-priced section — per-line prices hidden,
            // one-off + per-year subtotals shown at the foot instead.
            const subOnly = g.subtotalOnly
            const st = subTotals.get(g.id) ?? { oneOffSell: 0, annualSell: 0 }

            // One row. Mark's 2026-08-01 rules: required lines carry a green
            // tick; selectable lines carry a SLIDER (green = selected, grey =
            // not); an unselected option shows its unit price but NOT a total.
            const renderRow = (l: QuoteItemRow, blockLines: QuoteItemRow[], optionLabel?: string) => {
              const isChoice = l.optionality === 'option'
              // Decision 116: lines in an EXCLUDED section render as not
              // included — no tick, no total (mirrors the customer page).
              const picked = sectionOn && isPicked(l, blockLines)
              const qty = selections[l.id]?.qty ?? l.qty
              const subYear = l.recurring && l.termMonths != null && l.termMonths > 0 && l.termMonths < 12
              const perYear = annualised(l.sellPrice, l)
              const unit = l.recurring ? (subYear ? Math.round(perYear * l.termMonths! / 12 * 100) / 100 : perYear) : l.sellPrice
              const perLabel = l.recurring ? (subYear ? `over ${termLabel(l.termMonths)}` : 'per year') : 'one-off'
              const selectable = (isChoice || l.optionality === 'optional') && sectionOn
              return (
                <div key={l.id} className={`pline ${subOnly ? 'pline--sub' : ''} ${picked ? '' : 'pline--off'}`}>
                  <span className="pline__ctl">
                    {!selectable
                      ? (picked ? <span className="ptick" title="Included">✓</span> : <span className="muted">—</span>)
                      : <label className={`switch ${picked ? 'switch--on' : ''}`} title={picked ? 'Selected' : 'Not selected'}>
                          <input
                            type="checkbox"
                            checked={picked}
                            disabled={locked}
                            onChange={(e) => {
                              if (isChoice) {
                                // choose-one: turning one on turns its siblings off.
                                // 107: with NO default, the picked one toggles off to none.
                                const hasDefault = blockLines.some((x) => x.preselected)
                                const turnOff = picked && !hasDefault && !e.target.checked
                                const next = { ...selections }
                                for (const x of blockLines) {
                                  next[x.id] = { ...next[x.id], selected: turnOff ? false : x.id === l.id }
                                }
                                onSelections(next)
                              } else {
                                setSel(l.id, { selected: e.target.checked })
                              }
                            }}
                          />
                          <span className="switch__track"><span className="switch__knob" /></span>
                        </label>}
                  </span>
                  <div className="pline__main">
                    <div style={{ fontWeight: 550, fontSize: 12.5 }}>
                      {optionLabel && <span className="pill pill--info" style={{ marginRight: 6 }}>{optionLabel}</span>}
                      {l.name}{' '}
                      {l.subjectToSurvey && <span className="pill pill--warn">price may vary following survey</span>}
                    </div>
                    {l.description && <div className="pline__desc">{l.description}</div>}
                  </div>
                  <div className="pline__qty small">
                    {l.qtyEditable && picked
                      ? <>
                          <span className="qty">
                            <button disabled={locked} onClick={() => setSel(l.id, { qty: Math.round(Math.max(l.qtyMin ?? 1, qty - 1) * 100) / 100 })}>−</button>
                            <input readOnly value={qty} />
                            <button disabled={locked} onClick={() => setSel(l.id, { qty: Math.round(Math.min(l.qtyMax ?? QTY_MAX, qty + 1) * 100) / 100 })}>+</button>
                          </span>
                          <div className="tiny muted">({l.qtyMin}–{l.qtyMax})</div>
                        </>
                      : <span className="muted">× {qty}</span>}
                  </div>
                  {/* Decision 119: subtotal-priced sections carry no per-line prices. */}
                  {!subOnly && (
                    <div className="pline__unit num">
                      {formatGBP(unit)}
                      <div className="tiny muted">{l.recurring ? (subYear ? `each ${perLabel}` : 'each /yr') : 'each'}</div>
                    </div>
                  )}
                  {!subOnly && (
                    <div className="pline__total num">
                      {/* Unselected options: unit price stays visible, the total does not. */}
                      {picked ? formatGBP(unit * qty) : <span className="muted">—</span>}
                      {picked && <div className="tiny muted">{perLabel}</div>}
                    </div>
                  )}
                </div>
              )
            }

            return (
              <div key={g.id} className={`psec ${subOnly ? 'psec--sub' : ''}`} style={sectionOn ? undefined : { opacity: 0.55 }}>
                <h2>
                  {optionIndex != null && <span className="pill pill--info" style={{ marginRight: 8, verticalAlign: 'middle' }}>Alternative {String.fromCharCode(65 + optionIndex)}</span>}
                  {g.name}{multi && site ? <> — <span style={{ fontWeight: 400 }}>{site.name} ({site.postcode ?? '—'})</span></> : null}
                  {g.optional && !g.choiceSet && (
                    <label className={`switch ${sectionOn ? 'switch--on' : ''}`} style={{ marginLeft: 10, verticalAlign: 'middle' }}
                      title={sectionOn ? 'Section included — toggle to remove it from this quotation' : 'Section not included — toggle to add it'}>
                      <input type="checkbox" checked={sectionOn} disabled={locked}
                        onChange={(e) => setSel(`s:${g.id}`, { selected: e.target.checked })} />
                      <span className="switch__track"><span className="switch__knob" /></span>
                    </label>
                  )}
                  {g.choiceSet && (
                    <label className={`switch ${sectionOn ? 'switch--on' : ''}`} style={{ marginLeft: 10, verticalAlign: 'middle' }}
                      title={sectionOn ? 'This alternative is selected' : 'Choose this alternative'}>
                      <input type="checkbox" checked={sectionOn}
                        disabled={locked}
                        onChange={() => pickSetMember(g, sectionOn)} />
                      <span className="switch__track"><span className="switch__knob" /></span>
                    </label>
                  )}
                  {g.optional && !g.choiceSet && <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 400 }}>{sectionOn ? 'optional — included' : 'optional — not included'}</span>}
                  {g.choiceSet && <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 400 }}>{sectionOn ? 'selected alternative' : 'not selected'}</span>}
                </h2>
                {/* Section table heading — brand bar, white text (Mark, 2026-08-01). */}
                <div className="psec__head">
                  <span>Opt</span><span>Item</span><span>Qty</span>
                  {!subOnly && <span style={{ textAlign: 'right' }}>Unit Price</span>}
                  {!subOnly && <span style={{ textAlign: 'right' }}>Total Price</span>}
                </div>
                <div style={sectionOn ? undefined : { pointerEvents: 'none' }}>
                {g.kind === 'choose_one'
                  // Legacy top-level choose-one group: render its lines as an option run.
                  ? own.map((l, i) => (
                      <div key={l.id}>
                        {i > 0 && <div className="por">— OR —</div>}
                        {renderRow(l, own, `Option ${i + 1}`)}
                      </div>
                    ))
                  : <>
                      {own.map((l) => renderRow(l, own))}
                      {blocks.map(({ block, lines }) => (
                        <div key={block.id} className="pblock">
                          <div className="pblock__intro">{block.name} — choose <strong>ONE</strong> of the following{lines.every((x) => !x.preselected) ? ' (optional — or none)' : ''}:</div>
                          {lines.map((l, i) => (
                            <div key={l.id}>
                              {i > 0 && <div className="por">— OR —</div>}
                              {renderRow(l, lines, `Option ${i + 1}`)}
                            </div>
                          ))}
                        </div>
                      ))}
                    </>}
                </div>
                {subOnly && (
                  <div className="psubtotal">
                    <span>Section subtotal (ex VAT)</span>
                    {sectionOn
                      ? <span className="num">
                          {st.oneOffSell > 0 && <>One-off {formatGBP(st.oneOffSell)}</>}
                          {st.oneOffSell > 0 && st.annualSell > 0 && <span className="psubtotal__sep"> · </span>}
                          {st.annualSell > 0 && <>{formatGBP(st.annualSell)} per year</>}
                          {(st.oneOffSell > 0 || st.annualSell > 0) && (st.termSell ?? 0) > 0 && <span className="psubtotal__sep"> · </span>}
                          {(st.termSell ?? 0) > 0 && <>{formatGBP(st.termSell!)} over term</>}
                          {st.oneOffSell === 0 && st.annualSell === 0 && !(st.termSell ?? 0) && formatGBP(0)}
                        </span>
                      : <span className="muted">—</span>}
                  </div>
                )}
              </div>
            )
          }
          const renderedSets = new Set<string>()
          return topLevel.map((g) => {
            if (!g.choiceSet) return renderSection(g)
            if (renderedSets.has(g.choiceSet)) return null
            renderedSets.add(g.choiceSet)
            const members = topLevel.filter((x) => x.choiceSet === g.choiceSet)
            if (members.length === 1) return renderSection(g)
            const noDefault = members.every((m) => !m.preselected)
            return (
              <div key={`set:${g.choiceSet}`} className="pchoiceset">
                <div className="pblock__intro">Choose <strong>ONE</strong> of the following alternatives{noDefault ? ' (optional — or none)' : ''}:</div>
                {members.map((m, i) => (
                  <div key={m.id}>
                    {i > 0 && <div className="por">— OR —</div>}
                    {renderSection(m, i)}
                  </div>
                ))}
              </div>
            )
          })
          })()}
          {showEcc && (
            <div className="pnote">⚠ <strong>Excess construction charges may apply following survey.</strong>{' '}
              If the site survey identifies additional construction work, any charges will be confirmed with you in a revised quotation before you are committed.</div>
          )}
          <div className="ptotals">
            <div><span className="muted">One-off charges</span><span className="num">{formatGBP(t.oneOffSell)}</span></div>
            <div><span className="muted">Annual recurring</span><span className="num">{formatGBP(t.annualSell)} /yr</span></div>
            <div className="grand"><span>Contract value (ex VAT)</span><span className="num">{formatGBPCompact(t.contractValueSell)}</span></div>
          </div>
          {/* Decision 104: free-text footer — right after the contract value,
              mirrored on the customer page and PDF (decision 69: move together). */}
          {quote.footerText.trim() !== '' && <div className="pfooter"><QuoteMarkup text={quote.footerText} /></div>}
          <div className="paccept">
            <p className="small muted" style={{ margin: '0 0 10px' }}>
              This quotation is subject to{' '}
              <strong>{selectedTerms ? `${selectedTerms.name} ${selectedTerms.currentVersion ?? ''}` : isReseller ? `${partner.name}'s terms of business` : 'Wave9 terms (select at send)'}</strong>
              {' '}— the version you accept is recorded. Acceptance is subject to survey / technical due diligence.
              {detail.attachments.length > 0 && <> Supporting documents: {detail.attachments.map((a) => a.title).join(' · ')}.</>}
            </p>
            {locked
              ? <div className="pill pill--ok" style={{ display: 'inline-block' }} data-testid="preview-accepted">
                  ✓ Accepted {formatUKDate(frozen!.acceptedAt)}{frozen!.accepterName ? ` by ${frozen!.accepterName}` : ''}
                </div>
              : <button className="btn btn--primary" disabled title="The customer page ships in phase 3">Accept this quotation</button>}{' '}
            <span className="btn btn--sm btn--ghost">Download PDF</span>{/* decision 89: ask-a-question retired — replies to the owner's email carry questions */}
            <div className="tiny muted" style={{ marginTop: 8 }}>
              {locked
                ? 'This is the frozen acceptance record — the options and quantities the customer chose. Nothing here can be changed.'
                : 'Live totals recalculate as options and quantities are changed — this preview is the screen-share surface. Phase 3 makes this page real.'}
            </div>
          </div>
        </div>
        <div className="pfoot">
          {issuer.legal} · Registered in England &amp; Wales No. {issuer.companyNo} · Registered office: {issuer.regOffice} · VAT No. {issuer.vat}<br />
          All prices exclude VAT. {!isReseller && 'School data © GIAS, Open Government Licence v3.0. '}This quotation is issued subject to the terms referenced above.
        </div>
      </div>
    </div>
  )
}

// ============================================================== sites slide-over

/**
 * Edit the site list on a quote (decisions 62/81) — the affordance the trust
 * flow was missing: adding a school later, or dropping one, no longer means
 * re-running the whole start modal. Saving replaces the site set; sites that
 * survive (matched by URN) keep their section tags.
 */
function SitesSlideOver({ customer, sites, disabled, onClose, onSave }: {
  customer: { name: string }
  sites: QuoteDetail['sites']
  disabled: boolean
  onClose: () => void
  onSave: (sites: Array<{ name: string; urn?: string | null; postcode?: string | null; address?: unknown; contact?: string | null; numberOnRoll?: number | null }>) => void
}) {
  const [list, setList] = useState<Array<{
    name: string; urn: string | null; postcode: string | null; address: unknown; contact: string | null; numberOnRoll: number | null
  }>>(sites.map((s) => ({
    name: s.name, urn: s.urn, postcode: s.postcode, address: s.address, contact: s.contact, numberOnRoll: s.numberOnRoll ?? null,
  })))
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<SchoolHit[]>([])
  const [giasDown, setGiasDown] = useState<string | null>(null)
  const [trustOpen, setTrustOpen] = useState<string | null>(null)
  const [roster, setRoster] = useState<SchoolHit[]>([])
  const [rosterState, setRosterState] = useState<'idle' | 'loading' | 'error'>('idle')
  const [manualName, setManualName] = useState('')
  const [manualPostcode, setManualPostcode] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout>>()

  const dirty = JSON.stringify(list.map((s) => s.urn ?? s.name))
    !== JSON.stringify(sites.map((s) => s.urn ?? s.name))
  const has = (urn: string | null | undefined, name: string) =>
    list.some((s) => (urn && s.urn === urn) || (!urn && s.name === name))

  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      searchSchools(q.trim())
        .then((r) => { setHits(r.schools); setGiasDown(null) })
        .catch((e: Error) => { setHits([]); setGiasDown(e.message) })
    }, 300)
  }, [q])

  const add = (s: SchoolHit) => {
    if (has(s.urn, s.name)) return
    setList((l) => [...l, { name: s.name, urn: s.urn, postcode: s.postcode, address: s.address, contact: null, numberOnRoll: s.numberOnRoll ?? null }])
  }
  const openTrust = (uid: string) => {
    if (trustOpen === uid) { setTrustOpen(null); return }
    setTrustOpen(uid)
    setRoster([])
    setRosterState('loading')
    fetchTrustRoster(uid)
      .then((r) => { setRoster(r.schools); setRosterState('idle') })
      .catch(() => setRosterState('error'))
  }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover">
        <div className="slideover__head">
          <div>
            <h2>Sites on this quote</h2>
            <div className="small muted" style={{ marginTop: 3 }}>
              Prepared for <strong>{customer.name}</strong> — the sites are the schools it covers. Section tags survive for sites that stay.
            </div>
          </div>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          <div className="field">
            <label>Current sites ({list.length})</label>
            {list.length === 0 && <p className="hint">No sites — add at least one below.</p>}
            {list.map((s, i) => (
              <span key={`${s.urn ?? s.name}-${i}`} className="chip" style={{ margin: '0 6px 6px 0' }}>
                {s.name} <span className="muted tiny">{s.postcode ?? ''}{s.urn ? ` · URN ${s.urn}` : ''}{s.numberOnRoll != null ? ` · ${s.numberOnRoll} on roll` : ''}</span>
                {!disabled && <button onClick={() => setList((l) => l.filter((_, x) => x !== i))}>×</button>}
              </span>
            ))}
          </div>

          <div className="field">
            <label>Add a school (GIAS)</label>
            <input className="input" style={{ width: '100%' }} placeholder="School or trust name, URN or postcode…"
              value={q} onChange={(e) => setQ(e.target.value)} />
            {giasDown && q.trim().length >= 2 && (
              <div className="hint">GIAS search unavailable ({giasDown}) — add manually below.</div>
            )}
            <div style={{ marginTop: 8 }}>
              {hits.map((s, i) => s.kind === 'trust' ? (
                <div key={`t-${s.trustUid}-${i}`} className="school" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div className="rowflex" style={{ cursor: 'pointer', justifyContent: 'space-between' }} onClick={() => s.trustUid && openTrust(s.trustUid)}>
                    <div><strong>{s.name}</strong> <span className="small muted">trust — open the roster</span></div>
                    <span className="pill pill--info">{trustOpen === s.trustUid ? 'close' : 'roster'}</span>
                  </div>
                  {trustOpen === s.trustUid && (
                    <div style={{ marginTop: 8, borderTop: '1px solid var(--line-soft)', paddingTop: 8 }}>
                      {rosterState === 'loading' && <p className="muted small">Loading the trust's schools…</p>}
                      {rosterState === 'error' && <p className="pill pill--stop">Could not load the roster.</p>}
                      {roster.map((m) => (
                        <div key={m.urn ?? m.name} className="rowflex small" style={{ padding: '3px 0', justifyContent: 'space-between' }}>
                          <span>{m.name} <span className="muted tiny">({m.postcode ?? '—'})</span> <RegisterTag s={m} /></span>
                          <button className="btn btn--sm" disabled={disabled || has(m.urn, m.name)} onClick={() => add(m)}>
                            {has(m.urn, m.name) ? 'On quote' : '+ Add'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div key={`${s.urn}-${i}`} className="school" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <strong>{s.name}</strong> <RegisterTag s={s} />
                    <div className="small muted">{s.urn ? `${s.register ? 'Site ref' : 'URN'} ${s.urn} · ` : ''}{s.postcode ?? ''}</div>
                  </div>
                  <button className="btn btn--sm" disabled={disabled || has(s.urn, s.name)} onClick={() => add(s)}>
                    {has(s.urn, s.name) ? 'On quote' : '+ Add'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="field">
            <label>Add manually (no URN — flagged as an analytics gap)</label>
            <div className="rowflex">
              <input className="input" style={{ flex: 1.4 }} placeholder="Site name" value={manualName} onChange={(e) => setManualName(e.target.value)} />
              <input className="input" style={{ flex: 1 }} placeholder="Postcode" value={manualPostcode} onChange={(e) => setManualPostcode(e.target.value)} />
              <button className="btn btn--sm" disabled={disabled || !manualName.trim()}
                onClick={() => {
                  setList((l) => [...l, { name: manualName.trim(), urn: null, postcode: manualPostcode.trim() || null, address: {}, contact: null, numberOnRoll: null }])
                  setManualName(''); setManualPostcode('')
                }}>Add</button>
            </div>
          </div>
        </div>
        <div className="slideover__foot">
          {dirty && <span className="pill pill--warn">Unsaved changes</span>}
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" disabled={disabled || list.length === 0 || !dirty}
            onClick={() => onSave(list)}>
            Save {list.length} site{list.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </>
  )
}

// ============================================================== links / resend

/**
 * Per-recipient links on a SENT quote (decision 78). Raw tokens are
 * unrecoverable by design, so every resend mints a FRESH link (old ones stay
 * valid) and bumps the quote's last-sent date.
 */
function LinksSlideOver({ detail, mode, people, onClose, onPatch, onResend }: {
  detail: QuoteDetail
  /** Decision 156: internal mode shows the BCC picker; resends copy the person. */
  mode: Partner['mode']
  people: Array<{ id: string; name: string }>
  onClose: () => void
  onPatch: (patch: Record<string, unknown>) => void
  onResend: (recipientId?: string) => Promise<SendResult>
}) {
  const [busy, setBusy] = useState<string | null>(null)   // recipientId | 'all' | `link:<id>`
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<Map<string, { url: string; sent: boolean; queued: boolean; failure?: MailFailure }>>(new Map())
  // Decision 118: the copyable current link — minted fresh on demand (tokens
  // are hashed at rest, so the original can never be re-read).
  const [links, setLinks] = useState<Map<string, string>>(new Map())
  const [copied, setCopied] = useState<string | null>(null)
  // Decision 147: when the browser refused the automatic copy, say so —
  // the old code showed "✓ Copied" whether the write landed or not.
  const [copyBlocked, setCopyBlocked] = useState<string | null>(null)

  const markCopied = (recipientId: string) => {
    setCopyBlocked(null)
    setCopied(recipientId)
    setTimeout(() => setCopied(null), 2500)
  }

  const copyLink = async (recipientId: string) => {
    setBusy(`link:${recipientId}`); setError(null); setCopyBlocked(null)
    // ⚠️ Decision 147: the clipboard write must START inside the click
    // gesture — an await first spends the gesture and Safari refuses the
    // write. copyTextFromPromise hands the browser a promise for the text.
    const urlPromise = mintRecipientLink(detail.quote.id, recipientId).then((r) => r.url)
    const wrote = copyTextFromPromise(urlPromise)
    try {
      const url = await urlPromise
      setLinks((prev) => new Map(prev).set(recipientId, url))
      if (await wrote) markCopied(recipientId)
      else setCopyBlocked(recipientId)   // honest: the field + Copy button below
    } catch (e) { setError((e as Error).message) } finally { setBusy(null) }
  }

  const run = async (recipientId?: string) => {
    setBusy(recipientId ?? 'all'); setError(null)
    try {
      const res = await onResend(recipientId)
      setResults((prev) => {
        const next = new Map(prev)
        for (const l of res.links) {
          // Decision 90: per-recipient failure matched by address — a delegated
          // send that failed is surfaced on the exact row it concerns.
          const failure = (res.mail.failures ?? []).find((f) => f.email.toLowerCase() === l.email.toLowerCase())
          next.set(l.recipientId, {
            url: l.url,
            sent: res.mail.configured && res.mail.sent > 0 && !failure,
            queued: res.mail.queued > 0,
            failure,
          })
        }
        return next
      })
    } catch (e) { setError((e as Error).message) } finally { setBusy(null) }
  }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover">
        <div className="slideover__head">
          <div>
            <h2>Links &amp; resend</h2>
            <div className="small muted" style={{ marginTop: 3 }}>
              {detail.quote.quoteNumber} — each resend mints a FRESH personal link (old links keep working) and updates the “sent” date.
            </div>
          </div>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          {/* Decision 156: the internal BCC copy is changeable after send too —
              add a copy before a resend, exactly like reassigning the owner. */}
          {mode === 'internal' && people.length > 0 && (
            <div className="field">
              <label>BCC — internal copy (Wave 9 only)</label>
              <select className="input" style={{ maxWidth: 320 }} value={detail.quote.bccUserId ?? ''}
                onChange={(e) => onPatch({ bccUserId: e.target.value || null })}>
                <option value="">None</option>
                {people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <div className="hint">Blind-copied on every resend below. Invisible to the customer.</div>
            </div>
          )}
          {detail.recipients.map((r) => {
            const res = results.get(r.id)
            return (
              <div key={r.id} className="field" style={{ borderBottom: '1px solid var(--line-soft)', paddingBottom: 10 }}>
                <div className="rowflex" style={{ justifyContent: 'space-between' }}>
                  <span><strong>{r.name || r.email}</strong> <span className="muted small">{r.email}</span></span>
                  <span className="rowflex" style={{ gap: 6 }}>
                    <button className="btn btn--sm" disabled={busy !== null} title="Mint this recipient's link and copy it — no email is sent"
                      onClick={() => copyLink(r.id)}>
                      {busy === `link:${r.id}` ? 'Minting…' : copied === r.id ? '✓ Copied' : 'Copy link'}
                    </button>
                    <button className="btn btn--sm" disabled={busy !== null} onClick={() => run(r.id)}>
                      {busy === r.id ? 'Resending…' : 'Resend'}
                    </button>
                  </span>
                </div>
                {links.get(r.id) && (
                  <div className="rowflex" style={{ marginTop: 6, gap: 6 }}>
                    <input className="input" style={{ flex: 1, fontSize: 11.5 }} readOnly
                      value={links.get(r.id)} onFocus={(e) => e.target.select()} />
                    {/* Decision 147: a second click is a fresh gesture — copying
                        the now-known link from state always succeeds. */}
                    <button className="btn btn--sm" title="Copy this link"
                      onClick={() => { void navigator.clipboard.writeText(links.get(r.id)!).then(() => markCopied(r.id)).catch(() => {}) }}>
                      Copy
                    </button>
                  </div>
                )}
                {copyBlocked === r.id && (
                  <div className="hint">The browser blocked the automatic copy — use the Copy button beside the link.</div>
                )}
                {res && (
                  <>
                    <div className="rowflex" style={{ marginTop: 6 }}>
                      <input className="input" readOnly style={{ flex: 1, fontSize: 11.5 }} value={res.url} onFocus={(e) => e.target.select()} />
                      <button className="btn btn--sm" onClick={() => navigator.clipboard.writeText(res.url).catch(() => {})}>Copy</button>
                    </div>
                    {res.failure && <div style={{ marginTop: 6 }}><MailFailureBanner failures={[res.failure]} /></div>}
                    {!res.failure && (
                      <div className="hint">{res.sent ? 'Emailed — sent as the quote owner where their mailbox allows it.' : 'Email not configured — copy the link into your own email (queued as an event).'}</div>
                    )}
                  </>
                )}
              </div>
            )
          })}
          {error && <p className="pill pill--stop" style={{ whiteSpace: 'pre-wrap' }}>{error}</p>}
        </div>
        <div className="slideover__foot">
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>Close</button>
          <button className="btn btn--primary" disabled={busy !== null} onClick={() => run()}>
            {busy === 'all' ? 'Resending…' : `Resend to all (${detail.recipients.length})`}
          </button>
        </div>
      </div>
    </>
  )
}

// ============================================================== activity trail

const EVENT_LABELS: Record<string, { icon: string; label: string }> = {
  created: { icon: '✚', label: 'Created' },
  provisioning_pack: { icon: '🧰', label: 'Provisioning pack created' },
  customer_set: { icon: '⌂', label: 'Recipient organisation set' },
  sent: { icon: '➤', label: 'Sent' },
  resent: { icon: '↻', label: 'Resent' },
  viewed: { icon: '👁', label: 'Viewed' },
  // Decision 144: a staff / issuing-partner open — never flips status, never
  // emails the owner, never counts as the customer viewing.
  internal_view: { icon: '👁', label: 'Internal view (not the customer)' },
  selection_changed: { icon: '⇄', label: 'Selections changed' },
  accepted: { icon: '✓', label: 'ACCEPTED' },
  declined: { icon: '✕', label: 'Declined' },
  question: { icon: '?', label: 'Question asked' },
  withdrawn: { icon: '⊘', label: 'Withdrawn' },
  expired: { icon: '⌛', label: 'Expired' },
  validity_extended: { icon: '⏲', label: 'Validity extended' },   // decision 208
  supplier_quote_applied: { icon: '↑', label: 'Distributor quote applied' },   // decision 212
  superseded: { icon: '⇢', label: 'Superseded' },
  supersede_draft_created: { icon: '⇢', label: 'Revision draft created' },
  prices_refreshed: { icon: '↻', label: 'Prices refreshed' },
  preview_link_created: { icon: '⧉', label: 'Preview link issued' },
  successor_link_issued: { icon: '⇢', label: 'Successor link issued' },
  email_sent: { icon: '✉', label: 'Email sent' },
  email_queued: { icon: '✉', label: 'Email queued (mail not configured)' },
  email_failed: { icon: '✉', label: 'Email FAILED' },
  email_stitched: { icon: '⛁', label: 'Email logged on the HubSpot deal' },
  email_stitch_failed: { icon: '⛁', label: 'Email NOT found in HubSpot (deal timeline stitch given up)' },
  notify_viewed: { icon: '✉', label: 'Owner notified — viewed' },
  notify_accepted: { icon: '✉', label: 'Owner notified — accepted' },
  notify_declined: { icon: '✉', label: 'Owner notified — declined' },
  notify_question: { icon: '✉', label: 'Owner notified — question' },
  pdf_downloaded: { icon: '⎙', label: 'PDF downloaded' },
  deal_sync_ok: { icon: '⛁', label: 'HubSpot deal synced' },
  deal_sync_error: { icon: '⛁', label: 'HubSpot deal sync FAILED (queued for retry)' },
  deal_sync_skipped: { icon: '⛁', label: 'HubSpot sync skipped' },
  deal_sync_warning: { icon: '⛁', label: 'HubSpot sync warning' },
  asset_downloaded: { icon: '📄', label: 'Document downloaded' },
  cost_changed: { icon: '±', label: 'Cost overridden' },
  bcc_changed: { icon: '✉', label: 'Internal BCC changed' },
  attachment_uploaded: { icon: '📎', label: 'Attachment uploaded' },
  imported: { icon: '⇣', label: 'Imported from HubSpot' },
  import_refreshed: { icon: '⟳', label: 'Refreshed from HubSpot (stage / value)' },
  import_deal_gone: { icon: '⊘', label: 'HubSpot deal deleted — withdrawn' },
  cost_variation_logged: { icon: '±', label: 'Cost variation logged' },
  contract_variation_recorded: { icon: '±', label: 'Contract variation recorded' },
  deal_pulled: { icon: '⇣', label: 'Pulled from HubSpot' },
  deal_conflict: { icon: '⚠', label: 'Sync conflict — HubSpot won' },
  deal_attached_pull: { icon: '⛁', label: 'Deal history pulled on attach' },
  acceptance_pdf_filed: { icon: '⎙', label: 'Acceptance PDF filed on the HubSpot deal' },
  acceptance_pdf_failed: { icon: '⎙', label: 'Acceptance PDF NOT filed (given up — file it by hand)' },
}

function eventSummary(e: QuoteEvent): string | null {
  const d = e.detail
  switch (e.kind) {
    case 'sent': return `${d.recipients ?? '?'} recipient(s) — ${d.emailed ?? 0} emailed, ${d.queued ?? 0} queued`
    case 'resent': return `${d.recipients ?? '?'} link(s) reminted — ${d.emailed ?? 0} emailed, ${d.queued ?? 0} queued`
    case 'viewed': return d.first === true ? 'first view' : 'repeat view'
    case 'selection_changed': return `${d.selected ?? 0} on · ${d.deselected ?? 0} off · ${d.qtyEdits ?? 0} qty change(s)`
    case 'accepted': return `${String(d.name ?? '')} — ${String(d.email ?? '')}${d.poReference ? ` · PO ${String(d.poReference)}` : d.poToFollow ? ' · PO to follow' : ''}`
    case 'declined': return `${d.onBehalf ? 'recorded by Wave9 — ' : ''}${String(d.reason ?? '')}${d.detail ? ` — “${String(d.detail)}”` : ''}`
    case 'question': return `“${String(d.message ?? '')}”`
    case 'withdrawn': return d.note ? `note: ${String(d.note)}` : null
    case 'email_sent': case 'email_queued':
      return `${Array.isArray(d.to) ? (d.to as string[]).join(', ') : ''}${d.delegated ? ` — sent as ${String(d.from ?? 'the owner')}` : ''}`
    case 'email_failed':
      // Decision 90: a classified delegated failure shows its guidance, not the raw Graph error.
      return `${Array.isArray(d.to) ? (d.to as string[]).join(', ') : ''}${d.guidance ? ` — ${String(d.guidance)}` : d.error ? ` — ${String(d.error)}` : ''}`
    case 'email_stitched':
      return `${Array.isArray(d.engagements) ? d.engagements.length : '?'} engagement(s) → deal ${String(d.dealId ?? '')}`
    case 'email_stitch_failed':
      return `${String(d.reason ?? '')} — check the BCC address and the sales-email-read scope`
    case 'created': return d.duplicateOf ? `duplicate of ${String(d.duplicateOf)}` : d.supersedes ? `supersedes ${String(d.supersedes)}` : null
    case 'prices_refreshed': return `${d.refreshed ?? 0} line(s) — ${String(d.what ?? 'both')}`
    case 'deal_sync_ok': return `deal ${String(d.dealId ?? '')} ${d.created ? 'created' : 'updated'} — ${d.lineItems ?? 0} line item(s), ${String(d.stage ?? '')}`
    case 'deal_sync_error': return String(d.error ?? '')
    case 'deal_sync_skipped': return String(d.reason ?? '')
    case 'deal_sync_warning': return String(d.warning ?? '')
    case 'pdf_downloaded':
      // Decision 103: downloads are a pack — the quote plus its supporting docs.
      return d.pack
        ? `full pack — quote + ${d.docsIncluded ?? 0} document(s)${Array.isArray(d.docsSkipped) && d.docsSkipped.length ? ` (skipped: ${(d.docsSkipped as string[]).join(', ')})` : ''}`
        : null
    case 'acceptance_pdf_filed': return `deal ${String(d.dealId ?? '')} — note with the accepted PDF attached`
    case 'acceptance_pdf_failed': return `${String(d.reason ?? '')} — after ${d.attempts ?? '?'} attempt(s)`
    default: return null
  }
}

/** The event trail (decision 81) — everything phase 3 records, finally visible. */
/**
 * The event trail as a plain list — shared by the builder's Activity slide-over
 * and the quotes list's summary slide-over (Mark, 2026-08-06).
 */
export function ActivityFeed({ quoteId }: { quoteId: string }) {
  const [events, setEvents] = useState<QuoteEvent[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchQuoteEvents(quoteId).then((r) => setEvents(r.events)).catch((e: Error) => setError(e.message))
  }, [quoteId])

  return (
    <>
      {error && <p className="pill pill--stop">{error}</p>}
      {!events && !error && <p className="muted">Loading…</p>}
      {events?.length === 0 && <p className="muted">Nothing yet.</p>}
      {events?.map((e) => {
        const meta = EVENT_LABELS[e.kind] ?? { icon: '·', label: e.kind }
        const summary = eventSummary(e)
        return (
          <div key={e.id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--line-soft)' }}>
            <span style={{ width: 20, textAlign: 'center', flex: 'none' }}>{meta.icon}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="small"><strong>{meta.label}</strong>
                {e.recipient && <span className="muted"> — {e.recipient.name || e.recipient.email}</span>}
              </div>
              {summary && <div className="tiny muted" style={{ overflowWrap: 'anywhere' }}>{summary}</div>}
            </div>
            <span className="tiny muted" style={{ whiteSpace: 'nowrap' }}>{new Date(e.at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        )
      })}
    </>
  )
}

function ActivitySlideOver({ quoteId, quoteNumber, onClose }: {
  quoteId: string
  quoteNumber: string
  onClose: () => void
}) {
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover">
        <div className="slideover__head">
          <div>
            <h2>Activity</h2>
            <div className="small muted" style={{ marginTop: 3 }}>{quoteNumber} — newest first. Views, selections, questions and every email attempt.</div>
          </div>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          <ActivityFeed quoteId={quoteId} />
        </div>
        <div className="slideover__foot">
          <span style={{ flex: 1 }} />
          <button className="btn btn--primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  )
}

/**
 * Decision 125: what a TEMPLATE carries — cover note, footer, T&Cs and the ECC
 * notice. (Next steps is deliberately NOT here: it falls back to the partner's
 * branding at send time and has never been builder-editable — exposing it in
 * the template editor alone would invent a rule.) Same fields the Send panel
 * offers on a real quote, but
 * here they belong to the template and are the point of the screen, not a
 * detail behind a Send button.
 */
function TemplatePresentationSlideOver({ quote, termsSets, onClose, onPatch }: {
  quote: QuoteDetail['quote']
  termsSets: TermsSetRow[]
  onClose: () => void
  onPatch: (patch: Record<string, unknown>) => void
}) {
  const [note, setNote] = useState(quote.coverNote)
  const [footer, setFooter] = useState(quote.footerText)
  const noteRef = useRef<HTMLTextAreaElement | null>(null)
  const footerRef = useRef<HTMLTextAreaElement | null>(null)
  const usable = termsSets.filter((t) => t.currentVersion)

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover">
        <div className="slideover__head">
          <div>
            <h2>Cover note, footer &amp; T&amp;Cs</h2>
            <p className="small muted" style={{ margin: 0 }}>
              Every quote made from this template starts with these. They can still be edited
              per quote before it goes out.
            </p>
          </div>
          <button className="btn btn--sm" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          <div className="field">
            <label>Terms &amp; conditions</label>
            <select className="select" style={{ width: '100%' }} value={quote.termsSetId ?? ''}
              onChange={(e) => onPatch({ termsSetId: e.target.value || null })}>
              <option value="">None — chosen per quote</option>
              {usable.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{t.currentVersion ? ` (${t.currentVersion})` : ''}</option>
              ))}
            </select>
            <div className="hint">
              Quotes cite the set's CURRENT version at send — updating your terms never rewrites
              this template.
            </div>
          </div>

          <div className="field">
            <label>Cover note</label>
            <MarkupToolbar target={() => noteRef.current} disabled={false}
              onApply={(t) => { setNote(t); if (t !== quote.coverNote) onPatch({ coverNote: t }) }} />
            <textarea ref={noteRef} className="input" style={{ width: '100%' }} rows={7} value={note}
              placeholder="Opens the quote, above the lines. Leave blank to write one per quote."
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => note !== quote.coverNote && onPatch({ coverNote: note })} />
          </div>

          <div className="field">
            <label>Footer</label>
            <MarkupToolbar target={() => footerRef.current} disabled={false}
              onApply={(t) => { setFooter(t); if (t !== quote.footerText) onPatch({ footerText: t }) }} />
            <textarea ref={footerRef} className="input" style={{ width: '100%' }} rows={4} value={footer}
              placeholder="Shown after the contract value. Leave blank for none."
              onChange={(e) => setFooter(e.target.value)}
              onBlur={() => footer !== quote.footerText && onPatch({ footerText: footer })} />
          </div>

          <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={quote.eccNotice}
              onChange={(e) => onPatch({ eccNotice: e.target.checked })} />
            Show the excess construction charges notice
          </label>
        </div>
      </div>
    </>
  )
}

/**
 * Decision 127: the expiring contract, read-only, beside the quote replacing it.
 *
 * Mark, 2026-08-16: "when preparing the new quote, it would be good to have the
 * existing contract lines to be available to view". Facts and lines come from
 * the SHARED `ContractSummary` block, so this and the Renewals slide-over can
 * never disagree. Notes are included and writable — they are portal-only and
 * never sync anywhere (#105), and the moment you most want to jot one is while
 * you are on the phone building the replacement.
 *
 * ⚠️ Deliberately NO outcome actions (create quote, link deal, rollover, lost).
 * Those belong to the Renewals page; a partial set here would invite someone to
 * conclude a renewal from a screen that cannot show them the whole picture.
 * ⚠️ Nothing here is ever copied into the quote — old SKUs and prices routinely
 * no longer exist, which is exactly why renewals price from today's catalogue.
 */
function ContractSlideOver({ renewal, portalId, onClose }: {
  renewal: NonNullable<QuoteDetail['renewal']>
  portalId: string | null
  onClose: () => void
}) {
  const [events, setEvents] = useState<RenewalEvent[] | null>(null)
  const [noteText, setNoteText] = useState('')
  const [noteBusy, setNoteBusy] = useState(false)
  const [noteError, setNoteError] = useState<string | null>(null)

  const load = () => {
    fetchRenewalEvents(renewal.id).then((r) => setEvents(r.events)).catch(() => setEvents([]))
  }
  useEffect(load, [renewal.id])   // eslint-disable-line react-hooks/exhaustive-deps -- load is stable per renewal

  const notes = (events ?? []).filter((e) => e.kind === 'note' || e.kind === 'marked_at_risk')

  const submitNote = async () => {
    const text = noteText.trim()
    if (!text) return
    setNoteBusy(true); setNoteError(null)
    try { await addRenewalNote(renewal.id, text); setNoteText(''); load() }
    catch (e) { setNoteError((e as Error).message) } finally { setNoteBusy(false) }
  }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover">
        <div className="slideover__head">
          <div>
            <h2>{renewal.dealName ?? `Deal ${renewal.hsDealId}`}</h2>
            <p className="small muted" style={{ margin: 0 }}>{renewal.customerName ?? 'The contract this quote renews'}</p>
          </div>
          <button className="btn btn--sm" onClick={onClose}>✕</button>
        </div>
        <div className="slideover__body">
          <ContractFacts r={renewal} portalId={portalId} />
          <ContractLines lines={renewal.lineItems} />

          <div style={{ marginTop: 14 }}>
            <h3 className="small" style={{ margin: '0 0 6px' }}>
              Notes <span className="muted">(portal-only — never synced anywhere)</span>
            </h3>
            {notes.map((n, i) => (
              <div key={i} className="small" style={{ padding: '5px 0', borderBottom: '1px solid var(--line-soft)' }}>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {n.kind === 'marked_at_risk' && <span className="pill pill--warn tiny" style={{ marginRight: 6 }}>at risk</span>}
                  {String(n.detail.text ?? n.detail.note ?? '')}
                </div>
                <div className="tiny muted">{n.actor ?? 'someone'} · {formatUKDate(n.at)}</div>
              </div>
            ))}
            {events != null && notes.length === 0 && <p className="tiny muted" style={{ margin: '2px 0 6px' }}>Nothing yet.</p>}
            {events == null && <p className="tiny muted" style={{ margin: '2px 0 6px' }}>Loading…</p>}
            {noteError && <p className="pill pill--stop">{noteError}</p>}
            <div className="rowflex" style={{ marginTop: 6, alignItems: 'flex-start' }}>
              <textarea className="input" rows={2} style={{ flex: 1 }}
                placeholder="e.g. Spoke to the bursar — waiting on governors, call back w/c 24th"
                value={noteText} onChange={(e) => setNoteText(e.target.value)} />
              <button className="btn btn--sm" disabled={noteBusy || !noteText.trim()}
                onClick={() => void submitNote()}>{noteBusy ? 'Saving…' : 'Add note'}</button>
            </div>
          </div>

          <p className="tiny muted" style={{ marginTop: 12 }}>
            Recording the outcome — renewed, rolled over, not renewing — happens on the Renewals page.
          </p>
        </div>
      </div>
    </>
  )
}
